/**
 * Tiny i18n core: two languages, flat dotted keys, {placeholder} interpolation.
 * `en` is the source of truth; `id` is typed against its keys, so a missing
 * Indonesian string is a compile error. A missing key at runtime renders the
 * key itself, so a gap is visible instead of blank.
 *
 * Only interface chrome belongs here. Device/vendor data, metric keys and units
 * (dBm, Mbps, bps, m) are never translated.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type Lang = "en" | "id";
export const LANG_KEY = "nocops.lang";

export const en = {
  // app / shell
  "app.name": "Jalabyte Ops",
  "app.loading": "loading…",

  // navigation
  "nav.label": "Main navigation",
  "nav.overview": "Overview",
  "nav.olts": "OLTs",
  "nav.onts": "ONTs",
  "nav.topology": "Topology",
  "nav.netmap": "Link map",
  "nav.graphs": "Traffic graphs",
  "nav.integrations": "Integrations",
  "nav.subscribers": "Subscribers",
  "nav.alerts": "Alerts",
  "nav.users": "Users",
  "nav.wallboard": "Wallboard",
  "nav.settings": "Settings",

  // topbar
  "topbar.openNav": "Open navigation",
  "topbar.closeNav": "Close navigation",
  "topbar.signedInAs": "signed in as {email}",
  "topbar.logout": "logout",

  // language switch
  "lang.en": "English",
  "lang.id": "Indonesian",
  "lang.switchTo": "Switch language to {lang}",

  // login
  "login.email": "Email",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.busy": "Signing in…",

  // shared verbs / nouns
  "common.search": "Search",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.close": "Close",

  // OLT resources
  "oltRes.noSensors": "the device exposes no health sensors over SNMP",
  "oltRes.cliOnly": "read from the OLT's own CLI; it exposes no SNMP health sensors",
  "oltPorts.port": "Port",
  "oltPorts.errors": "Errors",


  "oltRes.limit": "limit",


  // TV wallboard

  // Customer graph share
  "share.loading": "Loading…",
  "share.invalid": "This link is not valid. It may have expired or been revoked — ask for a new one.",
  "share.subtitle": "Traffic in and out of your link",
  "share.footer": "Read-only view. Times are Asia/Jakarta (WIB).",
  "myGraphs.title": "My graphs",
  "myGraphs.empty": "No graphs have been shared with this account yet.",
  "wan.title": "CPE WAN (read-only)",
  "wan.service": "Service",
  "wan.mode": "Mode",
  "wan.detail": "PPPoE user / addressing",
  "wan.lastInform": "last inform",
  "wan.notLinked": "No TR-069 device is linked to this ONT, so its WAN settings cannot be read.",
  "wan.unavailable": "Could not read the WAN settings from the ACS.",
  "wan.none": "the CPE reports no WAN services",
  "wan.readOnly": "Read-only. Changing IPoE to PPPoE from here is not enabled yet.",

  "shares.title": "Share links",
  "shares.new": "New link",
  "shares.label": "Name shown to the customer",
  "shares.labelHint": "Goes at the top of their page, e.g. the company name.",
  "shares.graphLabel": "Graph name",
  "shares.expiry": "Expires",
  "shares.expiryDays": "{n} days",
  "shares.never": "no expiry",
  "shares.pick": "Ports to include",
  "shares.create": "Create link",
  "shares.created": "Share this link. It is shown only now — the server keeps a hash, so it cannot be displayed again.",
  "shares.views": "Views",
  "shares.revoke": "Revoke",
  "shares.empty": "no share links yet",
  "shares.active": "active",
  "shares.expired": "expired",
  "shares.revoked": "revoked",
  "shares.customerNote": "The customer sees only these graphs and the names you give them — never the device, interface or capacity.",



  // Users and invites
  "users.accounts": "Accounts",
  "users.invites": "Invitations",
  "users.invite": "Invite",
  "users.inviteTitle": "Invite someone",
  "users.inviteLinkBody": "Share this one-time link. It is shown only now — the server keeps a hash, so it cannot be printed again. It also went out by e-mail if SMTP is configured.",
  "users.sendInvite": "Create invite",
  "users.email": "E-mail",
  "users.role": "Role",
  "bras.diffServers": "different servers",
  "bras.diffServersHint": "The BRAS reached different server IPs for this hostname (their DNS returned different CDN nodes). The gap is the server's location, not path degradation.",
  "bras.nServers": "{n} servers",
  "bras.server": "server",
  "bras.servers3h": "Servers reached (3h)",
  "bras.noPtr": "no reverse DNS",
  "bras.cmpServers": "{slow} and {fast} reach different servers for this hostname: the {d} ms gap is the server's location (e.g. Singapore vs Jakarta), not path degradation. If customers on {slow} should be served locally, check that BRAS's DNS resolver and routing.",
  "bras.lg.servers": "Hostname targets: latency is judged per server IP; one line per server.",
  "bras.lastKnown": "Last known values, before the BRAS stopped answering",
  "bras.subtitle": "Internet reachability and quality from each subscriber gateway",
  "bras.thresholds": "Thresholds",
  "bras.health": "Internet health",
  "bras.ov.healthy": "Healthy",
  "bras.ov.degraded": "Degraded",
  "bras.ov.outage": "Outage",
  "bras.ov.stale": "Data stale",
  "bras.ov.none": "Not monitored",
  "bras.f.bras": "BRAS reachable",
  "bras.f.targets": "targets healthy",
  "bras.f.loss": "with loss (3h)",
  "bras.f.median": "median latency",
  "bras.live": "Live",
  "bras.staleNote": "Data stale · last probe {age} ago",
  "bras.pollAge": "BRAS poll {age} ago",
  "bras.probeAge": "probes {age} ago",
  "bras.case.shared": "Possible shared upstream or Internet issue",
  "bras.case.sharedD": "{n} targets degraded from every BRAS: {list}",
  "bras.case.bras": "Possible BRAS or path-specific degradation via {bras}",
  "bras.case.brasD": "{n} targets degraded: {list}",
  "bras.case.target": "Possible destination-specific issue: {target}",
  "bras.case.targetD": "Degraded from every BRAS; the other targets are fine",
  "bras.case.path": "Possible path-specific degradation: {target} via {bras}",
  "bras.case.pathD": "The same target is fine from the other BRAS",
  "bras.since": "since {age} ago",
  "bras.details": "Details",
  "bras.runMtr": "Run MTR",
  "bras.openIncident": "Open incident",
  "bras.inc.down": "BRAS unreachable: {bras}",
  "bras.inc.pppDrop": "PPPoE session drop on {bras}",
  "bras.unreachable": "Unreachable",
  "bras.lastOk": "last answered {age} ago",
  "bras.delta1h": "{d} last hour",
  "bras.pppDrop": "PPPoE session drop",
  "bras.pppDropN": "{n} disconnected",
  "bras.internetShort": "Internet",
  "bras.nHealthy": "{n}/{total} healthy",
  "bras.noTargetsShort": "no targets",
  "bras.up": "up",
  "bras.compare": "Compare:",
  "bras.diff": "Difference",
  "bras.via": "via {bras}",
  "bras.notProbed": "not probed",
  "bras.st.healthy": "Healthy",
  "bras.st.degraded": "Degraded",
  "bras.st.loss": "Loss",
  "bras.st.unreachable": "Unreachable",
  "bras.why.no_data": "not probed yet",
  "bras.why.stale": "probe stale",
  "bras.why.error": "probe failed",
  "bras.why.no_icmp": "server ignores ping",
  "bras.lossUp": "loss",
  "bras.vsTypical": "+{d} ms vs typical {v}",
  "bras.lastAt": "last {age} ago",
  "bras.lg.latency": "median latency (3h)",
  "bras.lg.loss": "probe with packet loss (height = loss %)",
  "bras.lg.error": "probe could not run",
  "bras.lg.judged": "Degraded = above the path's own typical latency by {pct}% and at least {ms} ms (Thresholds).",
  "bras.tt.avg": "Avg",
  "bras.tt.loss": "Loss",
  "bras.tt.period": "{p} · {n} probes · {ev} with loss",
  "bras.stateVia": "{state} via {bras}",
  "bras.d.now": "Latency now",
  "bras.d.typical": "Typical (24h)",
  "bras.d.jitter": "Jitter",
  "bras.d.lossNow": "Loss now",
  "bras.d.loss3h": "Loss 3h",
  "bras.d.avg3h": "Average 3h",
  "bras.cmpSlower": "{bras} is currently {d} ms slower than {fast}: possibly path-specific.",
  "bras.cmpSame": "Every BRAS within {d} ms of each other.",
  "bras.history": "History",
  "bras.chartMax": "max {v}",
  "bras.chartLegend2": "Lines: latency per BRAS. Red bars below: probes with packet loss, one lane per BRAS.",
  "bras.pppoeSessions": "PPPoE sessions",
  "bras.internetHealth": "Internet from this BRAS",
  "bras.runDiag": "Run diagnostics",
  "bras.pingResult": "Ping result",
  "bras.traceResult": "Trace",
  "bras.ek.api": "Tool error: the BRAS could not be reached or refused the command. This says nothing about the target.",
  "bras.ek.timeout": "The test ran out of time before it finished.",
  "bras.ek.unreachable": "The target did not answer a single packet.",
  "bras.ek.incomplete": "The trace stops before the target answers: the path breaks at the first silent hop, or the target does not answer ICMP.",
  "bras.viewRaw": "View raw output",
  "bras.hideRaw": "Hide raw output",
  "bras.res.ok": "OK",
  "bras.res.reachable": "Reachable",
  "bras.res.timeout": "Timed out",
  "bras.res.toolFail": "Tool error",
  "bras.res.toolFailS": "BRAS / API error",
  "bras.res.incomplete": "Incomplete",
  "bras.rateLimited": "reply rate-limited; later hops fine",
  "bras.diagnostics": "Diagnostics",
  "bras.manualNote": "Manual tests, logged below",
  "bras.count": "Count",
  "bras.spreadMs": "Spread (max − min): {v}",
  "bras.recentDiag": "Recent diagnostics",
  "bras.tab.manual": "Manual",
  "bras.tab.auto": "Automatic traces",
  "bras.col.time": "Time",
  "bras.col.source": "From",
  "bras.col.target": "Target",
  "bras.col.type": "Type",
  "bras.col.result": "Result",
  "bras.col.summary": "Summary",
  "bras.noManual": "No manual tests yet.",
  "bras.noAuto": "No automatic traces yet. One is taken when an incident opens.",
  "bras.category": "Category",
  "bras.probeType": "Probe",
  "bras.probedFrom": "Probed from",
  "bras.overrides": "Target-specific thresholds (optional)",
  "bras.latWarn": "Latency warning (ms)",
  "bras.lossWarn": "Loss warning (%)",
  "bras.usePolicy": "use policy",
  "bras.overrideNote": "A latency limit replaces the baseline judgement for this target. Empty = the global policy.",
  "bras.policyHint": "How probe results are judged, for the page and for alerts. Latency is compared with each path's own 24-hour median, so a 20 ms destination is not held to a 7 ms one.",
  "bras.p.lossWarn": "Warning at",
  "bras.p.lossCrit": "Critical at",
  "bras.p.latency": "Latency (vs the path's own baseline)",
  "bras.p.devPct": "Above baseline by",
  "bras.p.devMin": "and at least",
  "bras.p.latencyNote": "Degraded when latency is {pct}% over the path's typical AND at least {ms} ms over it.",
  "bras.p.pppPct": "Drop of",
  "bras.p.pppMin": "and at least",
  "bras.p.sessions": "sessions",
  "bras.loss3h": "3h loss {n}",
  "bras.apiPort": "API port (0 = SSH)",
  "bras.apiTls": "api-ssl (TLS)",
  "nav.bras": "BRAS & internet",
  "bras.title": "BRAS & internet",
  "bras.addRouter": "Add BRAS",
  "bras.addTarget": "Add target",
  "bras.editRouter": "Edit {name}",
  "bras.editTarget": "Edit target",
  "bras.st.up": "Up",
  "bras.st.down": "Down",
  "bras.st.unknown": "Unknown",
  "bras.st.disabled": "Disabled",
  "bras.load": "Load",
  "bras.memory": "Memory",
  "bras.uptime": "Uptime",
  "bras.test": "Test",
  "bras.testOk": "Connected · {id}",
  "bras.disable": "Disable",
  "bras.enable": "Enable",
  "bras.internet": "Internet from each BRAS",
  "bras.every": "5 pings every {s}s",
  "bras.target": "Target",
  "bras.noTargets": "No internet targets yet.",
  "bras.noTargetsAdmin": "No internet targets yet. Add public addresses the BRAS should always reach, e.g. 1.1.1.1, 8.8.8.8, your upstream gateway.",
  "bras.delTarget": "Delete target {name} and its history?",
  "bras.from": "From",
  "bras.targetAddr": "Address",
  "bras.ping": "Ping",
  "bras.pinging": "Pinging…",
  "bras.trace": "MTR / trace",
  "bras.tracing": "Tracing…",
  "bras.traceWait": "A trace takes up to a minute when hops do not answer.",
  "bras.loss": "Loss",
  "bras.recv": "Received",
  "bras.hop": "Hop",
  "bras.noReply": "no reply",
  "bras.autoTraceLong": "taken automatically when the target started failing",
  "bras.manualTrace": "run by hand",
  "bras.hops": "{n} hops",
  "bras.noHistory": "No probes in this window.",
  "bras.setup": "Add your BRAS",
  "bras.setupBody": "The panel uses the RouterOS API (Linux: SSH) with a read-only account to read health and PPPoE sessions, and to run ping and traceroute. RouterOS needs the api, read and test policies; nothing is ever configured on the router.",
  "bras.setupNote": "The BRAS must be reachable from the panel server on its API port (8728, or 8729 with TLS; Linux: SSH). Credentials are encrypted and never shown again.",
  "bras.kind": "Type",
  "bras.host": "Host / IP",
  "bras.sshPort": "SSH port",
  "bras.username": "Username",
  "bras.password": "Password",
  "bras.passwordKeep": "Password (blank = keep)",
  "bras.credNote": "Use a read-only account (RouterOS: policy api,read,test). The password is stored encrypted.",
  "bras.targetHint": "An address every BRAS pings each minute. Two lossy runs in a row open an alert and save a trace.",
  "bras.delRouterTitle": "Delete {name}?",
  "bras.delRouterBody": "Its probe history and traces are deleted and its alerts closed. The router itself is not touched.",
  "cping.title": "Ping from BRAS",
  "cping.explain": "Finds the BRAS holding this customer's PPPoE session and pings the session address from it: the latency of the access path, even when the ONT itself does not answer ping.",
  "cping.running": "Looking up the session on each BRAS and pinging…",
  "cping.found": "Session on {router} ({login}):",
  "cping.noSession": "No active PPPoE session on any BRAS for {logins}. The customer is not connected at the PPP level.",
  "cping.notHere": "no session",
  "cping.lastIp": "Last address the router reported: {ip} (not pinged: it may belong to someone else now).",
  "cping.run": "Run",
  "cping.again": "Run again",
  "int.noun.routers": "routers",
  "int.cfg.cred.bras": "Per router, encrypted, on the BRAS page",
  "int.provides.bras": "BRAS health",
  "int.provides.internet": "Internet reachability",
  "wb.alarm.new": "ALARM · {n} new",
  "wb.alarm.ack": "Acknowledge",
  "wb.sound.arm": "Click anywhere to arm sound",
  "wb.sound.on": "Alarm sound on (click to mute)",
  "wb.sound.off": "Alarm sound off (click to enable)",
  "sub.odp.issue": "Possible ODP-level issue",
  "sub.odp.down": "{n} of {total} services down or silent",
  "sub.odp.hint": "Several drops on one ODP failing together usually means a splitter or feeder fault, not separate customer problems.",
  "sub.odp.show": "Show these subscribers",
  "sub.f.allOdps": "All ODPs",
  "sub.d.viaTag": "matched by ACS tag #{id}",
  "sub.d.noOdp": "Not tagged",
  "sub.d.acsTag": "ACS tag",
  "sub.d.loginDiffers": "Tag login {tag} differs from billing PPPoE {billing}",
  "ont.opt.noReading": "no reading · last {age} ago",
  "ont.opt.noReadingWhy": "The OLT returned no optical value for this ONT on its latest poll. The value shown is the last one it did report.",
  "ont.rm.action": "Remove from panel",
  "ont.rm.title": "Remove {name} from the panel?",
  "ont.rm.body": "Use this only for an ONT that was deleted from the OLT (replaced or faulty). The OLT no longer lists it, so nothing on the network changes.",
  "ont.rm.alerts": "Its open alerts are closed.",
  "ont.rm.history": "Its optical history and events are deleted.",
  "ont.rm.links": "Subscriber and CPE links to it are removed.",
  "ont.rm.type": "Type the serial {serial} to confirm",
  "ont.rm.confirm": "Remove ONT",
  "sub.title": "Subscribers",
  "sub.count": "{n} subscribers",
  "sub.onOnt": "{n} on an ONT",
  "sub.cpeOnly": "{n} CPE only",
  "sub.unlinkedN": "{n} unlinked",
  "sub.svcLower.active": "active",
  "sub.svcLower.suspended": "suspended",
  "sub.svcLower.terminated": "terminated",
  "sub.svcLower.cancelled": "cancelled",
  "sub.svcLower.other": "other",
  "sub.source": "Source",
  "sub.syncedAgo": "synced {age} ago",
  "sub.neverSynced": "never synced",
  "sub.sync.degraded": "degraded",
  "sub.sync.failed": "last sync failed",
  "sub.sync.stale": "stale",
  "sub.sync.unknown": "not run yet",
  "sub.sync.disabled": "disabled",
  "sub.scope.operational": "Operational",
  "sub.scope.all": "All subscribers",
  "sub.search": "Search name, customer ID, ONT serial, IP, MAC, description…",
  "sub.clearSearch": "Clear search",
  "sub.filters": "Filters",
  "sub.quick.attention": "Needs attention",
  "sub.quick.active": "Active",
  "sub.quick.suspended": "Suspended",
  "sub.quick.unlinked": "Unlinked",
  "sub.quick.offline": "Offline",
  "sub.quick.weak": "Weak signal",
  "sub.quick.all": "All",
  "sub.selected": "{n} selected",
  "sub.export": "Export CSV",
  "sub.copyIds": "Copy customer IDs",
  "sub.clearSel": "Clear selection",
  "sub.exportView": "Export these {n} rows",
  "sub.selectPage": "Select this page",
  "sub.select": "Select {name}",
  "sub.col.subscriber": "Subscriber",
  "sub.col.service": "Service",
  "sub.col.svcStatus": "Service status",
  "sub.col.network": "Network",
  "sub.col.signal": "Signal",
  "sub.col.access": "Access",
  "sub.noMatch": "No subscriber matches",
  "sub.searchAll": "search all subscribers, including ended services",
  "sub.showing": "{from}–{to} of {n}",
  "sub.perPage": "Rows per page",
  "sub.perPageN": "{n} per page",
  "sub.prev": "Previous page",
  "sub.next": "Next page",
  "sub.f.olt": "OLT",
  "sub.f.allOlts": "All OLTs",
  "sub.f.pon": "PON",
  "sub.f.allPons": "All PONs",
  "sub.f.plan": "Package",
  "sub.f.allPlans": "All packages",
  "sub.f.link": "ONT link",
  "sub.f.linked": "Linked to an ONT",
  "sub.f.noOnt": "No ONT linked",
  "sub.f.clear": "Clear filters",
  "sub.svc.active": "Active",
  "sub.svc.suspended": "Suspended",
  "sub.svc.terminated": "Terminated",
  "sub.svc.cancelled": "Cancelled",
  "sub.net.online": "Online",
  "sub.net.weak": "Weak signal",
  "sub.net.critical": "Critical signal",
  "sub.net.los": "LOS",
  "sub.net.power": "Power loss",
  "sub.net.offline": "Offline",
  "sub.net.stale": "Stale",
  "sub.net.cpe_online": "CPE online",
  "sub.net.cpe_silent": "CPE silent",
  "sub.net.unknown": "Unknown",
  "sub.net.unlinked": "Unlinked",
  "sub.att.activeDown": "Active service, network down",
  "sub.att.activeDownFor": "Active service, down {d}",
  "sub.att.activeSignal": "Active service, signal degraded",
  "sub.att.activeSilent": "Active service, CPE silent",
  "sub.att.activeSilentFor": "Active service, CPE silent {d}",
  "sub.att.suspendedOnline": "Suspended, still online",
  "sub.att.endedLinked": "Ended, equipment still assigned",
  "sub.attWhy.activeDown": "The customer is paying for a service the network is not delivering.",
  "sub.attWhy.activeSignal": "The optical level is past the configured threshold; the service may drop.",
  "sub.attWhy.activeSilent": "The router stopped checking in with the ACS. It may be powered off, or the line may be down; no ONT is linked to tell which.",
  "sub.attWhy.suspendedOnline": "Billing suspended this service, but its equipment is still up. Check that suspension is enforced on the network.",
  "sub.attWhy.endedLinked": "The service has ended in billing, but an ONT is still assigned to it or its router is still online.",
  "sub.multi": "{n} ONTs · {up} up · {down} down",
  "sub.cpeAcs": "CPE via ACS",
  "sub.noDevice": "No ONT or CPE known",
  "sub.noPlan": "No package",
  "sub.sig.good": "Normal",
  "sub.sig.warn": "Warning",
  "sub.sig.crit": "Critical",
  "sub.sig.stale": "Stale reading",
  "sub.sig.noOptics": "No optics (CPE only)",
  "sub.sig.noOnt": "No ONT",
  "sub.sig.noReading": "No reading",
  "sub.acc.ont": "ONT linked",
  "sub.acc.onts": "{n} ONTs linked",
  "sub.acc.pppoe": "via PPPoE login",
  "sub.acc.confirmed": "confirmed by operator",
  "sub.acc.cpe": "CPE only",
  "sub.acc.inform": "ACS inform {age} ago",
  "sub.acc.neverInform": "never informed",
  "sub.linkOnt": "Link ONT",
  "sub.actions": "Actions for {name}",
  "sub.m.open": "Open subscriber",
  "sub.m.ont": "Open ONT",
  "sub.m.olt": "Open OLT",
  "sub.m.alerts": "View incident",
  "sub.m.linkAnother": "Link another ONT",
  "sub.m.copy": "Copy customer ID",
  "sub.path.olt": "OLT",
  "sub.path.pon": "PON",
  "sub.path.ont": "ONT",
  "sub.path.serial": "Serial",
  "sub.path.distance": "Distance",
  "sub.path.rx": "Current RX",
  "sub.d.subscriber": "Subscriber",
  "sub.d.client": "client {id}",
  "sub.d.service": "Service",
  "sub.d.fromWhmcs": "From WHMCS · updated {age} ago",
  "sub.d.network": "Network",
  "sub.d.cpeOnlyNote": "Only the router is known (GenieACS). No ONT is linked, so there is no optical data.",
  "sub.d.unlinkedNote": "No ONT or router is known for this service yet.",
  "sub.d.downFor": "Down for {d}",
  "sub.d.silentFor": "No inform for {d}",
  "sub.d.incident": "Active incident",
  "sub.d.openFor": "Open for {d}",
  "sub.d.acked": "acknowledged",
  "sub.d.openIncident": "Open incident",
  "sub.d.noAlerts": "No active alerts.",
  "sub.d.access": "Access devices",
  "sub.d.cpe": "Customer router (CPE)",
  "sub.d.viaPppoe": "matched by PPPoE login",
  "sub.d.model": "Model",
  "sub.d.lastInform": "Last inform",
  "sub.d.wanIp": "WAN IP",
  "sub.d.nothing": "Nothing on the network is linked to this service.",
  "sub.d.ontRx": "ONT RX",
  "sub.d.oltRx": "OLT RX",
  "sub.d.lastSeen": "Last seen",
  "sub.d.link": "Link",
  "sub.d.linkAuto": "Billing (PPPoE)",
  "sub.d.linkManual": "Operator",
  "sub.d.signalHistory": "Signal history",
  "sub.d.events": "Recent events",
  "sub.d.noEvents": "No network events recorded.",
  "sub.d.sourceNote": "Customer, package and service status come from WHMCS and are changed there.",
  "sub.ev.linked": "ONT linked",
  "sub.ev.unlinked": "ONT unlinked",
  "sub.na": "Not available",
  "sub.neverSeen": "Never seen",
  "sub.unlink": "Unlink",
  "sub.unlinkTitle": "Unlink ONT?",
  "sub.unlinkBody": "{serial} will no longer be shown as serving {name}. The ONT itself is not changed.",
  "sub.l.title": "Link ONT to subscriber",
  "sub.l.search": "Search serial, description, OLT, PON…",
  "sub.l.results": "Search results",
  "sub.l.suggested": "Suggested matches",
  "sub.l.suggestNote": "Suggestions are only hints. Nothing is linked until you confirm.",
  "sub.l.none": "No ONT matches",
  "sub.l.noSuggest": "No suggestion. Search by serial or description.",
  "sub.l.noDesc": "No description",
  "sub.l.strong": "Strong match",
  "sub.l.possible": "Possible match",
  "sub.l.whyCpe": "The customer's router reports this ONT's serial",
  "sub.l.whyName": "Customer name appears in the ONT description",
  "sub.l.taken": "Already linked to {name}",
  "sub.l.review": "Review",
  "sub.l.confirm": "Confirm link",
  "sub.l.confirmNote": "{serial} will be shown as serving {name}, and its alerts will lead here. You can unlink it later.",
  "set.title": "Settings",
  "set.subtitle": "How the system behaves",
  "set.search": "Search settings…",
  "set.noHits": "No matching setting",
  "set.nav.overview": "Overview",
  "set.nav.general": "General",
  "set.nav.monitoring": "Monitoring",
  "set.nav.integrations": "Integrations",
  "set.nav.access": "Access & Credentials",
  "set.nav.sharing": "Sharing",
  "set.nav.advanced": "Advanced",
  "set.yourAccess": "Your access",
  "set.access.admin": "Administrator: can edit monitoring policy, integrations, credentials and share links.",
  "set.access.noc": "NOC operator: can view every setting; changes need an administrator.",
  "set.access.viewer": "Viewer: read-only access to settings.",
  "set.access.portviewer": "Port viewer: no access to settings.",
  "set.viewOnly": "{what}: view only. Changes need an administrator.",
  "set.leaveDirty": "You have unsaved changes. Leave this section and discard them?",
  "set.enc.short": "encrypted",
  "set.enc.shortOff": "no key",
  "set.enc.on": "Encryption enabled",
  "set.enc.off": "Encryption not configured",
  "set.enc.enabled": "Enabled",
  "set.enc.notConfigured": "Not configured",
  "set.nActive": "{n} active",
  "set.nActiveLinks": "{n} active links",
  "set.nOfM": "{n} of {m}",
  "set.every": "every {d}",
  "set.adminOnly": "Administrators only",
  "set.open": "Open",
  "set.recent": "Recent changes",
  "set.noRecent": "No configuration changes recorded yet.",
  "set.configured": "Configured",
  "set.notConfigured": "Not configured",
  "set.notSet": "Not set",
  "set.missing": "Missing",
  "set.lastChanged": "Last changed {age} ago · {by}",
  "set.learnMore": "Learn more",
  "set.unsaved": "Unsaved changes",
  "set.saved": "Changes saved",
  "set.discard": "Discard",
  "set.saveChanges": "Save changes",
  "set.envManaged": "Set in the deployment environment; see Advanced for the variable names.",
  "set.actionsFor": "Actions for {name}",
  "set.ov.policySet": "Policy configured",
  "set.ov.policyDefault": "Using built-in defaults",
  "set.ov.rx": "Optical RX",
  "set.ov.alertEval": "Alert evaluation",
  "set.ov.configured": "{n} of {total} configured",
  "set.ov.notConfigured": "Not configured",
  "set.ov.oltCreds": "OLT credentials",
  "set.ov.reveal": "Secret reveal",
  "set.ov.writeOnly": "Write-only",
  "set.ov.expiring": "Expiring within 7 days",
  "set.ov.totalLinks": "All links",
  "set.ov.serverWide": "Server-wide defaults",
  "set.ov.sysInfo": "System information",
  "set.ov.diag": "Diagnostics",
  "set.ov.envNames": "Environment configuration",
  "set.gen.language": "Language",
  "set.gen.languageHint": "Your own interface language. It does not change what other users see.",
  "set.gen.system": "System",
  "set.gen.publicUrl": "Public URL",
  "set.gen.tz": "OLT timezone",
  "set.gen.tzUnset": "Not set (server clock)",
  "set.gen.inviteTtl": "Invite link validity",
  "set.gen.permissions": "Who can change settings",
  "set.perm.area": "Area",
  "set.perm.monitoring": "Monitoring policy",
  "set.perm.integrations": "Integrations",
  "set.perm.credentials": "OLT credentials",
  "set.perm.sharing": "Share links",
  "set.perm.view": "View",
  "set.perm.edit": "Edit",
  "set.perm.manage": "Manage",
  "set.perm.status": "Status only",
  "set.perm.replace": "Replace",
  "set.perm.none": "No access",
  "set.perm.note": "Enforced by the server, not just hidden here. Usernames are shown to administrators only; no role can read a password.",
  "set.mon.freshness": "Polling & freshness",
  "set.mon.job": "Job",
  "set.mon.interval": "Interval",
  "set.mon.staleAfter": "Stale after",
  "set.mon.oltPoll": "OLT polling",
  "set.mon.snmpPoll": "SNMP reachability",
  "set.mon.acsSync": "LibreNMS / GenieACS sync",
  "set.mon.whmcsSync": "WHMCS sync",
  "set.mon.alertEval": "Alert evaluation",
  "set.mon.staleRule": "Data is marked stale after two missed runs plus 30s grace; stale values are never shown as current.",
  "set.mon.alertPolicies": "Alert policies",
  "set.mon.manageRules": "Manage rules",
  "set.mon.rules": "Rules",
  "set.mon.critical": "Critical",
  "set.mon.util": "Traffic utilisation thresholds",
  "set.mon.utilHigh": "High",
  "set.mon.utilCrit": "Critical",
  "set.mon.utilHint": "Grades interface load on Traffic Graphs, against live capacity (LAG members that are up).",
  "set.opt.title": "Optical RX policy",
  "set.opt.bands": "Bands",
  "set.opt.healthy": "Healthy",
  "set.opt.warning": "Warning",
  "set.opt.critical": "Critical",
  "set.opt.overload": "Above −8 dBm is overload and always critical: a receiver limit, not a policy.",
  "set.opt.warnField": "Warning threshold",
  "set.opt.critField": "Critical threshold",
  "set.opt.err.number": "Enter both thresholds as numbers.",
  "set.opt.err.order": "Critical must be lower than warning, or the bands overlap.",
  "set.opt.err.range": "Thresholds must be between −40 and −8 dBm.",
  "set.opt.preview": "Preview",
  "set.opt.lvl.good": "Good",
  "set.opt.lvl.warn": "Warning",
  "set.opt.lvl.crit": "Critical",
  "set.opt.lvl.unknown": "No reading",
  "set.opt.impact": "Fleet impact",
  "set.opt.now": "Now",
  "set.opt.after": "After change",
  "set.opt.capped": "Counted over the first 5,000 ONTs.",
  "set.opt.appliesTo": "Applies to",
  "set.opt.allOnts": "All ONTs (global default)",
  "set.opt.overrides": "No vendor, OLT, PON or profile overrides are configured.",
  "set.opt.usedBy": "Used by",
  "set.use.ontList": "ONT list",
  "set.use.ontDetail": "ONT detail",
  "set.use.oltDetail": "OLT detail",
  "set.use.wallboard": "Wallboard",
  "set.use.alerts": "Alerts",
  "set.use.telegram": "Telegram notifications",
  "set.acs.title": "ACS servers",
  "set.acs.endpoint": "Endpoint",
  "set.acs.devices": "Devices",
  "set.acs.lastSync": "Last sync",
  "set.acs.notSynced": "Not synchronized",
  "set.acs.stale": "stale",
  "set.acs.neverTested": "Never tested",
  "set.acs.test": "Test connection",
  "set.acs.sync": "Sync now",
  "set.acs.disable": "Disable",
  "set.acs.enable": "Enable",
  "set.acs.deleteServer": "Delete server",
  "set.acs.disableTitle": "Disable {name}?",
  "set.acs.disableBody": "The collector stops syncing its CPEs. Mirrored devices stay but will go stale.",
  "set.acs.state.healthy": "Healthy",
  "set.acs.state.degraded": "Degraded",
  "set.acs.state.failed": "Failed",
  "set.acs.state.disabled": "Disabled",
  "set.acs.state.unknown": "Unknown",
  "set.int.connections": "Other integrations",
  "set.int.liveHealth": "Live health in Integrations",
  "set.int.librenms": "Topology, devices and traffic",
  "set.int.smtp": "Alert and invite email",
  "set.int.telegram": "Alert notifications",
  "set.int.whmcs": "Subscribers and billing",
  "set.int.auth": "authenticated",
  "set.int.noAuth": "no auth",
  "set.int.recipients": "{n} alert recipients",
  "set.int.botChat": "Bot token and chat set",
  "set.int.noToken": "no bot token",
  "set.int.noChat": "no chat id",
  "set.sec.title": "Security",
  "set.sec.encryption": "Credential encryption",
  "set.sec.masterKey": "Master encryption key",
  "set.sec.algorithm": "Algorithm",
  "set.sec.oltStored": "OLT credentials stored",
  "set.sec.acsStored": "ACS passwords stored",
  "set.sec.plaintext": "Plaintext secrets",
  "set.sec.plaintextNone": "None",
  "set.sec.reveal": "Secret reveal",
  "set.sec.revealOff": "Not possible (write-only)",
  "set.sec.summary": "Credentials are encrypted at rest and never sent back to the browser.",
  "set.cred.title": "OLT credentials",
  "set.cred.olt": "OLT",
  "set.cred.username": "Username",
  "set.cred.access": "Access",
  "set.cred.secrets": "Password",
  "set.cred.changed": "Last changed",
  "set.cred.login": "Last login",
  "set.cred.hidden": "Hidden",
  "set.cred.stored": "Stored",
  "set.cred.noPassword": "Missing",
  "set.cred.plusEnable": "+ enable",
  "set.cred.beforeAudit": "Before audit log",
  "set.cred.accepted": "Accepted {age} ago",
  "set.cred.pollFailing": "Last poll failed",
  "set.cred.notVerified": "Not verified yet",
  "set.cred.replace": "Replace credential",
  "set.cred.replaceTitle": "Replace credential for {name}?",
  "set.cred.replaceBody": "The collector logs in with the new credential on its next poll. A wrong password stops polling for this OLT until it is fixed.",
  "set.cred.newPassword": "New password",
  "set.cred.newEnable": "New enable password (blank = keep)",
  "set.cred.none": "No OLTs yet",
  "set.cred.writeOnly": "Passwords are write-only: they can be replaced, never displayed.",
  "set.share.create": "Create share link",
  "set.share.policy": "Share links give anyone holding the URL read access to the chosen graphs, without logging in. The URL is shown once at creation; only a hash is kept, so a lost link cannot be recovered: revoke it and create a new one.",
  "set.share.shared": "Shared graphs · {n}",
  "set.share.more": "+{n} more",
  "set.share.lastAccess": "Last accessed",
  "set.share.neverOpened": "Never opened",
  "set.share.by": "Created by {email}",
  "set.share.onceShort": "URL shown once at creation",
  "set.share.extend": "Extend expiration",
  "set.share.extendTitle": "Extend {name}?",
  "set.share.extendBody": "The link keeps working until the new date.",
  "set.share.extendExpired": "This link has expired. Extending it makes the same URL work again.",
  "set.share.newExpiry": "New expiry:",
  "set.share.revokeTitle": "Revoke share link?",
  "set.share.revokeBody": "{name} will immediately lose access. This cannot be undone.",
  "set.share.revokeLink": "Revoke link",
  "set.adv.title": "System information",
  "set.adv.hint": "Server configuration comes from environment variables. Values are never shown, only whether they are set.",
  "set.adv.setting": "Setting",
  "set.adv.env": "Environment variables",
  "set.adv.defaults": "Defaults apply if unset",
  "set.adv.librenms": "LibreNMS",
  "set.adv.smtp": "SMTP email",
  "set.adv.telegram": "Telegram",
  "set.adv.whmcs": "WHMCS",
  "set.adv.intervals": "Polling intervals",
  "set.adv.crypto": "Credential storage",
  "set.adv.restart": "Environment changes take effect after the API and worker restart.",
  "set.act.alert_rule.create": "Created alert rule",
  "set.act.alert_rule.update": "Changed alert rule",
  "set.act.alert_rule.delete": "Deleted alert rule",
  "set.act.acs.create": "Added ACS server",
  "set.act.acs.update": "Updated ACS server",
  "set.act.acs.delete": "Deleted ACS server",
  "set.act.graph_share.create": "Created share link",
  "set.act.graph_share.revoke": "Revoked share link",
  "set.act.graph_share.extend": "Extended share link",
  "set.act.olt.create": "Added OLT",
  "set.act.olt.update": "Updated OLT",
  "set.act.netlink.create": "Added topology link",
  "set.act.netlink.update": "Updated topology link",
  "set.act.netlink.delete": "Deleted topology link",
  "shares.preview": "Access preview",
  "shares.previewBody": "This link lets anyone who has it see traffic graphs for {name}, and nothing else:",
  "shares.previewExpiry": "Access ends automatically after {n} days.",
  "shares.previewNoExpiry": "Access never expires unless the link is revoked.",
  "tr.title": "Traffic",
  "tr.search": "Search device, IP, interface or description…",
  "tr.noHits": "No interface matches.",
  "tr.recent": "Recent",
  "tr.noData": "No traffic data for this interface.",
  "tr.fav": "Pin to favourites",
  "tr.unfav": "Remove from favourites",
  "tr.interface": "Interface",
  "tr.state.up": "Up",
  "tr.state.degraded": "Degraded",
  "tr.state.down": "Down",
  "tr.state.stale": "No data",
  "tr.state.unknown": "Unknown",
  "tr.live": "Live",
  "tr.stale": "Stale",
  "tr.polled": "polled {age} ago · every {every}",
  "tr.neverPolled": "never polled",
  "tr.source": "LibreNMS",
  "tr.down": "Interface down for {since}. Traffic shown below is history, not current.",
  "tr.unknownSince": "the whole window",
  "tr.lastTraffic": "last traffic ↓{in} ↑{out}, {at} ago",
  "tr.staleBody": "No LibreNMS poll for {age} (expected every {every}): the rates are not current. This is a monitoring gap, not an interface failure.",
  "tr.hot.in": "Inbound utilisation above {pct}%",
  "tr.hot.out": "Outbound utilisation above {pct}%",
  "tr.since": "since {age}",
  "tr.peakPct": "peak {v} · {pct}%",
  "tr.lagDegraded": "LAG degraded: {up} of {total} members up · {avail} of {all} available. Utilisation is measured against live capacity.",
  "tr.dir.in": "Inbound",
  "tr.dir.out": "Outbound",
  "tr.short.in": "In",
  "tr.short.out": "Out",
  "tr.grade.normal": "Normal",
  "tr.grade.high": "High",
  "tr.grade.critical": "Critical",
  "tr.notCurrent": "not current",
  "tr.capacity": "Capacity",
  "tr.members": "LAG · {up}/{total} members up × {each}",
  "tr.lagNoMembers": "LAG · no member data",
  "tr.ifSpeed": "interface speed",
  "tr.errors": "Errors",
  "tr.errorsSub": "in {in} · out {out} · discards not reported",
  "tr.history": "Traffic history · {win}",
  "tr.compare": "Compare previous period",
  "tr.view.librenms": "LibreNMS graph",
  "tr.view.native": "Native",
  "tr.legacyCaption": "Rendered by LibreNMS",
  "tr.legacyNote": "inbound above the axis, outbound below",
  "tr.nativeEmpty": "No samples yet for this window.",
  "tr.nativeEmptyHint": "JALABYTE OPS keeps one sample per LibreNMS poll; history builds up from the first sync after this feature was deployed.",
  "tr.nativeAria": "Traffic history, inbound and outbound",
  "tr.noDataBand": "NO DATA",
  "tr.capacityLine": "capacity {v}",
  "tr.prevPeriod": "previous period",
  "tr.nativeNote": "one point per {step} · gaps are missing polls, never zero",
  "tr.noDataShort": "no data",
  "tr.cur": "Current",
  "tr.avg": "Average",
  "tr.peak": "Peak",
  "tr.p95": "95th",
  "tr.p95Withheld": "Only {pct}% of the window is covered: a 95th percentile would not describe it.",
  "tr.insufficient": "too few",
  "tr.transferred": "Transferred · {win}",
  "tr.bytes.in": "Inbound",
  "tr.bytes.out": "Outbound",
  "tr.bytes.total": "Total",
  "tr.coverage": "From {n} of {exp} expected polls ({pct}%) since {since}.",
  "tr.noSamples": "No samples in this window yet.",
  "tr.peakShare": "peak {pct}% of capacity",
  "tr.lagMembers": "LAG members",
  "tr.membersUp": "{up}/{total} up",
  "tr.details": "Interface details",
  "tr.viewTopology": "View in Topology",
  "tr.d.device": "Device",
  "tr.d.alias": "Description",
  "tr.d.speed": "Speed",
  "tr.d.type": "Type",
  "tr.d.status": "Status",
  "tr.d.polled": "Last poll",
  "tr.d.source": "Source",
  "lm.viewTraffic": "View traffic",
  "topbar.attention": "{n} alerts need attention · {all} open",
  "al.view.active": "Active",
  "al.view.resolved": "Resolved",
  "al.view.rules": "Rules",
  "al.kind.power": "Customer power loss",
  "al.kind.los": "Loss of signal (LOS)",
  "al.kind.offline": "ONT offline",
  "al.kind.optical.warn": "Weak optical signal",
  "al.kind.optical.crit": "Optical signal critical",
  "al.kind.oltRx.warn": "Weak OLT RX signal",
  "al.kind.oltRx.crit": "OLT RX signal critical",
  "al.kind.temperature": "High temperature",
  "al.kind.supply": "Power supply abnormal",
  "al.kind.other": "Alert",
  "al.sub.power": "dying-gasp · the ONT lost electrical power",
  "al.sub.los": "no light at the ONT · fibre or optical side",
  "al.sub.offline": "down, cause not reported",
  "al.sub.optical": "ONT receive power",
  "al.sub.oltRx": "OLT receive power from this ONT",
  "al.sub.temperature": "ONT temperature",
  "al.sub.supply": "ONT voltage / current",
  "al.sub.other": "monitoring alert",
  "al.cause.power": "The ONT sent a dying-gasp: it lost electrical power. Usually the customer side (adapter, socket, power cut), not the fibre.",
  "al.cause.los": "Loss of signal: the ONT sees no light. Points at the fibre or optical path (drop cable, connector, splitter), not at power.",
  "al.cause.offline": "The OLT reports the ONT down without a cause.",
  "al.backOnlineNote": "The ONT is back online; this alert closes on the next collector poll.",
  "al.sevShort.critical": "Crit",
  "al.sevShort.warn": "Warn",
  "al.sevShort.info": "Info",
  "al.sev.critical": "critical",
  "al.sev.warn": "warning",
  "al.below": "{sev} below {v}",
  "al.above": "{sev} above {v}",
  "al.state.muted": "Muted",
  "al.state.ack": "Ack",
  "al.state.unack": "Unack",
  "al.sum.incidents": "{n} incidents",
  "al.sum.alerts": "{n} alerts",
  "al.sum.crit": "{n} critical",
  "al.sum.warn": "{n} warning",
  "al.sum.attention": "{n} need attention",
  "al.sum.down": "{n} offline",
  "al.sum.optical": "{n} optical",
  "al.quick.attention": "Needs attention",
  "al.quick.critical": "Critical",
  "al.quick.offline": "Offline",
  "al.quick.optical": "Optical",
  "al.quick.grouped": "Correlated",
  "al.quick.all": "All active",
  "al.search": "Search customer, serial, ONT #, OLT, PON, type…",
  "al.groupBy": "Group",
  "al.group.incident": "Incident",
  "al.group.none": "None (raw alerts)",
  "al.group.olt": "OLT",
  "al.group.pon": "PON",
  "al.group.type": "Type",
  "al.bulk.n": "{n} incidents selected ({alerts} alerts)",
  "al.bulk.clear": "Clear",
  "al.act.ack": "Acknowledge",
  "al.act.ackShort": "Ack",
  "al.act.ackAll": "Ack all",
  "al.act.assignMe": "Assign to me",
  "al.act.unassign": "Unassign",
  "al.act.mute1h": "Mute 1h",
  "al.act.mute": "Mute notifications {d}",
  "al.act.unmute": "Unmute",
  "al.act.details": "Details",
  "al.act.ont": "Open ONT",
  "al.act.pon": "View PON",
  "al.act.olt": "View OLT",
  "al.act.closeRecord": "Close alert record",
  "al.col.sev": "Sev",
  "al.col.incident": "Incident",
  "al.col.target": "Target",
  "al.col.current": "Current",
  "al.col.age": "Duration",
  "al.col.state": "State",
  "al.col.owner": "Owner",
  "al.selectAll": "Select all",
  "al.select": "Select",
  "al.actions": "Actions",
  "al.related": "{n} related alerts",
  "al.related1": "1 related alert",
  "al.more": "+{n} more",
  "al.openedAt": "Opened {at}",
  "al.cur.offline": "Offline",
  "al.cur.backOnline": "Back online",
  "al.cur.stale": "stale · {age} ago",
  "al.empty.attention": "Nothing needs attention: every active incident is acknowledged or muted ({n} active).",
  "al.empty.filter": "No incident matches.",
  "al.grp.ponOutage": "PON {pon} outage · {olt}",
  "al.grp.ponOutage.what": "{n} ONTs down within {spread}",
  "al.grp.ponOutage.hint": "possible shared network issue on this PON",
  "al.grp.powerOutage": "Possible area power outage · {olt}",
  "al.grp.powerOutage.what": "{n} ONTs reported dying-gasp within {spread}",
  "al.grp.powerOutage.hint": "many customers lost power at once; not confirmed",
  "al.grp.affected": "{n} of {total} ONTs",
  "al.grp.onts": "{n} ONTs",
  "al.ackTitle": "Acknowledge {n} incidents",
  "al.ackMeaning": "You have seen it and are handling it. It stays open until the condition recovers.",
  "al.note": "Note",
  "al.notePh": "e.g. customer contacted, technician dispatched",
  "al.partOf": "Part of: {what} ({n} ONTs)",
  "al.d.cause": "Likely meaning",
  "al.d.signal": "Current signal",
  "al.d.status": "Status",
  "al.d.timeline": "Timeline",
  "al.d.tech": "Technical details",
  "al.d.history": "Optical signal · last 24h",
  "al.metric.ont_rx_power_dbm": "ONT RX",
  "al.metric.ont_olt_rx_dbm": "OLT RX",
  "al.metric.ont_tx_power_dbm": "ONT TX",
  "al.metric.ont_temperature_c": "Temperature",
  "al.metric.ont_voltage_v": "Voltage",
  "al.metric.ont_current_ma": "Bias current",
  "al.metric.ont_run_state": "State",
  "al.measured": "measured {age} ago",
  "al.staleNote": "No fresh reading: the values shown are the last ones received, not the signal now.",
  "al.ackedBy": "Acknowledged by {who}",
  "al.notAcked": "Unacknowledged: nobody has taken it",
  "al.unassigned": "Unassigned",
  "al.mutedUntil": "Notifications muted until {at}",
  "al.ev.opened": "{what} opened",
  "al.ev.closed": "{what} closed by {who}",
  "al.ev.notified": "Notification sent",
  "al.ev.acknowledged": "Acknowledged by {who}",
  "al.ev.assigned": "Assigned by {who}",
  "al.ev.suppressed": "Notifications muted by {who}",
  "al.ev.unsuppressed": "Notifications unmuted by {who}",
  "al.system": "the system",
  "al.src.rule": "alert rule",
  "al.src.olt": "OLT collector",
  "al.tech.serial": "ONT serial {s}",
  "al.closeTitle": "Close alert record?",
  "al.closeBody": "This closes the record only. It does not fix or resolve anything.",
  "al.closeWarnTelemetry": "The signal still breaches its threshold: the monitor will open a new alert on its next evaluation.",
  "al.closeWarnDown": "The ONT is still down: closing hides the outage until the ONT changes state again.",
  "al.closeAlt": "To stop notifications while you work on it, mute it instead. Recovered conditions close by themselves.",
  "al.noHistory": "No optical readings in the last 24h.",
  "al.now": "now",
  "al.lastDays": "Last {n}d",
  "al.res.sum": "{n} resolved · {auto} recovered automatically",
  "al.res.opened": "Opened",
  "al.res.duration": "Duration",
  "al.res.how": "Resolution",
  "al.res.closedBy": "Closed by {who}",
  "al.res.auto": "Recovered automatically",
  "al.res.ackedBy": "acked by {who}",
  "al.res.none": "Nothing resolved in this window.",
  "al.res.note": "Telemetry alerts close themselves when the value recovers past the rule's hysteresis; offline alerts when the ONT is back online.",
  "al.ago": "{age} ago",
  "ua.title": "Users & access",
  "ua.invite": "Invite user",
  "ua.ago": "{age} ago",
  "ua.remove": "Remove",
  "ua.sum.active": "{n} active",
  "ua.sum.external": "{n} external",
  "ua.sum.pending": "{n} pending invitations",
  "ua.tab.users": "Users",
  "ua.tab.roles": "Roles & permissions",
  "ua.tab.invites": "Invitations",
  "ua.tab.audit": "Audit log",
  "ua.role.admin": "Admin",
  "ua.role.noc": "NOC",
  "ua.role.viewer": "Viewer",
  "ua.role.portviewer": "Port viewer",
  "ua.roleDesc.admin": "Full configuration and access control",
  "ua.roleDesc.noc": "Network monitoring and operational actions",
  "ua.roleDesc.viewer": "Read-only monitoring; customer details masked",
  "ua.roleDesc.portviewer": "Only the traffic graphs assigned to them",
  "ua.status.active": "Active",
  "ua.status.inactive": "Inactive",
  "ua.status.disabled": "Disabled",
  "ua.status.invited": "Invited",
  "ua.status.expired": "Expired",
  "ua.search": "Search users, roles, assigned ports…",
  "ua.quick.all": "All",
  "ua.quick.admin": "Admins",
  "ua.quick.noc": "NOC",
  "ua.quick.external": "External",
  "ua.quick.inactive": "Inactive",
  "ua.quick.never": "Never signed in",
  "ua.quick.disabled": "Disabled",
  "ua.col.user": "User",
  "ua.col.role": "Role",
  "ua.col.scope": "Access scope",
  "ua.col.status": "Status",
  "ua.col.lastActive": "Last active",
  "ua.col.security": "Security",
  "ua.invitedAgo": "invited {age} ago",
  "ua.expiresIn": "expires in {age}",
  "ua.expiredAgo": "expired {age} ago",
  "ua.acceptedAgo": "accepted {age} ago",
  "ua.sec.invite": "Invite link, not yet used",
  "ua.manage": "Manage",
  "ua.noMatch": "No user matches.",
  "ua.noPending": "No pending invitations.",
  "ua.scope.admin": "Entire system",
  "ua.scope.noc": "Entire network",
  "ua.scope.viewer": "Entire network · read-only",
  "ua.scope.none": "No ports assigned yet",
  "ua.scope.ports": "{n} assigned ports",
  "ua.you": "You",
  "ua.owner": "Owner",
  "ua.external": "External",
  "ua.never": "Never signed in",
  "ua.review": "Consider reviewing access",
  "ua.sec.password": "Password set",
  "ua.sec.noPassword": "No password",
  "ua.sec.sessions": "{n} active sessions",
  "ua.sec.oneSession": "1 active session",
  "ua.sec.noSessions": "No active session",
  "ua.sum.inactive": "{n} inactive",
  "ua.actions": "Actions for {email}",
  "ua.act.view": "View access",
  "ua.act.role": "Change role",
  "ua.act.graphs": "Assign graphs",
  "ua.act.security": "Security",
  "ua.act.reset": "Set a new password",
  "ua.act.sessions": "Revoke sessions",
  "ua.act.disable": "Disable account",
  "ua.act.enable": "Re-enable account",
  "ua.block.self": "You cannot change or disable your own account; ask another admin.",
  "ua.block.lastAdmin": "Cannot remove the last enabled administrator. Promote another admin first.",
  "ua.block.owner": "The owner account cannot be demoted or disabled by another admin.",
  "ua.block.notOwner": "Only the owner account may set another user's password.",
  "ua.block.ownerTarget": "Another owner's password cannot be set.",
  "ua.roleTitle": "Change role",
  "ua.current": "current",
  "ua.grants": "This grants",
  "ua.removes": "This removes",
  "ua.roleSessions": "Their open sessions end, so the new role takes effect at their next sign-in.",
  "ua.grantAdmin": "Grant Admin",
  "ua.changeRole": "Change role",
  "ua.disableTitle": "Disable account?",
  "ua.enableTitle": "Re-enable account?",
  "ua.disableBody": "They immediately lose access to JALABYTE OPS.",
  "ua.disableSessions": "Their {n} active sessions are revoked.",
  "ua.noDelete": "Accounts are disabled, never deleted, so the audit history keeps pointing at them. Re-enable restores access.",
  "ua.enableBody": "Access is restored with the role they had: {role}.",
  "ua.sessionsTitle": "Revoke all sessions?",
  "ua.sessionsBody": "Ends their {n} signed-in sessions. They stay enabled and can sign in again.",
  "ua.sessionsNote": "Use this for a lost laptop or a suspected compromise. Each open page stops working within minutes, when its short-lived access token expires.",
  "ua.sessionsDone": "{n} sessions revoked.",
  "ua.sec.access": "Access",
  "ua.sec.activity": "Activity",
  "ua.sec.security": "Security",
  "ua.sec.recent": "Recent access events",
  "ua.lastLogin": "Last sign-in",
  "ua.created": "Created",
  "ua.disabledAt": "Disabled",
  "ua.inactiveNote": "No activity for over {days} days: consider whether this access is still needed.",
  "ua.password": "Password",
  "ua.mfaNone": "Not available in JALABYTE OPS yet",
  "ua.sessionsLabel": "Sessions",
  "ua.selfNote": "This is your account: role changes and disabling must be done by another admin.",
  "ua.audit.none": "No access events recorded.",
  "ua.audit.someone": "Someone",
  "ua.audit.unknown": "an unknown account",
  "ua.audit.anyUser": "Any user",
  "ua.audit.note": "Access and security events only. Every account is kept in history, including disabled ones.",
  "ua.yes": "Yes",
  "ua.no": "No",
  "ua.grant.masked": "Masked",
  "ua.grant.assigned": "Assigned only",
  "ua.grant.superadmin": "Owner only",
  "ua.rolesIntro": "What each role can do. Access scope (which ports a port viewer sees) is set per user; the permission level comes from the role.",
  "ua.permission": "Permission",
  "ua.members": "{n} members",
  "ua.member": "1 member",
  "ua.group.monitor": "Monitoring",
  "ua.group.operate": "Operations",
  "ua.group.configure": "Configuration",
  "ua.group.access": "Access control",
  "ua.perm.network": "View overview, wallboard, topology and link map",
  "ua.perm.devices": "View OLTs, PONs and ONTs",
  "ua.perm.alerts": "View alerts",
  "ua.perm.customers": "See customer names and contacts",
  "ua.perm.graphs": "View traffic graphs",
  "ua.perm.ack": "Acknowledge and close alerts",
  "ua.perm.reboot": "Reboot ONTs",
  "ua.perm.ontEdit": "Edit ONT notes and CPE links",
  "ua.perm.wan": "View CPE WAN details",
  "ua.perm.olts": "Add and edit OLTs and their credentials",
  "ua.perm.acs": "Manage ACS servers and credentials",
  "ua.perm.integrations": "Test and sync integrations",
  "ua.perm.rules": "Edit alert rules and thresholds",
  "ua.perm.linkmap": "Edit the link map",
  "ua.perm.shares": "Create graph share links",
  "ua.perm.users": "Manage users, roles and invitations",
  "ua.perm.resetOthers": "Set another user's password",
  "ua.perm.ownPassword": "Change own password",
  "ua.inviteNote": "An invite link is shown once, when it is created: only its hash is stored. Resend issues a new link and cancels the old one.",
  "ua.inv.open": "Pending and expired",
  "ua.inv.none": "No pending invitations.",
  "ua.inv.resend": "Resend",
  "ua.inv.revoke": "Revoke",
  "ua.inv.history": "History",
  "ua.inv.acceptedN": "{n} accepted in the last 30 days",
  "ua.inv.historyNote": "Revoked invitations are in the audit log.",
  "ua.hide": "Hide",
  "ua.show": "Show",
  "ua.cat.all": "All",
  "ua.cat.role": "Role",
  "ua.cat.account": "Account",
  "ua.cat.invite": "Invitation",
  "ua.cat.password": "Password",
  "ua.cat.login": "Sign-in",
  "ua.cat.session": "Sessions",
  "ua.cat.scope": "Graph access",
  "ua.ev.role": "{actor} changed {target}'s role: {from} → {to}",
  "ua.ev.enabled": "{actor} re-enabled {target}",
  "ua.ev.disabled": "{actor} disabled {target}",
  "ua.ev.update": "{actor} updated {target}",
  "ua.ev.disable": "{actor} disabled {target}",
  "ua.ev.sessions_revoke": "{actor} revoked {n} sessions of {target}",
  "ua.ev.invite_create": "{actor} invited {target} as {to}",
  "ua.ev.invite_revoke": "{actor} revoked the invitation for {target}",
  "ua.ev.invite_accept": "{target} accepted the invitation as {to}",
  "ua.ev.password_reset": "{actor} set a new password for {target}",
  "ua.ev.password_reset_denied": "{actor} was refused setting {target}'s password",
  "ua.ev.password_change": "{target} changed their password",
  "ua.ev.password_fail": "{target} entered a wrong current password",
  "ua.ev.graph_ports_set": "{actor} changed the graphs {target} can see",
  "ua.ev.login_ok": "{target} signed in",
  "ua.ev.login_fail": "Failed sign-in for {target}",
  "ua.ev.logout": "{target} signed out",
  "ua.resendTitle": "Resend invitation",
  "ua.resendNote": "A new link replaces the old one, which stops working.",
  "ua.inviteTitle": "Invite user",
  "ua.linkOnce": "Share this link with them. It is shown only now; if lost, resend.",
  "ua.expiry": "Invitation expiry",
  "ua.days": "{n} days",
  "ua.can": "Can",
  "ua.cannot": "Cannot",
  "ua.pvNote": "Assign which graphs they may see after they accept.",
  "users.roleAdmin": "Admin",
  "users.roleNoc": "NOC",
  "users.roleViewer": "Viewer",
  "users.rolePortViewer": "Port viewer",
  "users.graphs": "Graphs",
  "users.graphAccess": "Graphs for {email}",
  "users.graphAccessEmpty": "No graphs assigned yet — this account will see an empty page.",
  "pw.change": "Password",
  "pw.changeTitle": "Change your password",
  "pw.resetTitle": "Set a new password for {email}",
  "pw.reset": "Password",
  "pw.current": "Current password",
  "pw.new": "New password",
  "pw.confirm": "Repeat new password",
  "pw.tooShort": "At least {n} characters.",
  "pw.mismatch": "The two passwords do not match.",
  "pw.changeNote": "You will be signed out of your other sessions.",
  "pw.resetNote": "Their other sessions end immediately. Tell them the new password over a channel you trust.",
  "pw.changeDone": "Password changed. Other sessions have been signed out.",
  "pw.resetDone": "Password set for {email}. Their sessions have been signed out.",

  "users.roleHint": "Admin manages users and settings. NOC works alerts and can reboot an ONT. Viewer reads only, with customer names masked. Port viewer sees nothing but the graphs you assign.",
  "users.status": "Status",
  "users.active": "active",
  "users.disabled": "disabled",
  "users.lastLogin": "Last login",
  "users.expires": "Expires",
  "users.enable": "Enable",
  "users.disable": "Disable",
  "users.revoke": "Revoke",
  "users.copy": "Copy",
  "users.copied": "Copied",
  "users.noUsers": "no accounts yet",
  "users.noInvites": "no invitations",
  "users.invite_pending": "pending",
  "users.invite_accepted": "accepted",
  "users.invite_expired": "expired",

  // Accept invite
  "invite.title": "Accept invitation",
  "invite.intro": "Set a password for {email} ({role}).",
  "invite.password": "Password",
  "invite.confirm": "Repeat password",
  "invite.mismatch": "The two passwords do not match.",
  "invite.pwHint": "At least 8 characters.",
  "invite.submit": "Create account",
  "invite.checking": "Checking the invitation…",
  "invite.noToken": "This link carries no invitation token.",

  "common.refresh": "Refresh",
  "common.retry": "Retry",
  "common.all": "All",
  "common.none": "None",
  "common.yes": "Yes",
  "common.no": "No",
  "common.enabled": "Enabled",
  "common.disabled": "Disabled",
  "common.loading": "Loading…",
  "common.empty": "No rows",
  "common.error": "Something went wrong",
  "common.confirm": "Are you sure?",
  "common.name": "Name",
  "common.description": "Description",
  "common.notes": "Notes",
  "common.status": "Status",
  "common.actions": "Actions",
  "common.details": "Details",
  "common.lastSeen": "Last seen",
  "common.updated": "Updated",
  "common.created": "Created",
  "common.signal": "Signal",
  "common.distance": "Distance",
  "common.uptime": "Uptime",
  "common.customer": "Customer",
  "common.reboot": "Reboot",
  "common.threshold": "Threshold",
  "common.recovered": "Recovered",
  "common.never": "Never",


  // overview
  "overview.openAlerts": "Open alerts",
  "overview.title": "Network overview",
  "ont.wan.lastKnown": "last known {ip}",
  "ont.live.online": "Online",
  "ont.live.offline": "Offline",
  "ont.live.missing": "Missing",
  "ont.live.stale": "Stale",
  "ont.live.unknown": "Unknown",
  "ont.rx.good": "Good",
  "ont.rx.moderate": "Moderate",
  "ont.rx.weak": "Weak",
  "ont.rx.critical": "Critical",
  "ont.rx.nodata": "No data",
  "ont.cause.dyingGasp": "Power loss detected (dying gasp)",
  "ont.cause.los": "Loss of signal (LOS)",
  "ont.cause.other": "Other cause",
  "ont.cause.none": "Cause not reported",
  "ont.quick.all": "All",
  "ont.quick.offline": "Offline",
  "ont.quick.weak": "Weak optical",
  "ont.quick.alerts": "Active alerts",
  "ont.quick.unlinked": "Unlinked",
  "ont.quick.stale": "Stale",
  "ont.list.summary": "{n} ONTs · {off} offline",
  "ont.list.search": "Serial, customer, ONT #, MAC or WAN IP…",
  "ont.list.anyOptical": "Any optical level",
  "ont.list.anyCause": "Any outage cause",
  "ont.list.neverDown": "Never down",
  "ont.list.last": "last",
  "ont.list.downFor": "offline {d}",
  "ont.list.ago": "{d} ago",
  "ont.list.alertsN": "{n} open alerts",
  "ont.list.range": "{a}–{b} of {n}",
  "ont.col.ont": "ONT / customer",
  "ont.col.oltPon": "OLT / PON",
  "ont.col.optical": "Optical",
  "ont.col.outage": "Last outage",
  "ont.h.seen": "seen {age} ago",
  "ont.h.uptime": "online {d}",
  "ont.h.offlineFor": "offline for {d}",
  "ont.act.refresh": "Refresh",
  "ont.act.openPon": "Open PON",
  "ont.act.more": "More",
  "ont.act.copy": "Copy",
  "ont.act.copySerial": "Copy serial",
  "ont.act.restartTr069": "Restart CPE via TR-069",
  "ont.act.restartOlt": "Restart ONT via OLT",
  "ont.act.restartWarn": "{name} will drop offline for a minute or two while it restarts. The customer loses service during that time.",
  "ont.act.restartConfirm": "Restart now",
  "ont.inc.active": "Active incident",
  "ont.inc.offline": "ONT offline",
  "ont.inc.missing": "ONT missing from the OLT",
  "ont.inc.started": "Started",
  "ont.inc.duration": "Duration",
  "ont.inc.lastRx": "Last RX",
  "ont.inc.alertsTitle": "Active alerts",
  "ont.inc.lastOutage": "Last outage",
  "ont.inc.none": "No active incidents",
  "ont.inc.neverDown": "No outage recorded",
  "ont.inc.cause": "Cause",
  "ont.inc.ongoing": "ongoing · {d}",
  "ont.kpi.rx": "ONT RX",
  "ont.kpi.lastRx": "Last RX",
  "ont.kpi.offlineFor": "Offline for",
  "ont.kpi.alerts": "Alerts",
  "ont.kpi.none": "None",
  "ont.cpe.online": "Online",
  "ont.cpe.silent": "No recent inform",
  "ont.cpe.unknown": "Unknown",
  "ont.cpe.none": "Not linked",
  "ont.cpe.acsDown": "ACS unreachable",
  "ont.wan.up": "Up",
  "ont.wan.down": "Down",
  "ont.wan.unknown": "Unknown",
  "ont.wan.none": "None",
  "ont.wan.roleHidden": "WAN details are visible to admin and NOC roles only.",
  "ont.conn.up": "Up",
  "ont.conn.down": "Down",
  "ont.conn.disabled": "Disabled",
  "ont.conn.unknown": "Unknown",
  "ont.cust.title": "Customer / CPE",
  "ont.cust.notLinked": "Not linked",
  "ont.cust.link": "Link subscriber",
  "ont.bill.title": "Billing",
  "ont.bill.active": "Active",
  "ont.bill.suspended": "Suspended",
  "ont.bill.pending": "Pending",
  "ont.bill.suspendedNote": "Service suspended in WHMCS: an offline ONT may be a billing block, not a fault.",
  "ont.cust.inform": "inform {age} ago",
  "ont.opt.title": "Optical",
  "ont.opt.history": "History",
  "ont.opt.down": "OLT → ONT",
  "ont.opt.up": "ONT → OLT",
  "ont.opt.ontRx": "ONT RX (received by the ONT)",
  "ont.opt.oltRx": "OLT RX (received by the OLT)",
  "ont.opt.oltRxDir": "OLT RX: the ONT's signal as received at the OLT",
  "ont.opt.stale": "stale reading",
  "ont.opt.lastKnown": "last known",
  "ont.opt.thresholds": "Thresholds",
  "ont.opt.bands": "weak < {w} · critical < {c} dBm",
  "ont.opt.sampled": "Sampled",
  "ont.opt.current": "Current",
  "ont.opt.avg": "Average",
  "ont.opt.min": "Min",
  "ont.opt.max": "Max",
  "ont.opt.gapNote": "Gaps mark periods with no reading (ONT offline or telemetry missing); they are never drawn as 0 dBm.",
  "ont.opt.now": "Current readings",
  "ont.opt.ponCompare": "Compared with PON {pon}",
  "ont.opt.ponAvg": "PON average RX",
  "ont.opt.thisOnt": "This ONT",
  "ont.opt.rank": "Rank",
  "ont.opt.rankOf": "#{r} weakest of {n} online",
  "ont.path.title": "Network path",
  "ont.path.noCpe": "no CPE linked",
  "ont.ev.recent": "Recent events",
  "ont.ev.all": "Full history",
  "ont.ev.history": "Event history",
  "ont.ev.oltReported": "reported by the OLT",
  "ont.notes.add": "Add note",
  "ont.notes.none": "No operational notes",
  "ont.mac.none": "The OLT has not reported any MAC behind this ONT.",
  "ont.tab.overview": "Overview",
  "ont.tab.optical": "Optical",
  "ont.tab.wan": "WAN / CPE",
  "ont.tab.events": "Events",
  "ont.tab.technical": "Technical",
  "ont.tech.identity": "Identity",
  "ont.tech.internalId": "Internal id",
  "ont.tech.provisioning": "Provisioning & state",
  "olt.si.offlineElsewhere": "{n} more ONTs offline on other PONs",
  "olt.lastValue": "last {v}",
  "olt.tele.noData": "No data · unreachable",
  "olt.pollFailed": "POLL FAILED",
  "olt.ab.oltDown": "All {n} ONTs are unreachable with the OLT; they are listed under the ONTs tab.",
  "olt.fleet.title": "OLT fleet",
  "olt.fleet.summary": "{n} OLTs · {healthy} healthy",
  "olt.fleet.all": "All",
  "olt.fleet.abnormal": "Needs attention",
  "olt.fleet.search": "Search OLT, IP, site…",
  "olt.fleet.noMatch": "No OLT matches this filter.",
  "olt.col.olt": "OLT",
  "olt.col.health": "Health",
  "olt.col.pon": "PON",
  "olt.col.ont": "ONT online",
  "olt.col.offline": "Offline",
  "olt.col.warn": "Warnings",
  "olt.col.lastPoll": "Last poll",
  "olt.state.healthy": "Healthy",
  "olt.state.degraded": "Degraded",
  "olt.state.critical": "Critical",
  "olt.state.down": "Down",
  "olt.state.stale": "Stale",
  "olt.state.unknown": "Unknown",
  "olt.reason.unreachable": "OLT unreachable",
  "olt.reason.neverPolled": "never polled",
  "olt.reason.stale": "no poll for 3 intervals",
  "olt.reason.ponDown": "a PON is dark",
  "olt.reason.manyOffline": "25%+ ONTs offline",
  "olt.reason.offline": "ONTs offline",
  "olt.reason.partial": "CLI read partially",
  "olt.reason.hot": "running hot",
  "olt.ago": "{age} ago",
  "olt.never": "never polled",
  "olt.polledAgo": "polled {age} ago",
  "olt.uptime": "up {up}",
  "olt.staleTag": "STALE",
  "olt.notReported": "not reported",
  "olt.lastReported": "last report {v} · {at}",
  "olt.kpi.pon": "PON",
  "olt.kpi.ont": "ONT",
  "olt.kpi.online": "Online",
  "olt.kpi.offline": "Offline",
  "olt.kpi.warnings": "Warnings",
  "olt.tab.overview": "Overview",
  "olt.tab.pons": "PONs",
  "olt.tab.onts": "ONTs",
  "olt.tab.traffic": "Traffic",
  "olt.tab.health": "Health",
  "olt.tab.events": "Events",
  "olt.si.title": "Service impact",
  "olt.si.all": "All offline",
  "olt.si.none": "No customers offline",
  "olt.si.oltDown": "OLT unreachable",
  "olt.si.potentially": "{n} ONTs potentially affected",
  "olt.si.ponDown": "PON {pon} is dark",
  "olt.si.offline": "{n} ONTs offline",
  "olt.powerLoss": "power loss (dying gasp)",
  "olt.moreN": "+{n} more",
  "olt.offlineN": "{n} offline",
  "olt.weakN": "{n} weak",
  "olt.downFor": "down {age}",
  "olt.h.title": "Device health",
  "olt.h.reach": "Reachability",
  "olt.h.telemetry": "Telemetry",
  "olt.h.uptime": "Uptime",
  "olt.h.memory": "Memory",
  "olt.h.temp": "Temperature",
  "olt.h.fan": "Fan",
  "olt.h.sensors": "Environment sensors",
  "olt.reach.reachable": "Up",
  "olt.reach.unreachable": "Down",
  "olt.reach.degraded": "Up · partial read",
  "olt.reach.unknown": "Unknown",
  "olt.reach.up": "Up",
  "olt.reach.down": "Down",
  "olt.tele.fresh": "Fresh",
  "olt.tele.stale": "Stale",
  "olt.fan.ok": "Normal",
  "olt.fan.fault": "Fault",
  "olt.info.title": "Device information",
  "olt.info.ip": "Management IP",
  "olt.info.mgmt": "Management",
  "olt.info.advanced": "Advanced",
  "olt.info.lastError": "Last error",
  "olt.info.cliNote": "CLI note",
  "olt.pon.title": "PON health",
  "olt.pon.otherHealthy": "{n} other PONs healthy",
  "olt.pon.more": "+{n} more PONs",
  "olt.pon.col": "PON",
  "olt.pon.weak": "Weak RX",
  "olt.pon.traffic": "Traffic",
  "olt.pon.onlyAbnormal": "Only PONs that need attention ({n})",
  "olt.pon.back": "Back to PONs",
  "olt.pon.onts": "ONTs",
  "olt.pon.toOnts": "OLT → ONTs (TX)",
  "olt.pon.fromOnts": "ONTs → OLT (RX)",
  "olt.pon.noTraffic": "This OLT does not report per-PON counters.",
  "olt.pon.allOnts": "All ONTs on this PON ({n})",
  "olt.ponTier.down": "Down",
  "olt.ponTier.degraded": "Degraded",
  "olt.ponTier.warn": "Weak optics",
  "olt.ponTier.healthy": "Healthy",
  "olt.opt.title": "Optical health",
  "olt.opt.analysis": "Optical analysis",
  "olt.opt.good": "Good",
  "olt.opt.moderate": "Moderate",
  "olt.opt.weak": "Weak",
  "olt.opt.critical": "Critical",
  "olt.opt.noReading": "{n} without a current reading (offline)",
  "olt.opt.avg": "Average RX",
  "olt.opt.worst": "Weakest RX",
  "olt.opt.ponWeak": "PON {pon}: {n} weak",
  "olt.p.offline": "Offline",
  "olt.p.missing": "Missing",
  "olt.p.rxCrit": "Critical RX",
  "olt.p.rxWarn": "Low RX",
  "olt.ab.title": "Abnormal ONTs",
  "olt.ab.count": "{n} of {total}",
  "olt.ab.none": "Every ONT is online with a healthy signal.",
  "olt.ab.ont": "ONT / customer",
  "olt.ab.problem": "Problem",
  "olt.ab.distance": "Distance",
  "olt.ab.allAbnormal": "All {n} abnormal",
  "olt.ab.allOnts": "View all {n} ONTs",
  "olt.inv.search": "Search serial, customer, PON…",
  "olt.inv.serial": "Serial",
  "olt.inv.since": "Down for",
  "olt.inv.allPons": "All PONs",
  "olt.inv.none": "No ONT matches.",
  "olt.inv.showAll": "Show all {n}",
  "olt.inv.f.all": "All",
  "olt.inv.f.abnormal": "Abnormal",
  "olt.inv.f.offline": "Offline",
  "olt.inv.f.weak": "Weak RX",
  "olt.inv.f.online": "Online",
  "olt.tr.perspective": "OLT perspective",
  "olt.tr.txMeans": "sent by the OLT (to the ONTs on a PON port)",
  "olt.tr.rxMeans": "received by the OLT",
  "olt.tr.cli": "Port counters · OLT CLI",
  "olt.tr.cliNote": "Read from the OLT's own counters each poll and averaged between polls. Current values only; no history is kept for these.",
  "olt.tr.sampled": "Sampled",
  "olt.tr.history": "Interfaces & history · LibreNMS",
  "olt.tr.historyNote": "Click an interface for its traffic history.",
  "olt.tr.notMirrored": "LibreNMS does not mirror this OLT's interfaces, so no history is available.",
  "olt.tr.speed": "Speed",
  "olt.tr.util": "Util",
  "olt.ev.none": "No events in this window.",
  "olt.ev.cleared": "cleared",
  "olt.ev.online": "ONT back online",
  "olt.ev.offline": "ONT went offline",
  "olt.ev.down_cause": "Down cause reported",
  "olt.ev.alarm": "OLT alarm",
  "olt.ev.discovered": "ONT discovered",
  "olt.ev.f.all": "All",
  "olt.ev.f.critical": "Critical",
  "olt.ev.f.warning": "Warning",
  "olt.ev.f.ont": "ONT",
  "olt.ev.f.device": "Device",
  "wb.apiDown": "Cannot reach the NocOps API",
  "wb.stale": "DATA STALE",
  "wb.live": "LIVE",
  "wb.polled": "OLT polled {age} ago",
  "wb.network": "Network",
  "wb.state.healthy": "HEALTHY",
  "wb.state.degraded": "DEGRADED",
  "wb.state.major": "MAJOR INCIDENT",
  "wb.state.unknown": "UNKNOWN",
  "wb.olt": "OLT",
  "wb.online": "Online",
  "wb.ont": "ONT",
  "wb.down": "Down",
  "wb.ontsOffline": "ONTs offline",
  "wb.incidents": "Incidents",
  "wb.correlated": "correlated",
  "wb.warnings": "Warnings",
  "wb.alertsOpen": "open alerts: {n}",
  "wb.majorIncident": "Major incident",
  "wb.more": "more",
  "wb.oltDown": "OLT DOWN",
  "wb.ponDown": "PON DOWN",
  "wb.ontsPotentially": "ONTs potentially affected",
  "wb.downFor": "down for",
  "wb.staleApi": "The wallboard has not reached the API for {age}.",
  "wb.staleNever": "No OLT poll has ever been recorded.",
  "wb.stalePoll": "Last OLT poll {age} ago.",
  "wb.staleTitle": "MONITORING DATA IS STALE",
  "wb.staleHint": "The values below may not reflect the network now.",
  "wb.serviceImpact": "Service impact",
  "wb.ontsAffected": "{n} ONTs affected",
  "wb.moreIncidents": "+{n} more incidents · {onts} ONTs",
  "wb.noImpact": "No service impact",
  "wb.kind.olt": "OLT down",
  "wb.kind.pon": "PON down",
  "wb.kind.multi": "{n} ONTs offline",
  "wb.kind.ont": "ONT offline",
  "wb.potentiallyN": "{n} ONTs potentially affected",
  "wb.onlineOf": "{on}/{all} online",
  "wb.powerLoss": "Power loss detected (dying gasp)",
  "wb.lastCause": "Last cause: {cause}",
  "wb.sitesWord": "sites",
  "wb.oltsWord": "OLTs",
  "wb.ponsWord": "PONs",
  "wb.sites": "POP / site health",
  "wb.fleet": "OLT fleet",
  "wb.oltsOnline": "{up}/{all} online",
  "wb.otherHealthy": "{n} other {unit} healthy",
  "wb.moreAbnormal": "+{n} more {unit} · {bad} abnormal",
  "wb.noSite": "No site",
  "wb.offlineN": "{n} offline",
  "wb.oltPartial": "Partially polled",
  "wb.hot": "Running hot",
  "wb.up": "up",
  "wb.polledAgo": "polled {age} ago",
  "wb.ponHealth": "PON health",
  "wb.ponsFull": "{up}/{all} healthy",
  "wb.weakN": "{n} weak",
  "wb.downN": "down {age}",
  "wb.noPons": "No PON ports polled yet",
  "wb.collectorError": "Collector failing",
  "wb.noWarnings": "No other warnings",
  "wb.ponTrouble": "PON outage",
  "incident.ont.temperature_c": "ONT temperature",
  "incident.ont.voltage_v": "ONT supply voltage",
  "incident.ont.tx_power_dbm": "ONT TX power",
  "incident.ont.current_ma": "ONT laser bias current",
  "wb.unlisted": "+{n} more warnings not shown here — see Alerts",
  "wb.opticalElsewhere": "{n} optical alerts are counted under Optical health.",
  "wb.moreWarnings": "+{n} more warnings",
  "wb.optical": "Optical health",
  "wb.belowWarn": "ONTs below {dbm} dBm",
  "wb.noOptical": "No fresh optical readings",
  "wb.uplinks": "Busiest links",
  "wb.noUplinks": "No link traffic mirrored",
  "lm.devices": "Devices",
  "lm.up": "Up",
  "lm.links": "Links",
  "lm.down": "Down",
  "lm.downTitle": "{d} device(s) and {l} link(s) down",
  "lm.search": "Search device, IP, interface…",
  "lm.noResults": "No match",
  "lm.filters": "Filters & layers",
  "lm.reset": "Reset view",
  "lm.zoomIn": "Zoom in",
  "lm.zoomOut": "Zoom out",
  "lm.layers": "Layers",
  "lm.labels": "Labels",
  "lm.visualization": "Visualization",
  "lm.status": "Status",
  "lm.deviceType": "Device type",
  "lm.layerMonitored": "Monitored devices",
  "lm.layerManual": "Manual links",
  "lm.layerDiscovered": "Discovered links",
  "lm.layerForeign": "Foreign devices",
  "lm.labelNames": "Device names",
  "lm.labelIps": "Management IP",
  "lm.labelIfaces": "Interface names",
  "lm.labelCapacity": "Link capacity",
  "lm.labelRates": "RX/TX values",
  "lm.visUtil": "Link utilization",
  "lm.visFlow": "Traffic flow animation",
  "lm.st.up": "Up",
  "lm.st.warn": "Warning",
  "lm.st.down": "Down",
  "lm.st.unknown": "Unknown",
  "lm.st.stale": "Stale",
  "lm.role.core": "Core",
  "lm.role.agg": "Aggregation",
  "lm.role.router": "Router",
  "lm.role.switch": "Switch",
  "lm.role.olt": "OLT",
  "lm.role.device": "Network device",
  "lm.role.foreign": "Foreign (LLDP/CDP)",
  "lm.inferred": "inferred",
  "lm.pinned": "marked by you",
  "lm.setCore": "Mark as core",
  "lm.unsetCore": "Unmark core",
  "lm.coreTitle": "Path to core and impact use this device as the core (saved in this browser)",
  "lm.link.healthy": "< 50%",
  "lm.link.moderate": "50–80%",
  "lm.link.high": "> 80%",
  "lm.link.down": "Down",
  "lm.link.nodata": "No data",
  "lm.help": "Legend & map help",
  "lm.help1": "Line colour and thickness follow utilization; a red ✕ marks a down link.",
  "lm.help2": "Dashed lines were discovered over LLDP/CDP; solid lines were drawn by an operator.",
  "lm.help3": "Dim dashed boxes are foreign devices: discovered, not monitored, no telemetry.",
  "lm.help4": "Roles are inferred: the device with the most monitored neighbours is treated as core.",
  "lm.help5": "Zoom in for interface names and traffic; zoomed out, only core, aggregation, OLTs and problems are labelled.",
  "lm.help6": "Drag a device to place it; positions persist across reloads.",
  "lm.linkDetails": "Link details",
  "lm.deviceDetails": "Device details",
  "lm.capacity": "Capacity",
  "lm.traffic": "Traffic",
  "lm.utilization": "Utilization",
  "lm.originManual": "Drawn by operator",
  "lm.originDiscovered": "Discovered via LLDP/CDP",
  "lm.graph": "Traffic graph",
  "lm.viewPorts": "View ports",
  "lm.pathToCore": "Path to core",
  "lm.clear": "Clear",
  "lm.alerts": "Alerts",
  "lm.trafficGraphs": "Traffic graphs",
  "lm.mgmt": "Management",
  "lm.platform": "Platform",
  "lm.uptime": "Uptime",
  "lm.load": "CPU / memory",
  "lm.noLinksHere": "No links to this device.",
  "lm.foreignNote": "Discovered via {proto}. Not monitored: no telemetry available.",
  "lm.impactPotential": "Potential impact",
  "lm.impactIfFails": "If this fails",
  "lm.impactInferred": "inferred from topology",
  "lm.impactNone": "Nothing downstream loses its path to core.",
  "lm.impactDevices": "devices",
  "lm.impactOnts": "ONTs",
  "lm.impactOntsOnline": "{n} online now",
  "lm.confirmed": "Confirmed down",
  "lm.potential": "Potentially cut off",
  "lm.showImpact": "Show on map",
  "lm.relatedAlerts": "Related alerts",
  "lm.pathNone": "No path to a core device.",
  "lm.pathSingle": "Single-homed: no backup path.",
  "lm.pathPrimary": "Primary",
  "lm.pathBackup": "Backup",
  "lm.hops": "hops: {n}",
  "lm.impactOf": "Impact of {name}",
  "lm.noCore": "No core device can be inferred without links.",
  "lm.close": "Close",
  "lm.edit": "Edit link",
  "overview.health": "Health",
  "overview.healthHint": "ONTs online",
  "overview.affected": "Affected",
  "overview.affectedHint": "customers offline",
  "overview.incidents": "Incidents",
  "overview.activeIncidents": "Active incidents",
  "overview.viewAll": "View all",
  "overview.inspect": "Inspect",
  "overview.noIncidents": "No open incidents. The network is quiet.",
  "overview.networkStatus": "Network status",
  "overview.collectors": "Collectors",
  "overview.api": "API",
  "overview.healthy": "Healthy",
  "overview.unreachable": "Unreachable",
  "overview.customerOffline": "customer offline",
  "incident.ont.run_state": "ONT offline",
  "incident.ont.rx_power_dbm": "Optical RX power",
  "incident.ont.olt_rx_dbm": "OLT RX power",
  "incident.olt.unreachable": "OLT unreachable",
  "nav.group.network": "Network",
  "nav.group.operations": "Operations",
  "nav.group.admin": "Admin",
  "live.live": "Live",
  "live.stale": "Stale",
  "live.agoS": "updated {n} s ago",
  "live.agoM": "updated {n} min ago",
  "topbar.openAlerts": "{n} open alerts",

  // OLT list / form
  "olts.add": "Add OLT",
  "olts.editTitle": "Edit {name}",
  "olts.empty": "no OLTs registered yet",
  "olts.mgmtIp": "Management IP",
  "olts.driver": "Driver",
  "olts.port": "Port (blank = driver default)",
  "olts.cliUser": "CLI username",
  "olts.cliPass": "CLI password",
  "olts.cliPassKeep": "CLI password (blank = keep)",
  "olts.enablePass": "Enable password (optional)",
  "olts.location": "Location (optional)",
  "olts.vendor": "Vendor",
  "olts.model": "Model",
  "olts.firmware": "Firmware",

  // OLT detail
  "oltDetail.location": "Location",
  "oltDetail.lastPolled": "Last polled",
  "oltDetail.noPorts": "no PON ports registered",

  // ONT list
  "onts.allOlts": "all OLTs",
  "onts.allPons": "all PONs",
  "onts.empty": "no ONTs match",
  "onts.serial": "Serial",
  "onts.number": "ONT #",
  "onts.distance": "Distance",

  // ONT detail
  "ontDetail.runState": "Run state",
  "ontDetail.configState": "Config state",
  "ontDetail.matchState": "Match state",
  "ontDetail.lastUp": "Last up",
  "ontDetail.lastDown": "Last down",
  "ontDetail.lastDyingGasp": "Last dying gasp",
  "ontDetail.lastDownCause": "Last down cause",
  "ontDetail.ponPath": "PON path",
  "ontDetail.index": "Index",
  "ontDetail.oltIp": "OLT IP",
  "ontDetail.oltStatus": "OLT status",
  "ontDetail.onuType": "ONU type",
  "ontDetail.subscriber": "Subscriber",
  "ontDetail.matchedBy": "Matched by",
  "ontDetail.acsServer": "ACS server",
  "ontDetail.deviceId": "Device ID",
  "ontDetail.manufacturer": "Manufacturer",
  "ontDetail.wanMac": "WAN MAC",
  "ontDetail.wanIp": "WAN IP",
  "ontDetail.lastInform": "Last inform",
  "ontDetail.lastBoot": "Last boot",
  "ontDetail.change": "Change",
  "ontDetail.unlink": "Unlink",
  "ontDetail.noCpe": "No CPE matched — GenieACS and the OLT often report different serials.",
  "ontDetail.linkCpe": "Link CPE",
  "ontDetail.cpeConflict": "That CPE is already linked to another ONT — {msg}. Unlink it there first.",
  "ontDetail.notesPlaceholder": "e.g. customer X, second floor, spare ONT in stock…",
  "ontDetail.learnedMacs": "Learned MACs",
  "ontDetail.macsFrom": "{n} from the OLT MAC table",
  "ontDetail.vlan": "vlan",
  "ontDetail.opticalHistory": "Optical history",
  "ontDetail.noSamples": "no samples in this window",
  "ontDetail.noEvents": "no events recorded",
  "ontDetail.rebootedByOperator": "rebooted by operator",
  "ontDetail.noOpenAlerts": "no open alerts",
  "ontDetail.rebootTr069": "Reboot via TR-069",
  "ontDetail.rebooting": "Rebooting…",
  "ontDetail.rebootSentTr069": "Reboot sent over TR-069 (GenieACS).",
  "ontDetail.rebootSentOlt": "Reboot sent over the OLT CLI.",
  "ontDetail.pickerTitle": "Link a CPE",
  "ontDetail.pickerDesc": "Search GenieACS devices by serial, device ID, MAC or model, then link the right one.",
  "ontDetail.pickerPlaceholder": "serial, device ID, MAC or model",
  "ontDetail.noCpeMatch": "no CPEs match",
  "ontDetail.linked": "Linked",
  "ontDetail.link": "Link",

  // optical metric names
  "metric.rxPower": "Rx power",
  "metric.txPower": "Tx power",
  "metric.oltRx": "OLT Rx",
  "metric.temperature": "Temperature",
  "metric.voltage": "Voltage",
  "metric.current": "Current",

  // ONT event names
  "event.down_cause": "Down cause",

  // topology

  // link map
  "netmap.addLink": "Add link",
  "netmap.editLink": "Edit link",
  "netmap.autoArrange": "Auto arrange",
  "netmap.autoArrangeTitle": "Discard every position you dragged and go back to the automatic layout",
  "netmap.autoArrangeConfirm": "Discard all dragged device positions and re-run the automatic layout?",
  "netmap.fit": "Fit",
  "netmap.posNotSaved": "Device position not saved: {msg}",
  "netmap.autoArrangeFailed": "Auto arrange failed: {msg}",
  "netmap.noDevices": "No devices mirrored yet.",
  "netmap.noDevicesHint": "This map is populated by the LibreNMS collector — it needs a LibreNMS URL and API token, and one completed sync.",
  "netmap.configureLink": "Configure and run the LibreNMS collector →",
  "netmap.noLinks": "{n} devices mirrored, but nothing links them yet.",
  "netmap.noLinksHint": "LibreNMS neighbour discovery (CDP/LLDP) reported no adjacency between your own devices — every neighbour it found belongs to someone else, and those are hidden by default. Draw the real topology yourself:",
  "netmap.noLinksAdmin": "“Add link” above joins two of your devices, optionally pinned to ports so the link carries live Rx/Tx.",
  "netmap.noLinksViewer": "an admin can add links from this page.",
  "netmap.peersExist": " {n} foreign device(s) exist and can be shown with the toggle above.",
  "netmap.svgLabel": "Network link map: {nodes} devices, {links} links. Drag to pan, scroll to zoom.",
  "netmap.svgTitle": "Network link map: {nodes} devices, {links} links",
  "netmap.aSide": "A side",
  "netmap.bSide": "B side",
  "netmap.pickDevice": "pick a device",
  "netmap.portOptional": "Port (optional — gives the link its Rx/Tx)",
  "netmap.noPort": "no port (no rates)",
  "netmap.labelOptional": "Label (optional)",
  "netmap.labelPlaceholder": "e.g. 10G trunk",
  "netmap.needTwo": "a link needs two different devices",
  "netmap.discoveredNoGraph": "LibreNMS graphs are available for links drawn on this page; this one comes from LLDP/CDP discovery.",
  "netmap.noPortPinned": "no port pinned on this side",
  "netmap.noPortPinnedAdmin": " — edit the link and pick one to get a graph",
  "netmap.speed": "speed",
  "netmap.loadingPorts": "loading ports…",
  "netmap.notMirrored": "This device is not in the LibreNMS mirror — run a LibreNMS sync.",
  "netmap.noPortsMirrored": "no ports mirrored for this device",
  "netmap.peerNoPorts": "{name} is not monitored by LibreNMS — it was discovered over {proto}, so it has no ports here.",
  "netmap.portsName": "name",
  "netmap.portsAlias": "alias",
  "netmap.portsUpDown": "up/down",
  "netmap.portsRx": "current Rx",
  "netmap.portsTx": "current Tx",
  "netmap.rowTitle": "Click for the LibreNMS graph",
  "netmap.openOlt": "Open OLT page",

  // traffic graphs
  "graphs.subtitle": "traffic in and out, from LibreNMS",
  "graphs.port": "Port",
  "graphs.searchPlaceholder": "search device, ifName or description…",
  "graphs.devicePlaceholder": "device",
  "graphs.portPlaceholder": "port",
  "graphs.inOut": "in {rx} · out {tx}",
  "graphs.capped": "list truncated — narrow the search to see the rest",
  "graphs.noMatch": "nothing matches that search",
  "graphs.noDevices": "no LibreNMS devices mirrored yet — run a LibreNMS sync",
  "graphs.trafficTitle": "Traffic in/out · {win}",
  "graphs.pick": "pick a device and a port",
  "graphs.alt": "LibreNMS throughput for {device} {port} over {win}",
  "graph.unavailable": "LibreNMS graph unavailable: {msg}",
  "graph.loading": "loading graph…",
  "graph.none": "no graph",

  // integrations
  "integrations.subtitle": "External systems and their last collector run.",
  "integrations.test": "Test",
  "integrations.testing": "Testing…",
  "integrations.sync": "Sync",
  "integrations.syncing": "Syncing…",
  "integrations.verified": "Connection verified",
  "integrations.synced": "Synced: {onts} ONTs, {events} events",
  "integrations.syncStarted": "Sync started",
  "integrations.loadingRuns": "loading runs…",
  "integrations.noRuns": "no runs recorded",
  "integrations.finished": "Finished",
  "lm.viewTopology": "View in Topology",
  "topo.expand": "Expand",
  "topo.inferred": "inferred from name",
  "topo.collapse": "Collapse",
  "topo.subtitle": "Where each thing sits in the network, and where in that hierarchy a problem is.",
  "topo.state.down": "Down",
  "topo.state.critical": "Critical",
  "topo.state.degraded": "Degraded",
  "topo.state.stale": "Stale",
  "topo.state.warn": "Warning",
  "topo.state.unknown": "Unknown",
  "topo.state.healthy": "Healthy",
  "topo.conf.confirmed": "Confirmed",
  "topo.conf.discovered": "Discovered",
  "topo.conf.manual": "Manual",
  "topo.conf.confirmed.hint": "both ends report it",
  "topo.conf.discovered.hint": "one end reports it",
  "topo.conf.manual.hint": "set by an operator",
  "topo.fresh.ftth": "FTTH",
  "topo.fresh.discovery": "Discovery",
  "topo.fresh.stale": "Stale",
  "topo.fresh.none": "No data",
  "topo.fresh.current": "Current",
  "topo.view.infra": "Infrastructure",
  "topo.view.discovery": "Discovery",
  "topo.view.unresolved": "Unresolved",
  "topo.filter.all": "All",
  "topo.filter.problems": "Problems only",
  "topo.noProblems": "No problems anywhere in the hierarchy.",
  "topo.empty": "No sites, OLTs or devices yet.",
  "topo.sum.sites": "Sites",
  "topo.sum.olts": "OLTs",
  "topo.sum.pons": "PONs",
  "topo.sum.onts": "ONTs online",
  "topo.sum.devices": "Network devices",
  "topo.sum.links": "Links",
  "topo.sum.unresolved": "Unresolved",
  "topo.sum.confirmedN": "{n} confirmed",
  "topo.issues": "Infrastructure issues · {n}",
  "topo.allHealthy": "Infrastructure healthy",
  "topo.unassigned": "Not assigned to a site",
  "topo.unassignedHint": "These devices have no location in LibreNMS and no site in their name. Set a location in LibreNMS to place them.",
  "topo.nOlt": "{n} OLT",
  "topo.nPon": "{n} PON",
  "topo.onlineOf": "{on}/{total} ONT online",
  "topo.nDev": "{n} devices",
  "topo.access": "Access network",
  "topo.infra": "Network infrastructure",
  "topo.nOffline": "{n} offline",
  "topo.noPons": "No PON ports recorded yet.",
  "topo.nWeak": "{n} weak optical",
  "topo.othersHealthy": "{n} other ONTs healthy",
  "topo.openPon": "Open PON",
  "topo.nDown": "{n} down",
  "topo.nLinks": "{n} links",
  "topo.noLinks": "no links",
  "topo.search": "Search topology: device, IP, OLT, PON, ONT serial or customer…",
  "topo.noHits": "Nothing matches.",
  "topo.kind.site": "Site",
  "topo.kind.olt": "OLT",
  "topo.kind.pon": "PON",
  "topo.kind.ont": "ONT",
  "topo.kind.device": "Device",
  "topo.locate": "Locate",
  "topo.detail.pick": "Select a node to see where it sits and what it affects. Health rolls up: an offline ONT degrades its PON and OLT; only a dark PON or a down OLT makes a site critical.",
  "topo.detail.reach": "Reachability",
  "topo.detail.temp": "Temperature",
  "topo.detail.alerts": "Open alerts",
  "topo.detail.online": "Online",
  "topo.detail.offline": "Offline",
  "topo.detail.weak": "Weak optical",
  "topo.detail.lastDrop": "Latest drop",
  "topo.detail.cause": "Down cause",
  "topo.detail.rx": "Rx",
  "topo.detail.status": "Status",
  "topo.detail.site": "Site",
  "topo.detail.links": "Discovered links",
  "topo.reach.down": "Unreachable",
  "topo.reach.unknown": "Not polled yet",
  "topo.reach.up": "Reachable",
  "topo.polled": "polled {age} ago",
  "topo.openOlt": "Open OLT",
  "topo.viewMap": "View in Link Map",
  "topo.ontList": "ONT list",
  "topo.openOnt": "Open ONT",
  "topo.dev.up": "Up",
  "topo.dev.down": "Down",
  "topo.dev.unknown": "Unknown",
  "topo.siteInferred": "inferred from the device name",
  "topo.nUnresolvedFrom": "{n} neighbours it reports could not be resolved — see Unresolved.",
  "topo.discStaleShort": "Discovery is stale: these links may be out of date.",
  "topo.discStale": "LibreNMS discovery is stale: relationships below may be out of date. The FTTH hierarchy comes from the OLT collector and has its own freshness.",
  "topo.legend": "States",
  "topo.legendHint": "Healthy rows show only a green dot; anything else is labelled.",
  "topo.disc.source": "Source",
  "topo.disc.synced": "synced {age} ago",
  "topo.disc.devices": "devices",
  "topo.disc.links": "links",
  "topo.disc.unresolved": "unresolved",
  "topo.disc.knownDevices": "Known devices",
  "topo.disc.relationships": "Relationships",
  "topo.col.device": "Device",
  "topo.col.platform": "Platform",
  "topo.col.site": "Site",
  "topo.col.links": "Links",
  "topo.col.status": "Status",
  "topo.col.local": "Device",
  "topo.col.remote": "Neighbour",
  "topo.col.method": "Method",
  "topo.col.confidence": "Confidence",
  "topo.isOlt": "OLT",
  "topo.viaN": "via {n} interfaces",
  "topo.unres.intro": "Far ends discovery reported but could not tie to a monitored device. Each group says why, and what would resolve it.",
  "topo.unres.none": "Every discovered neighbour resolves to a monitored device.",
  "topo.unres.resolve": "To resolve",
  "topo.unres.seenFrom": "Reported by",
  "topo.unres.noName": "no name reported",
  "topo.unres.xRows": "×{n}",
  "topo.unres.self": "Factory-default name",
  "topo.unres.self.why": "The neighbour announces a generic name (such as \"MikroTik\") that matches the reporting device itself, so it looks like a link to itself.",
  "topo.unres.self.fix": "give the neighbouring device a unique identity (system name), or add a manual link in the Link Map.",
  "topo.unres.ambiguous": "Name shared by several devices",
  "topo.unres.ambiguous.why": "Several different boxes (different chassis MACs) announce this same name, so it cannot identify one device.",
  "topo.unres.ambiguous.fix": "rename those devices so each has a unique identity.",
  "topo.unres.anonymous": "No identity reported",
  "topo.unres.anonymous.why": "Only one side of the link was discovered and the far end reported no name.",
  "topo.unres.anonymous.fix": "enable LLDP/CDP with a system name on the far end.",
  "topo.unres.unmonitored": "Neighbour not monitored",
  "topo.unres.unmonitored.why": "The far end is named but LibreNMS does not monitor it: often third-party equipment such as IX peers or customer routers.",
  "topo.unres.unmonitored.fix": "add it to LibreNMS if it is yours; otherwise this is expected.",
  "int.subtitle": "Health of the collectors and external systems that feed JALABYTE OPS: can the data on screen be trusted?",
  "int.view.cards": "Cards",
  "int.view.table": "Table",
  "int.state.healthy": "Healthy",
  "int.state.running": "Running",
  "int.state.degraded": "Degraded",
  "int.state.failed": "Failed",
  "int.state.stale": "Stale",
  "int.state.disabled": "Disabled",
  "int.state.unknown": "Unknown",
  "int.noun.olts": "OLTs",
  "int.noun.devices": "Devices",
  "int.noun.services": "Services",
  "int.noun.objects": "Objects",
  "int.m.onts": "ONTs",
  "int.m.events": "Events",
  "int.m.links": "Links",
  "int.m.mirrored": "Mirrored",
  "int.m.failed": "Failed",
  "int.m.linked": "Linked to ONT",
  "int.never": "never",
  "int.ago": "{age} ago",
  "int.job.verified": "Connection verified",
  "int.job.failedN": "{n} failed",
  "int.job.testing": "Testing connection",
  "int.job.syncing": "Sync running",
  "int.job.scheduled": "Scheduled run in progress",
  "int.stage.clients": "Fetching clients {done} / {total}",
  "int.job.test.ok": "Connection OK",
  "int.job.test.fail": "Connection failed",
  "int.job.sync.ok": "Sync complete",
  "int.job.sync.fail": "Sync failed",
  "int.group.collectors": "Collectors",
  "int.group.observability": "Network observability",
  "int.group.acs": "CPE / ACS",
  "int.group.billing": "Billing",
  "int.group.other": "Other",
  "int.posture.operational": "All systems operational",
  "int.posture.degraded": "Monitoring degraded",
  "int.posture.failure": "Monitoring failure",
  "int.posture.unknown": "No data yet",
  "int.healthyOf": "{n} / {total} healthy",
  "int.fresh.olt": "ONT status",
  "int.fresh.snmp": "OLT reachability",
  "int.checked": "checked {age} ago",
  "int.conf.title": "Monitoring confidence",
  "int.conf.good": "Good",
  "int.conf.degraded": "Degraded",
  "int.conf.low": "Low",
  "int.conf.allFresh": "All critical collectors are fresh: the network picture on screen is current.",
  "int.banner.notDown": "Do not assume network devices are down.",
  "int.banner.investigate": "Investigate",
  "int.tag.source": "Source problem",
  "int.tag.collector": "Collector problem",
  "int.tag.external": "External system",
  "int.why.stale": "No successful run for {age}; expected every {every}. Its data may be outdated.",
  "int.why.staleNever": "Never completed successfully; expected every {every}.",
  "int.why.collector": "The collector job itself failed: {detail}",
  "int.why.external": "The external system returned an error: {detail}",
  "int.why.source": "The collector ran, but none of {n} {noun} answered. Check the network path to them.",
  "int.why.partial": "{n} of {total} {noun} failed; the rest are current.",
  "int.why.warnings": "Answered, but incomplete: {w}",
  "int.why.slow": "Last run took {ms}, usually {p50}.",
  "int.why.never": "No run recorded yet.",
  "int.more": "+{n} more",
  "int.affected": "Affected",
  "int.mayBeFine": "network devices may still be operational",
  "int.lastOk": "Last success",
  "int.busy": "A run is already in progress",
  "int.act.test": "Test connection",
  "int.act.retry": "Retry",
  "int.act.run": "Run now",
  "int.act.sync": "Sync now",
  "int.kind.collector": "Collector",
  "int.kind.external": "External",
  "int.disabled": "Not configured, intentionally off.",
  "int.every": "every {every}",
  "int.took": "took",
  "int.lastFail": "last failure",
  "int.reliability24": "24h OK",
  "int.provides": "Provides",
  "int.investigate": "Investigate",
  "int.details": "Details",
  "int.provides.ontStatus": "ONT status",
  "int.provides.optical": "Optical levels",
  "int.provides.ponHealth": "PON health",
  "int.provides.oltHealth": "OLT CPU & temperature",
  "int.provides.outages": "Outage events",
  "int.provides.oltReach": "OLT management reachability",
  "int.provides.devices": "Network devices",
  "int.provides.links": "Topology & link map",
  "int.provides.traffic": "Traffic graphs",
  "int.provides.cpe": "CPE status",
  "int.provides.wan": "WAN IP & PPPoE",
  "int.provides.tr069": "TR-069 reboot",
  "int.provides.subscribers": "Subscriber names",
  "int.provides.billing": "Billing status",
  "int.col.integration": "Integration",
  "int.col.state": "State",
  "int.col.lastOk": "Last success",
  "int.col.every": "Interval",
  "int.col.duration": "Duration",
  "int.col.result": "Last result",
  "int.col.trend": "Recent runs",
  "int.col.started": "Started",
  "int.tab.overview": "Overview",
  "int.tab.history": "History",
  "int.tab.failures": "Failures",
  "int.tab.performance": "Performance",
  "int.tab.config": "Configuration",
  "int.cfg.fromOlts": "Polls every OLT record directly",
  "int.cfg.noEndpoint": "No endpoint configured",
  "int.cfg.enabled": "Enabled",
  "int.cfg.notConfigured": "No — not configured",
  "int.cfg.yes": "Yes",
  "int.cfg.defaultInterval": "default, the worker has not reported its interval yet",
  "int.cfg.stale": "Stale after",
  "int.cfg.source": "Source",
  "int.cfg.timeout": "Timeout",
  "int.cfg.credentials": "Credentials",
  "int.cfg.note": "Credentials are never shown here. Operations (test, sync) are on this page; configuration lives where noted.",
  "int.cfg.cred.olt": "Per OLT, encrypted — OLTs → Edit",
  "int.cfg.cred.snmp": "SNMP_COMMUNITY in the server .env",
  "int.cfg.cred.env": "Server .env (redeploy to change)",
  "int.cfg.cred.acs": "Encrypted — Settings → ACS servers",
  "int.sec.freshness": "Freshness",
  "int.sec.result": "Last result",
  "int.sec.reliability": "Reliability",
  "int.sec.sourceFailures": "Sources that failed in the last run",
  "int.sec.failedRuns": "Failed runs",
  "int.lastRun": "Last run",
  "int.none24": "None recorded",
  "int.staleAfter": "stale after {w}",
  "int.warning": "Warning",
  "int.w1h": "Last 1h",
  "int.w24h": "Last 24h",
  "int.okOf": "{ok} / {n} successful",
  "int.run.ok": "Success",
  "int.run.error": "Failed",
  "int.run.disabled": "Disabled",
  "int.run.partial": "Partial",
  "int.noSourceFailures": "Every source answered in the last run.",
  "int.noFailedRuns": "No failed runs recorded.",
  "int.perf.last": "Last",
  "int.perf.avg1h": "Average 1h",
  "int.perf.p50": "Median 24h",
  "int.perf.p95": "p95 24h",
  "int.perf.chart": "Duration, last {n} runs",
  "int.perf.slowRule": "A run over 3× the 24h median (and 30s above it) marks the integration degraded even when it succeeds.",
  "conf.oltNever": "No OLT collection recorded yet: ONT status is unknown.",
  "conf.oltDown": "ONT status has not been updated for {age}.",
  "conf.oltDownNever": "The OLT collector has never completed: ONT status is unknown.",
  "conf.oltPartial": "{n} of {total} OLTs are not answering the collector: their ONT status is not current.",
  "conf.snmpDownOltOk": "OLT reachability (SNMP) last confirmed {age} ago. ONT status from the OLT collector is current.",
  "conf.snmpDown": "OLT reachability (SNMP) last confirmed {age} ago.",
  "conf.side.librenms": "{name}: network devices and traffic may be outdated",
  "conf.side.genieacs": "{name}: CPE and WAN data may be outdated",
  "conf.side.whmcs": "{name}: subscriber and billing data may be outdated",
  "conf.side.other": "{name} may be outdated",

  // subscribers

  // alerts
  "alerts.active": "Active alerts",
  "alerts.openCount": "{n} open",
  "alerts.loading": "Loading alerts…",
  "alerts.loadError": "Unable to load alerts.",
  "alerts.empty": "No active alerts.",
  "alerts.severity": "Severity",
  "alerts.alert": "Alert",
  "alerts.target": "Target",
  "alerts.value": "Value",
  "alerts.opened": "Opened",
  "alerts.ack": "Ack",
  "alerts.rules": "Alert rules",
  "alerts.rule": "Rule",
  "alerts.hysteresis": "Hysteresis",
  "alerts.consecutive": "Consecutive",
  "alerts.cooldown": "Cooldown (s)",
  "alerts.noRules": "no rules",
  "alerts.rulesHelp": "Hysteresis is the recovery margin in the metric’s own units (a rule “< -27.0” with hysteresis 1.0 clears at -26.0). Consecutive is how many breaching samples must land before an alert opens. Cooldown throttles repeat notifications for the same rule and target.",
  "alerts.deleteRuleTitle": "Delete rule “{name}”?",
  "alerts.deleteRuleBefore": "Nothing will be evaluated for",
  "alerts.deleteRuleAfter": "afterwards. Disable it instead if you only want to silence it for now.",
  "alerts.deleting": "deleting…",

  // settings
  "settings.addServer": "Add server",
  "settings.addServerTitle": "Add GenieACS server",
  "settings.editTitle": "Edit {name}",
  "settings.nbiUrl": "NBI base URL",
  "settings.nbiHint": "GenieACS northbound API — port 7557, not the 3000 web UI.",
  "settings.username": "Username (optional)",
  "settings.passwordKeep": "Password (blank = keep current)",
  "settings.password": "Password (optional)",
  "settings.passwordKeepHint": "Leave blank to keep the stored password.",
  "settings.passwordHint": "The NBI may be unauthenticated.",
  "settings.notesOptional": "Notes (optional)",
  "settings.enabledHint": "Enabled (synced by the collector)",
  "settings.urlScheme": "base URL must start with http:// or https://",
  "settings.deleteTitle": "Delete {name}?",
  "settings.deleteBody": "This removes the server and its {n} mirrored CPE record(s). ONTs keep working; TR-069 reboot stops until another server mirrors them.",
  "settings.lastChecked": "Last checked",
  "settings.working": "working…",
  "settings.connected": "connected — {n} devices",
  "settings.syncedDevices": "synced {n} devices",
  "settings.noServers": "no ACS servers yet",
  "settings.noServersAdmin": "no ACS servers yet — add one to start mirroring CPEs",
  "settings.passwordNote": "Passwords are encrypted at rest and never returned by the API.",
  "settings.oltCredentialsBody": "OLT SSH/Telnet passwords are encrypted at rest with AES-GCM using the OPS_MASTER_KEY env var. The UI exposes them only to the admin role, with audit logging.",

  // shared additions
  "common.librenmsNotConfigured": "LibreNMS is not configured — set LIBRENMS_URL and LIBRENMS_API_TOKEN, then sync.",
  "common.noDescription": "no description",
  "common.deleting": "Deleting…",
  "status.ok": "OK",
  "status.error": "Error",
  "status.running": "Running",
  "status.up": "Up",
  "status.down": "Down",
  "status.disabled": "Disabled",
  "status.active": "Active",
  "status.suspended": "Suspended",
  // human-facing status words
  "status.online": "Online",
  "status.offline": "Offline",
  "status.degraded": "Degraded",
  "status.reachable": "Reachable",
  "status.unreachable": "Unreachable",
  "status.unknown": "Unknown",
  "status.open": "Open",
  "status.acknowledged": "Acknowledged",
  "status.closed": "Closed",
  "status.critical": "Critical",
  "status.warning": "Warning",
  "status.info": "Info",
} as const;

export type Key = keyof typeof en;

export const id: Record<Key, string> = {
  "app.name": "Jalabyte Ops",
  "app.loading": "memuat…",

  "nav.label": "Menu utama",
  "nav.overview": "Ringkasan",
  "nav.olts": "OLT",
  "nav.onts": "ONT",
  "nav.topology": "Topologi",
  "nav.netmap": "Peta Jaringan",
  "nav.graphs": "Grafik Trafik",
  "nav.integrations": "Integrasi",
  "nav.subscribers": "Pelanggan",
  "nav.alerts": "Notifikasi",
  "nav.users": "Pengguna",
  "nav.wallboard": "Layar NOC",
  "nav.settings": "Pengaturan",

  "topbar.openNav": "Buka menu",
  "topbar.closeNav": "Tutup menu",
  "topbar.signedInAs": "masuk sebagai {email}",
  "topbar.logout": "keluar",

  "lang.en": "Inggris",
  "lang.id": "Indonesia",
  "lang.switchTo": "Ganti bahasa ke {lang}",

  "login.email": "Email",
  "login.password": "Kata sandi",
  "login.submit": "Masuk",
  "login.busy": "Sedang masuk…",

  "common.search": "Cari",
  "common.save": "Simpan",
  "common.saving": "Menyimpan…",
  "common.cancel": "Batal",
  "common.delete": "Hapus",
  "common.edit": "Ubah",
  "common.add": "Tambah",
  "common.close": "Tutup",

  // OLT resources
  "oltRes.noSensors": "perangkat tidak menyediakan sensor kondisi lewat SNMP",
  "oltRes.cliOnly": "dibaca dari CLI OLT sendiri; perangkat tidak menyediakan sensor lewat SNMP",
  "oltPorts.port": "Port",
  "oltPorts.errors": "Error",


  "oltRes.limit": "batas",


  // TV wallboard

  // Customer graph share
  "share.loading": "Memuat…",
  "share.invalid": "Tautan ini tidak berlaku. Mungkin sudah kedaluwarsa atau dicabut — silakan minta tautan baru.",
  "share.subtitle": "Trafik masuk dan keluar jalur Anda",
  "share.footer": "Tampilan baca saja. Waktu memakai Asia/Jakarta (WIB).",
  "myGraphs.title": "Grafik saya",
  "myGraphs.empty": "Belum ada grafik yang dibagikan ke akun ini.",
  "wan.title": "WAN CPE (baca saja)",
  "wan.service": "Layanan",
  "wan.mode": "Mode",
  "wan.detail": "User PPPoE / pengalamatan",
  "wan.lastInform": "kontak terakhir",
  "wan.notLinked": "Tidak ada perangkat TR-069 yang tertaut ke ONT ini, jadi pengaturan WAN-nya tidak bisa dibaca.",
  "wan.unavailable": "Tidak bisa membaca pengaturan WAN dari ACS.",
  "wan.none": "CPE tidak melaporkan layanan WAN",
  "wan.readOnly": "Baca saja. Mengubah IPoE ke PPPoE dari sini belum diaktifkan.",

  "shares.title": "Tautan berbagi",
  "shares.new": "Tautan baru",
  "shares.label": "Nama yang dilihat pelanggan",
  "shares.labelHint": "Muncul di bagian atas halaman mereka, misalnya nama perusahaan.",
  "shares.graphLabel": "Nama grafik",
  "shares.expiry": "Kedaluwarsa",
  "shares.expiryDays": "{n} hari",
  "shares.never": "tanpa batas waktu",
  "shares.pick": "Port yang disertakan",
  "shares.create": "Buat tautan",
  "shares.created": "Bagikan tautan ini. Hanya muncul sekarang — server menyimpan hash-nya, jadi tidak bisa ditampilkan lagi.",
  "shares.views": "Dilihat",
  "shares.revoke": "Cabut",
  "shares.empty": "belum ada tautan berbagi",
  "shares.active": "aktif",
  "shares.expired": "kedaluwarsa",
  "shares.revoked": "dicabut",
  "shares.customerNote": "Pelanggan hanya melihat grafik ini dan nama yang Anda beri — bukan perangkat, antarmuka, atau kapasitasnya.",



  // Users and invites
  "users.accounts": "Akun",
  "users.invites": "Undangan",
  "users.invite": "Undang",
  "users.inviteTitle": "Undang orang baru",
  "users.inviteLinkBody": "Bagikan tautan sekali pakai ini. Hanya muncul sekarang — server menyimpan hash-nya, jadi tidak bisa ditampilkan lagi. Tautan juga dikirim lewat email bila SMTP aktif.",
  "users.sendInvite": "Buat undangan",
  "users.email": "Email",
  "users.role": "Peran",
  "bras.diffServers": "server berbeda",
  "bras.diffServersHint": "BRAS menjangkau IP server yang berbeda untuk hostname ini (DNS-nya memberi node CDN berbeda). Selisihnya karena lokasi server, bukan penurunan jalur.",
  "bras.nServers": "{n} server",
  "bras.server": "server",
  "bras.servers3h": "Server yang dijangkau (3j)",
  "bras.noPtr": "tanpa reverse DNS",
  "bras.cmpServers": "{slow} dan {fast} menjangkau server berbeda untuk hostname ini: selisih {d} ms karena lokasi server (mis. Singapura vs Jakarta), bukan penurunan jalur. Bila pelanggan di {slow} seharusnya dilayani lokal, periksa resolver DNS dan routing BRAS tersebut.",
  "bras.lg.servers": "Target hostname: latensi dinilai per IP server; satu garis per server.",
  "bras.lastKnown": "Nilai terakhir yang diketahui, sebelum BRAS berhenti menjawab",
  "bras.subtitle": "Keterjangkauan dan kualitas internet dari tiap gateway pelanggan",
  "bras.thresholds": "Ambang",
  "bras.health": "Kesehatan internet",
  "bras.ov.healthy": "Sehat",
  "bras.ov.degraded": "Menurun",
  "bras.ov.outage": "Gangguan",
  "bras.ov.stale": "Data basi",
  "bras.ov.none": "Belum dipantau",
  "bras.f.bras": "BRAS terjangkau",
  "bras.f.targets": "target sehat",
  "bras.f.loss": "dengan loss (3j)",
  "bras.f.median": "latensi median",
  "bras.live": "Live",
  "bras.staleNote": "Data basi · probe terakhir {age} lalu",
  "bras.pollAge": "poll BRAS {age} lalu",
  "bras.probeAge": "probe {age} lalu",
  "bras.case.shared": "Kemungkinan masalah upstream bersama atau internet",
  "bras.case.sharedD": "{n} target menurun dari semua BRAS: {list}",
  "bras.case.bras": "Kemungkinan penurunan di BRAS atau jalur via {bras}",
  "bras.case.brasD": "{n} target menurun: {list}",
  "bras.case.target": "Kemungkinan masalah di tujuan: {target}",
  "bras.case.targetD": "Menurun dari semua BRAS; target lain normal",
  "bras.case.path": "Kemungkinan penurunan jalur: {target} via {bras}",
  "bras.case.pathD": "Target yang sama normal dari BRAS lain",
  "bras.since": "sejak {age} lalu",
  "bras.details": "Detail",
  "bras.runMtr": "Jalankan MTR",
  "bras.openIncident": "Buka insiden",
  "bras.inc.down": "BRAS tidak terjangkau: {bras}",
  "bras.inc.pppDrop": "Sesi PPPoE turun drastis di {bras}",
  "bras.unreachable": "Tidak terjangkau",
  "bras.lastOk": "terakhir menjawab {age} lalu",
  "bras.delta1h": "{d} sejam terakhir",
  "bras.pppDrop": "Sesi PPPoE turun drastis",
  "bras.pppDropN": "{n} terputus",
  "bras.internetShort": "Internet",
  "bras.nHealthy": "{n}/{total} sehat",
  "bras.noTargetsShort": "tanpa target",
  "bras.up": "aktif",
  "bras.compare": "Bandingkan:",
  "bras.diff": "Selisih",
  "bras.via": "via {bras}",
  "bras.notProbed": "tidak diprobe",
  "bras.st.healthy": "Sehat",
  "bras.st.degraded": "Menurun",
  "bras.st.loss": "Loss",
  "bras.st.unreachable": "Tidak terjangkau",
  "bras.why.no_data": "belum diprobe",
  "bras.why.stale": "probe basi",
  "bras.why.error": "probe gagal",
  "bras.why.no_icmp": "server tidak membalas ping",
  "bras.lossUp": "loss",
  "bras.vsTypical": "+{d} ms dari biasanya {v}",
  "bras.lastAt": "terakhir {age} lalu",
  "bras.lg.latency": "latensi median (3j)",
  "bras.lg.loss": "probe dengan packet loss (tinggi = % loss)",
  "bras.lg.error": "probe tidak bisa berjalan",
  "bras.lg.judged": "Menurun = di atas latensi biasa jalur itu sebesar {pct}% dan minimal {ms} ms (Ambang).",
  "bras.tt.avg": "Rata-rata",
  "bras.tt.loss": "Loss",
  "bras.tt.period": "{p} · {n} probe · {ev} dengan loss",
  "bras.stateVia": "{state} via {bras}",
  "bras.d.now": "Latensi kini",
  "bras.d.typical": "Biasanya (24j)",
  "bras.d.jitter": "Jitter",
  "bras.d.lossNow": "Loss kini",
  "bras.d.loss3h": "Loss 3j",
  "bras.d.avg3h": "Rata-rata 3j",
  "bras.cmpSlower": "{bras} saat ini {d} ms lebih lambat dari {fast}: kemungkinan masalah jalur.",
  "bras.cmpSame": "Semua BRAS dalam selisih {d} ms.",
  "bras.history": "Riwayat",
  "bras.chartMax": "maks {v}",
  "bras.chartLegend2": "Garis: latensi per BRAS. Batang merah di bawah: probe dengan packet loss, satu jalur per BRAS.",
  "bras.pppoeSessions": "Sesi PPPoE",
  "bras.internetHealth": "Internet dari BRAS ini",
  "bras.runDiag": "Jalankan diagnostik",
  "bras.pingResult": "Hasil ping",
  "bras.traceResult": "Trace",
  "bras.ek.api": "Kesalahan alat: BRAS tidak bisa dijangkau atau menolak perintah. Ini tidak menjelaskan apa pun tentang target.",
  "bras.ek.timeout": "Pengujian kehabisan waktu sebelum selesai.",
  "bras.ek.unreachable": "Target tidak menjawab satu paket pun.",
  "bras.ek.incomplete": "Trace berhenti sebelum target menjawab: jalur putus di hop pertama yang diam, atau target tidak menjawab ICMP.",
  "bras.viewRaw": "Lihat keluaran mentah",
  "bras.hideRaw": "Sembunyikan keluaran mentah",
  "bras.res.ok": "OK",
  "bras.res.reachable": "Terjangkau",
  "bras.res.timeout": "Waktu habis",
  "bras.res.toolFail": "Kesalahan alat",
  "bras.res.toolFailS": "Kesalahan BRAS / API",
  "bras.res.incomplete": "Tidak lengkap",
  "bras.rateLimited": "balasan dibatasi; hop berikutnya normal",
  "bras.diagnostics": "Diagnostik",
  "bras.manualNote": "Uji manual, tercatat di bawah",
  "bras.count": "Jumlah",
  "bras.spreadMs": "Rentang (maks − min): {v}",
  "bras.recentDiag": "Diagnostik terbaru",
  "bras.tab.manual": "Manual",
  "bras.tab.auto": "Trace otomatis",
  "bras.col.time": "Waktu",
  "bras.col.source": "Dari",
  "bras.col.target": "Target",
  "bras.col.type": "Jenis",
  "bras.col.result": "Hasil",
  "bras.col.summary": "Ringkasan",
  "bras.noManual": "Belum ada uji manual.",
  "bras.noAuto": "Belum ada trace otomatis. Trace diambil saat insiden dibuka.",
  "bras.category": "Kategori",
  "bras.probeType": "Probe",
  "bras.probedFrom": "Diprobe dari",
  "bras.overrides": "Ambang khusus target (opsional)",
  "bras.latWarn": "Peringatan latensi (ms)",
  "bras.lossWarn": "Peringatan loss (%)",
  "bras.usePolicy": "pakai kebijakan",
  "bras.overrideNote": "Batas latensi menggantikan penilaian baseline untuk target ini. Kosong = kebijakan global.",
  "bras.policyHint": "Cara menilai hasil probe, untuk halaman dan alert. Latensi dibandingkan dengan median 24 jam jalur itu sendiri, jadi tujuan 20 ms tidak dinilai seperti tujuan 7 ms.",
  "bras.p.lossWarn": "Peringatan di",
  "bras.p.lossCrit": "Kritis di",
  "bras.p.latency": "Latensi (dibanding baseline jalur)",
  "bras.p.devPct": "Di atas baseline sebesar",
  "bras.p.devMin": "dan minimal",
  "bras.p.latencyNote": "Menurun bila latensi {pct}% di atas biasanya DAN minimal {ms} ms di atasnya.",
  "bras.p.pppPct": "Turun sebesar",
  "bras.p.pppMin": "dan minimal",
  "bras.p.sessions": "sesi",
  "bras.loss3h": "loss 3j {n}",
  "bras.apiPort": "Port API (0 = SSH)",
  "bras.apiTls": "api-ssl (TLS)",
  "nav.bras": "BRAS & internet",
  "bras.title": "BRAS & internet",
  "bras.addRouter": "Tambah BRAS",
  "bras.addTarget": "Tambah target",
  "bras.editRouter": "Ubah {name}",
  "bras.editTarget": "Ubah target",
  "bras.st.up": "Aktif",
  "bras.st.down": "Mati",
  "bras.st.unknown": "Tidak diketahui",
  "bras.st.disabled": "Nonaktif",
  "bras.load": "Beban",
  "bras.memory": "Memori",
  "bras.uptime": "Uptime",
  "bras.test": "Uji",
  "bras.testOk": "Terhubung · {id}",
  "bras.disable": "Nonaktifkan",
  "bras.enable": "Aktifkan",
  "bras.internet": "Internet dari tiap BRAS",
  "bras.every": "5 ping tiap {s} dtk",
  "bras.target": "Target",
  "bras.noTargets": "Belum ada target internet.",
  "bras.noTargetsAdmin": "Belum ada target internet. Tambahkan alamat publik yang harus selalu terjangkau BRAS, mis. 1.1.1.1, 8.8.8.8, gateway upstream Anda.",
  "bras.delTarget": "Hapus target {name} beserta riwayatnya?",
  "bras.from": "Dari",
  "bras.targetAddr": "Alamat",
  "bras.ping": "Ping",
  "bras.pinging": "Ping…",
  "bras.trace": "MTR / trace",
  "bras.tracing": "Tracing…",
  "bras.traceWait": "Trace bisa sampai semenit bila ada hop yang tidak menjawab.",
  "bras.loss": "Loss",
  "bras.recv": "Diterima",
  "bras.hop": "Hop",
  "bras.noReply": "tidak menjawab",
  "bras.autoTraceLong": "diambil otomatis saat target mulai gagal",
  "bras.manualTrace": "dijalankan manual",
  "bras.hops": "{n} hop",
  "bras.noHistory": "Tidak ada probe di rentang ini.",
  "bras.setup": "Tambahkan BRAS Anda",
  "bras.setupBody": "Panel memakai RouterOS API (Linux: SSH) dengan akun baca-saja untuk membaca kondisi dan sesi PPPoE, serta menjalankan ping dan traceroute. RouterOS membutuhkan policy api, read, dan test; tidak ada konfigurasi yang diubah di router.",
  "bras.setupNote": "BRAS harus bisa dijangkau dari server panel lewat port API-nya (8728, atau 8729 dengan TLS; Linux: SSH). Kredensial dienkripsi dan tidak pernah ditampilkan lagi.",
  "bras.kind": "Jenis",
  "bras.host": "Host / IP",
  "bras.sshPort": "Port SSH",
  "bras.username": "Username",
  "bras.password": "Kata sandi",
  "bras.passwordKeep": "Kata sandi (kosong = tetap)",
  "bras.credNote": "Gunakan akun baca-saja (RouterOS: policy api,read,test). Kata sandi disimpan terenkripsi.",
  "bras.targetHint": "Alamat yang di-ping setiap BRAS tiap menit. Dua kali berturut-turut loss membuka alert dan menyimpan trace.",
  "bras.delRouterTitle": "Hapus {name}?",
  "bras.delRouterBody": "Riwayat probe dan trace-nya dihapus dan alert-nya ditutup. Router-nya sendiri tidak diubah.",
  "cping.title": "Ping dari BRAS",
  "cping.explain": "Mencari BRAS yang memegang sesi PPPoE pelanggan ini lalu mem-ping alamat sesinya dari sana: latensi jalur akses, bahkan bila ONT-nya sendiri tidak menjawab ping.",
  "cping.running": "Mencari sesi di tiap BRAS dan melakukan ping…",
  "cping.found": "Sesi di {router} ({login}):",
  "cping.noSession": "Tidak ada sesi PPPoE aktif di BRAS mana pun untuk {logins}. Pelanggan tidak terhubung di level PPP.",
  "cping.notHere": "tidak ada sesi",
  "cping.lastIp": "Alamat terakhir yang dilaporkan router: {ip} (tidak di-ping: bisa jadi milik orang lain sekarang).",
  "cping.run": "Jalankan",
  "cping.again": "Jalankan lagi",
  "int.noun.routers": "router",
  "int.cfg.cred.bras": "Per router, terenkripsi, di halaman BRAS",
  "int.provides.bras": "Kondisi BRAS",
  "int.provides.internet": "Keterjangkauan internet",
  "wb.alarm.new": "ALARM · {n} baru",
  "wb.alarm.ack": "Tandai diketahui",
  "wb.sound.arm": "Klik di mana saja untuk mengaktifkan suara",
  "wb.sound.on": "Suara alarm aktif (klik untuk bisukan)",
  "wb.sound.off": "Suara alarm mati (klik untuk aktifkan)",
  "sub.odp.issue": "Kemungkinan masalah di ODP",
  "sub.odp.down": "{n} dari {total} layanan mati atau diam",
  "sub.odp.hint": "Beberapa pelanggan di satu ODP gagal bersamaan biasanya berarti gangguan splitter atau feeder, bukan masalah pelanggan terpisah.",
  "sub.odp.show": "Tampilkan pelanggan ini",
  "sub.f.allOdps": "Semua ODP",
  "sub.d.viaTag": "cocok lewat tag ACS #{id}",
  "sub.d.noOdp": "Belum ditag",
  "sub.d.acsTag": "Tag ACS",
  "sub.d.loginDiffers": "Login di tag {tag} berbeda dengan PPPoE billing {billing}",
  "ont.opt.noReading": "tanpa pembacaan · terakhir {age} lalu",
  "ont.opt.noReadingWhy": "OLT tidak mengembalikan nilai optik untuk ONT ini pada polling terakhir. Nilai yang ditampilkan adalah yang terakhir dilaporkannya.",
  "ont.rm.action": "Hapus dari panel",
  "ont.rm.title": "Hapus {name} dari panel?",
  "ont.rm.body": "Gunakan hanya untuk ONT yang sudah dihapus dari OLT (diganti atau rusak). OLT tidak lagi mencantumkannya, jadi tidak ada yang berubah di jaringan.",
  "ont.rm.alerts": "Alert yang masih terbuka akan ditutup.",
  "ont.rm.history": "Riwayat optik dan kejadiannya dihapus.",
  "ont.rm.links": "Tautan pelanggan dan CPE ke ONT ini dilepas.",
  "ont.rm.type": "Ketik serial {serial} untuk konfirmasi",
  "ont.rm.confirm": "Hapus ONT",
  "sub.title": "Pelanggan",
  "sub.count": "{n} pelanggan",
  "sub.onOnt": "{n} di ONT",
  "sub.cpeOnly": "{n} hanya CPE",
  "sub.unlinkedN": "{n} belum tertaut",
  "sub.svcLower.active": "aktif",
  "sub.svcLower.suspended": "ditangguhkan",
  "sub.svcLower.terminated": "dihentikan",
  "sub.svcLower.cancelled": "dibatalkan",
  "sub.svcLower.other": "lainnya",
  "sub.source": "Sumber",
  "sub.syncedAgo": "sinkron {age} lalu",
  "sub.neverSynced": "belum pernah sinkron",
  "sub.sync.degraded": "menurun",
  "sub.sync.failed": "sinkron terakhir gagal",
  "sub.sync.stale": "basi",
  "sub.sync.unknown": "belum berjalan",
  "sub.sync.disabled": "nonaktif",
  "sub.scope.operational": "Operasional",
  "sub.scope.all": "Semua pelanggan",
  "sub.search": "Cari nama, ID pelanggan, serial ONT, IP, MAC, deskripsi…",
  "sub.clearSearch": "Hapus pencarian",
  "sub.filters": "Filter",
  "sub.quick.attention": "Perlu perhatian",
  "sub.quick.active": "Aktif",
  "sub.quick.suspended": "Ditangguhkan",
  "sub.quick.unlinked": "Belum tertaut",
  "sub.quick.offline": "Offline",
  "sub.quick.weak": "Sinyal lemah",
  "sub.quick.all": "Semua",
  "sub.selected": "{n} dipilih",
  "sub.export": "Ekspor CSV",
  "sub.copyIds": "Salin ID pelanggan",
  "sub.clearSel": "Hapus pilihan",
  "sub.exportView": "Ekspor {n} baris ini",
  "sub.selectPage": "Pilih halaman ini",
  "sub.select": "Pilih {name}",
  "sub.col.subscriber": "Pelanggan",
  "sub.col.service": "Layanan",
  "sub.col.svcStatus": "Status layanan",
  "sub.col.network": "Jaringan",
  "sub.col.signal": "Sinyal",
  "sub.col.access": "Akses",
  "sub.noMatch": "Tidak ada pelanggan yang cocok",
  "sub.searchAll": "cari semua pelanggan, termasuk layanan yang sudah berakhir",
  "sub.showing": "{from}–{to} dari {n}",
  "sub.perPage": "Baris per halaman",
  "sub.perPageN": "{n} per halaman",
  "sub.prev": "Halaman sebelumnya",
  "sub.next": "Halaman berikutnya",
  "sub.f.olt": "OLT",
  "sub.f.allOlts": "Semua OLT",
  "sub.f.pon": "PON",
  "sub.f.allPons": "Semua PON",
  "sub.f.plan": "Paket",
  "sub.f.allPlans": "Semua paket",
  "sub.f.link": "Tautan ONT",
  "sub.f.linked": "Tertaut ke ONT",
  "sub.f.noOnt": "Tanpa ONT",
  "sub.f.clear": "Hapus filter",
  "sub.svc.active": "Aktif",
  "sub.svc.suspended": "Ditangguhkan",
  "sub.svc.terminated": "Dihentikan",
  "sub.svc.cancelled": "Dibatalkan",
  "sub.net.online": "Online",
  "sub.net.weak": "Sinyal lemah",
  "sub.net.critical": "Sinyal kritis",
  "sub.net.los": "LOS",
  "sub.net.power": "Listrik padam",
  "sub.net.offline": "Offline",
  "sub.net.stale": "Basi",
  "sub.net.cpe_online": "CPE online",
  "sub.net.cpe_silent": "CPE diam",
  "sub.net.unknown": "Tidak diketahui",
  "sub.net.unlinked": "Belum tertaut",
  "sub.att.activeDown": "Layanan aktif, jaringan mati",
  "sub.att.activeDownFor": "Layanan aktif, mati {d}",
  "sub.att.activeSignal": "Layanan aktif, sinyal menurun",
  "sub.att.activeSilent": "Layanan aktif, CPE diam",
  "sub.att.activeSilentFor": "Layanan aktif, CPE diam {d}",
  "sub.att.suspendedOnline": "Ditangguhkan, masih online",
  "sub.att.endedLinked": "Berakhir, perangkat masih terpasang",
  "sub.attWhy.activeDown": "Pelanggan membayar layanan yang tidak sedang diberikan jaringan.",
  "sub.attWhy.activeSignal": "Level optik melewati ambang yang dikonfigurasi; layanan bisa putus.",
  "sub.attWhy.activeSilent": "Router berhenti melapor ke ACS. Mungkin mati listrik atau jalurnya putus; tidak ada ONT tertaut untuk memastikan.",
  "sub.attWhy.suspendedOnline": "Billing menangguhkan layanan ini, tetapi perangkatnya masih menyala. Pastikan penangguhan diterapkan di jaringan.",
  "sub.attWhy.endedLinked": "Layanan sudah berakhir di billing, tetapi ONT masih terpasang atau routernya masih online.",
  "sub.multi": "{n} ONT · {up} up · {down} mati",
  "sub.cpeAcs": "CPE via ACS",
  "sub.noDevice": "Tidak ada ONT atau CPE yang diketahui",
  "sub.noPlan": "Tanpa paket",
  "sub.sig.good": "Normal",
  "sub.sig.warn": "Peringatan",
  "sub.sig.crit": "Kritis",
  "sub.sig.stale": "Pembacaan basi",
  "sub.sig.noOptics": "Tanpa optik (hanya CPE)",
  "sub.sig.noOnt": "Tanpa ONT",
  "sub.sig.noReading": "Tanpa pembacaan",
  "sub.acc.ont": "ONT tertaut",
  "sub.acc.onts": "{n} ONT tertaut",
  "sub.acc.pppoe": "lewat login PPPoE",
  "sub.acc.confirmed": "dikonfirmasi operator",
  "sub.acc.cpe": "Hanya CPE",
  "sub.acc.inform": "inform ACS {age} lalu",
  "sub.acc.neverInform": "belum pernah inform",
  "sub.linkOnt": "Tautkan ONT",
  "sub.actions": "Aksi untuk {name}",
  "sub.m.open": "Buka pelanggan",
  "sub.m.ont": "Buka ONT",
  "sub.m.olt": "Buka OLT",
  "sub.m.alerts": "Lihat insiden",
  "sub.m.linkAnother": "Tautkan ONT lain",
  "sub.m.copy": "Salin ID pelanggan",
  "sub.path.olt": "OLT",
  "sub.path.pon": "PON",
  "sub.path.ont": "ONT",
  "sub.path.serial": "Serial",
  "sub.path.distance": "Jarak",
  "sub.path.rx": "RX saat ini",
  "sub.d.subscriber": "Pelanggan",
  "sub.d.client": "klien {id}",
  "sub.d.service": "Layanan",
  "sub.d.fromWhmcs": "Dari WHMCS · diperbarui {age} lalu",
  "sub.d.network": "Jaringan",
  "sub.d.cpeOnlyNote": "Hanya router yang diketahui (GenieACS). Tidak ada ONT tertaut, jadi tidak ada data optik.",
  "sub.d.unlinkedNote": "Belum ada ONT atau router yang diketahui untuk layanan ini.",
  "sub.d.downFor": "Mati selama {d}",
  "sub.d.silentFor": "Tanpa inform selama {d}",
  "sub.d.incident": "Insiden aktif",
  "sub.d.openFor": "Terbuka {d}",
  "sub.d.acked": "sudah di-ack",
  "sub.d.openIncident": "Buka insiden",
  "sub.d.noAlerts": "Tidak ada alert aktif.",
  "sub.d.access": "Perangkat akses",
  "sub.d.cpe": "Router pelanggan (CPE)",
  "sub.d.viaPppoe": "cocok lewat login PPPoE",
  "sub.d.model": "Model",
  "sub.d.lastInform": "Inform terakhir",
  "sub.d.wanIp": "IP WAN",
  "sub.d.nothing": "Belum ada perangkat jaringan yang tertaut ke layanan ini.",
  "sub.d.ontRx": "RX ONT",
  "sub.d.oltRx": "RX OLT",
  "sub.d.lastSeen": "Terakhir terlihat",
  "sub.d.link": "Tautan",
  "sub.d.linkAuto": "Billing (PPPoE)",
  "sub.d.linkManual": "Operator",
  "sub.d.signalHistory": "Riwayat sinyal",
  "sub.d.events": "Kejadian terbaru",
  "sub.d.noEvents": "Belum ada kejadian jaringan tercatat.",
  "sub.d.sourceNote": "Pelanggan, paket, dan status layanan berasal dari WHMCS dan diubah di sana.",
  "sub.ev.linked": "ONT ditautkan",
  "sub.ev.unlinked": "Tautan ONT dilepas",
  "sub.na": "Tidak tersedia",
  "sub.neverSeen": "Belum pernah terlihat",
  "sub.unlink": "Lepas tautan",
  "sub.unlinkTitle": "Lepas tautan ONT?",
  "sub.unlinkBody": "{serial} tidak lagi ditampilkan melayani {name}. ONT-nya sendiri tidak diubah.",
  "sub.l.title": "Tautkan ONT ke pelanggan",
  "sub.l.search": "Cari serial, deskripsi, OLT, PON…",
  "sub.l.results": "Hasil pencarian",
  "sub.l.suggested": "Saran kecocokan",
  "sub.l.suggestNote": "Saran hanya petunjuk. Tidak ada yang ditautkan sebelum Anda konfirmasi.",
  "sub.l.none": "Tidak ada ONT yang cocok",
  "sub.l.noSuggest": "Tidak ada saran. Cari lewat serial atau deskripsi.",
  "sub.l.noDesc": "Tanpa deskripsi",
  "sub.l.strong": "Cocok kuat",
  "sub.l.possible": "Mungkin cocok",
  "sub.l.whyCpe": "Router pelanggan melaporkan serial ONT ini",
  "sub.l.whyName": "Nama pelanggan muncul di deskripsi ONT",
  "sub.l.taken": "Sudah tertaut ke {name}",
  "sub.l.review": "Tinjau",
  "sub.l.confirm": "Konfirmasi tautan",
  "sub.l.confirmNote": "{serial} akan ditampilkan melayani {name}, dan alert-nya akan mengarah ke sini. Anda bisa melepasnya nanti.",
  "set.title": "Pengaturan",
  "set.subtitle": "Cara sistem berperilaku",
  "set.search": "Cari pengaturan…",
  "set.noHits": "Tidak ada pengaturan yang cocok",
  "set.nav.overview": "Ringkasan",
  "set.nav.general": "Umum",
  "set.nav.monitoring": "Pemantauan",
  "set.nav.integrations": "Integrasi",
  "set.nav.access": "Akses & Kredensial",
  "set.nav.sharing": "Berbagi",
  "set.nav.advanced": "Lanjutan",
  "set.yourAccess": "Akses Anda",
  "set.access.admin": "Administrator: dapat mengubah kebijakan pemantauan, integrasi, kredensial, dan tautan berbagi.",
  "set.access.noc": "Operator NOC: dapat melihat semua pengaturan; perubahan memerlukan administrator.",
  "set.access.viewer": "Viewer: akses baca saja ke pengaturan.",
  "set.access.portviewer": "Port viewer: tidak punya akses ke pengaturan.",
  "set.viewOnly": "{what}: hanya lihat. Perubahan memerlukan administrator.",
  "set.leaveDirty": "Ada perubahan yang belum disimpan. Tinggalkan bagian ini dan buang perubahan?",
  "set.enc.short": "terenkripsi",
  "set.enc.shortOff": "tanpa kunci",
  "set.enc.on": "Enkripsi aktif",
  "set.enc.off": "Enkripsi belum dikonfigurasi",
  "set.enc.enabled": "Aktif",
  "set.enc.notConfigured": "Belum dikonfigurasi",
  "set.nActive": "{n} aktif",
  "set.nActiveLinks": "{n} tautan aktif",
  "set.nOfM": "{n} dari {m}",
  "set.every": "tiap {d}",
  "set.adminOnly": "Khusus administrator",
  "set.open": "Buka",
  "set.recent": "Perubahan terbaru",
  "set.noRecent": "Belum ada perubahan konfigurasi yang tercatat.",
  "set.configured": "Dikonfigurasi",
  "set.notConfigured": "Belum dikonfigurasi",
  "set.notSet": "Belum diatur",
  "set.missing": "Tidak ada",
  "set.lastChanged": "Terakhir diubah {age} lalu · {by}",
  "set.learnMore": "Selengkapnya",
  "set.unsaved": "Perubahan belum disimpan",
  "set.saved": "Perubahan tersimpan",
  "set.discard": "Buang",
  "set.saveChanges": "Simpan perubahan",
  "set.envManaged": "Diatur di environment deployment; nama variabel ada di Lanjutan.",
  "set.actionsFor": "Aksi untuk {name}",
  "set.ov.policySet": "Kebijakan dikonfigurasi",
  "set.ov.policyDefault": "Memakai nilai bawaan",
  "set.ov.rx": "RX optik",
  "set.ov.alertEval": "Evaluasi alert",
  "set.ov.configured": "{n} dari {total} dikonfigurasi",
  "set.ov.notConfigured": "Belum dikonfigurasi",
  "set.ov.oltCreds": "Kredensial OLT",
  "set.ov.reveal": "Tampilkan rahasia",
  "set.ov.writeOnly": "Hanya tulis",
  "set.ov.expiring": "Kedaluwarsa dalam 7 hari",
  "set.ov.totalLinks": "Semua tautan",
  "set.ov.serverWide": "Bawaan seluruh server",
  "set.ov.sysInfo": "Informasi sistem",
  "set.ov.diag": "Diagnostik",
  "set.ov.envNames": "Konfigurasi environment",
  "set.gen.language": "Bahasa",
  "set.gen.languageHint": "Bahasa antarmuka Anda sendiri. Tidak mengubah tampilan pengguna lain.",
  "set.gen.system": "Sistem",
  "set.gen.publicUrl": "URL publik",
  "set.gen.tz": "Zona waktu OLT",
  "set.gen.tzUnset": "Belum diatur (jam server)",
  "set.gen.inviteTtl": "Masa berlaku undangan",
  "set.gen.permissions": "Siapa yang dapat mengubah pengaturan",
  "set.perm.area": "Area",
  "set.perm.monitoring": "Kebijakan pemantauan",
  "set.perm.integrations": "Integrasi",
  "set.perm.credentials": "Kredensial OLT",
  "set.perm.sharing": "Tautan berbagi",
  "set.perm.view": "Lihat",
  "set.perm.edit": "Ubah",
  "set.perm.manage": "Kelola",
  "set.perm.status": "Status saja",
  "set.perm.replace": "Ganti",
  "set.perm.none": "Tanpa akses",
  "set.perm.note": "Ditegakkan oleh server, bukan sekadar disembunyikan di sini. Username hanya terlihat oleh administrator; tidak ada peran yang bisa membaca kata sandi.",
  "set.mon.freshness": "Polling & kesegaran data",
  "set.mon.job": "Tugas",
  "set.mon.interval": "Interval",
  "set.mon.staleAfter": "Basi setelah",
  "set.mon.oltPoll": "Polling OLT",
  "set.mon.snmpPoll": "Keterjangkauan SNMP",
  "set.mon.acsSync": "Sinkron LibreNMS / GenieACS",
  "set.mon.whmcsSync": "Sinkron WHMCS",
  "set.mon.alertEval": "Evaluasi alert",
  "set.mon.staleRule": "Data ditandai basi setelah dua putaran terlewat ditambah 30 dtk; nilai basi tidak pernah ditampilkan sebagai terkini.",
  "set.mon.alertPolicies": "Kebijakan alert",
  "set.mon.manageRules": "Kelola aturan",
  "set.mon.rules": "Aturan",
  "set.mon.critical": "Kritis",
  "set.mon.util": "Ambang utilisasi trafik",
  "set.mon.utilHigh": "Tinggi",
  "set.mon.utilCrit": "Kritis",
  "set.mon.utilHint": "Menilai beban antarmuka di Grafik Trafik terhadap kapasitas aktif (anggota LAG yang up).",
  "set.opt.title": "Kebijakan RX optik",
  "set.opt.bands": "Rentang",
  "set.opt.healthy": "Sehat",
  "set.opt.warning": "Peringatan",
  "set.opt.critical": "Kritis",
  "set.opt.overload": "Di atas −8 dBm adalah overload dan selalu kritis: batas penerima, bukan kebijakan.",
  "set.opt.warnField": "Ambang peringatan",
  "set.opt.critField": "Ambang kritis",
  "set.opt.err.number": "Isi kedua ambang dengan angka.",
  "set.opt.err.order": "Ambang kritis harus lebih rendah dari peringatan, atau rentang saling tumpang tindih.",
  "set.opt.err.range": "Ambang harus di antara −40 dan −8 dBm.",
  "set.opt.preview": "Pratinjau",
  "set.opt.lvl.good": "Baik",
  "set.opt.lvl.warn": "Peringatan",
  "set.opt.lvl.crit": "Kritis",
  "set.opt.lvl.unknown": "Tanpa data",
  "set.opt.impact": "Dampak ke armada",
  "set.opt.now": "Sekarang",
  "set.opt.after": "Setelah diubah",
  "set.opt.capped": "Dihitung dari 5.000 ONT pertama.",
  "set.opt.appliesTo": "Berlaku untuk",
  "set.opt.allOnts": "Semua ONT (bawaan global)",
  "set.opt.overrides": "Belum ada pengecualian per vendor, OLT, PON, atau profil.",
  "set.opt.usedBy": "Dipakai oleh",
  "set.use.ontList": "Daftar ONT",
  "set.use.ontDetail": "Detail ONT",
  "set.use.oltDetail": "Detail OLT",
  "set.use.wallboard": "Wallboard",
  "set.use.alerts": "Alert",
  "set.use.telegram": "Notifikasi Telegram",
  "set.acs.title": "Server ACS",
  "set.acs.endpoint": "Endpoint",
  "set.acs.devices": "Perangkat",
  "set.acs.lastSync": "Sinkron terakhir",
  "set.acs.notSynced": "Belum disinkron",
  "set.acs.stale": "basi",
  "set.acs.neverTested": "Belum pernah diuji",
  "set.acs.test": "Uji koneksi",
  "set.acs.sync": "Sinkron sekarang",
  "set.acs.disable": "Nonaktifkan",
  "set.acs.enable": "Aktifkan",
  "set.acs.deleteServer": "Hapus server",
  "set.acs.disableTitle": "Nonaktifkan {name}?",
  "set.acs.disableBody": "Kolektor berhenti menyinkron CPE-nya. Perangkat yang sudah dicerminkan tetap ada tetapi akan basi.",
  "set.acs.state.healthy": "Sehat",
  "set.acs.state.degraded": "Menurun",
  "set.acs.state.failed": "Gagal",
  "set.acs.state.disabled": "Nonaktif",
  "set.acs.state.unknown": "Tidak diketahui",
  "set.int.connections": "Integrasi lain",
  "set.int.liveHealth": "Kondisi langsung di Integrasi",
  "set.int.librenms": "Topologi, perangkat, dan trafik",
  "set.int.smtp": "Email alert dan undangan",
  "set.int.telegram": "Notifikasi alert",
  "set.int.whmcs": "Pelanggan dan penagihan",
  "set.int.auth": "terautentikasi",
  "set.int.noAuth": "tanpa auth",
  "set.int.recipients": "{n} penerima alert",
  "set.int.botChat": "Token bot dan chat terisi",
  "set.int.noToken": "tanpa token bot",
  "set.int.noChat": "tanpa chat id",
  "set.sec.title": "Keamanan",
  "set.sec.encryption": "Enkripsi kredensial",
  "set.sec.masterKey": "Kunci enkripsi utama",
  "set.sec.algorithm": "Algoritma",
  "set.sec.oltStored": "Kredensial OLT tersimpan",
  "set.sec.acsStored": "Kata sandi ACS tersimpan",
  "set.sec.plaintext": "Rahasia teks polos",
  "set.sec.plaintextNone": "Tidak ada",
  "set.sec.reveal": "Tampilkan rahasia",
  "set.sec.revealOff": "Tidak bisa (hanya tulis)",
  "set.sec.summary": "Kredensial dienkripsi saat disimpan dan tidak pernah dikirim kembali ke browser.",
  "set.cred.title": "Kredensial OLT",
  "set.cred.olt": "OLT",
  "set.cred.username": "Username",
  "set.cred.access": "Akses",
  "set.cred.secrets": "Kata sandi",
  "set.cred.changed": "Terakhir diubah",
  "set.cred.login": "Login terakhir",
  "set.cred.hidden": "Tersembunyi",
  "set.cred.stored": "Tersimpan",
  "set.cred.noPassword": "Tidak ada",
  "set.cred.plusEnable": "+ enable",
  "set.cred.beforeAudit": "Sebelum log audit",
  "set.cred.accepted": "Diterima {age} lalu",
  "set.cred.pollFailing": "Polling terakhir gagal",
  "set.cred.notVerified": "Belum terverifikasi",
  "set.cred.replace": "Ganti kredensial",
  "set.cred.replaceTitle": "Ganti kredensial untuk {name}?",
  "set.cred.replaceBody": "Kolektor login dengan kredensial baru pada polling berikutnya. Kata sandi yang salah menghentikan polling OLT ini sampai diperbaiki.",
  "set.cred.newPassword": "Kata sandi baru",
  "set.cred.newEnable": "Kata sandi enable baru (kosong = tetap)",
  "set.cred.none": "Belum ada OLT",
  "set.cred.writeOnly": "Kata sandi hanya bisa ditulis: dapat diganti, tidak pernah ditampilkan.",
  "set.share.create": "Buat tautan berbagi",
  "set.share.policy": "Tautan berbagi memberi siapa pun yang memegang URL akses baca ke grafik terpilih tanpa login. URL hanya ditampilkan sekali saat dibuat; server hanya menyimpan hash, jadi tautan yang hilang tidak bisa dipulihkan: cabut lalu buat yang baru.",
  "set.share.shared": "Grafik dibagikan · {n}",
  "set.share.more": "+{n} lagi",
  "set.share.lastAccess": "Terakhir diakses",
  "set.share.neverOpened": "Belum pernah dibuka",
  "set.share.by": "Dibuat oleh {email}",
  "set.share.onceShort": "URL ditampilkan sekali saat dibuat",
  "set.share.extend": "Perpanjang masa berlaku",
  "set.share.extendTitle": "Perpanjang {name}?",
  "set.share.extendBody": "Tautan tetap berfungsi sampai tanggal baru.",
  "set.share.extendExpired": "Tautan ini sudah kedaluwarsa. Memperpanjangnya membuat URL yang sama berfungsi lagi.",
  "set.share.newExpiry": "Kedaluwarsa baru:",
  "set.share.revokeTitle": "Cabut tautan berbagi?",
  "set.share.revokeBody": "{name} langsung kehilangan akses. Tindakan ini tidak dapat dibatalkan.",
  "set.share.revokeLink": "Cabut tautan",
  "set.adv.title": "Informasi sistem",
  "set.adv.hint": "Konfigurasi server berasal dari variabel environment. Nilainya tidak pernah ditampilkan, hanya apakah sudah diatur.",
  "set.adv.setting": "Pengaturan",
  "set.adv.env": "Variabel environment",
  "set.adv.defaults": "Nilai bawaan jika kosong",
  "set.adv.librenms": "LibreNMS",
  "set.adv.smtp": "Email SMTP",
  "set.adv.telegram": "Telegram",
  "set.adv.whmcs": "WHMCS",
  "set.adv.intervals": "Interval polling",
  "set.adv.crypto": "Penyimpanan kredensial",
  "set.adv.restart": "Perubahan environment berlaku setelah API dan worker dimulai ulang.",
  "set.act.alert_rule.create": "Membuat aturan alert",
  "set.act.alert_rule.update": "Mengubah aturan alert",
  "set.act.alert_rule.delete": "Menghapus aturan alert",
  "set.act.acs.create": "Menambah server ACS",
  "set.act.acs.update": "Memperbarui server ACS",
  "set.act.acs.delete": "Menghapus server ACS",
  "set.act.graph_share.create": "Membuat tautan berbagi",
  "set.act.graph_share.revoke": "Mencabut tautan berbagi",
  "set.act.graph_share.extend": "Memperpanjang tautan berbagi",
  "set.act.olt.create": "Menambah OLT",
  "set.act.olt.update": "Memperbarui OLT",
  "set.act.netlink.create": "Menambah tautan topologi",
  "set.act.netlink.update": "Memperbarui tautan topologi",
  "set.act.netlink.delete": "Menghapus tautan topologi",
  "shares.preview": "Pratinjau akses",
  "shares.previewBody": "Tautan ini memungkinkan siapa pun yang memilikinya melihat grafik trafik untuk {name}, dan tidak lebih:",
  "shares.previewExpiry": "Akses berakhir otomatis setelah {n} hari.",
  "shares.previewNoExpiry": "Akses tidak pernah berakhir kecuali tautan dicabut.",
  "tr.title": "Trafik",
  "tr.search": "Cari perangkat, IP, antarmuka atau deskripsi…",
  "tr.noHits": "Tidak ada antarmuka yang cocok.",
  "tr.recent": "Terakhir",
  "tr.noData": "Tidak ada data trafik untuk antarmuka ini.",
  "tr.fav": "Sematkan ke favorit",
  "tr.unfav": "Hapus dari favorit",
  "tr.interface": "Antarmuka",
  "tr.state.up": "Hidup",
  "tr.state.degraded": "Menurun",
  "tr.state.down": "Mati",
  "tr.state.stale": "Tanpa data",
  "tr.state.unknown": "Tidak diketahui",
  "tr.live": "Live",
  "tr.stale": "Basi",
  "tr.polled": "di-poll {age} lalu · tiap {every}",
  "tr.neverPolled": "belum pernah di-poll",
  "tr.source": "LibreNMS",
  "tr.down": "Antarmuka mati selama {since}. Trafik di bawah adalah riwayat, bukan kondisi saat ini.",
  "tr.unknownSince": "seluruh rentang",
  "tr.lastTraffic": "trafik terakhir ↓{in} ↑{out}, {at} lalu",
  "tr.staleBody": "Tidak ada poll LibreNMS selama {age} (seharusnya tiap {every}): laju tidak terkini. Ini celah pemantauan, bukan antarmuka rusak.",
  "tr.hot.in": "Utilisasi masuk di atas {pct}%",
  "tr.hot.out": "Utilisasi keluar di atas {pct}%",
  "tr.since": "sejak {age}",
  "tr.peakPct": "puncak {v} · {pct}%",
  "tr.lagDegraded": "LAG menurun: {up} dari {total} anggota hidup · {avail} dari {all} tersedia. Utilisasi dihitung terhadap kapasitas yang hidup.",
  "tr.dir.in": "Masuk",
  "tr.dir.out": "Keluar",
  "tr.short.in": "Masuk",
  "tr.short.out": "Keluar",
  "tr.grade.normal": "Normal",
  "tr.grade.high": "Tinggi",
  "tr.grade.critical": "Kritis",
  "tr.notCurrent": "tidak terkini",
  "tr.capacity": "Kapasitas",
  "tr.members": "LAG · {up}/{total} anggota hidup × {each}",
  "tr.lagNoMembers": "LAG · tanpa data anggota",
  "tr.ifSpeed": "kecepatan antarmuka",
  "tr.errors": "Galat",
  "tr.errorsSub": "masuk {in} · keluar {out} · discard tidak dilaporkan",
  "tr.history": "Riwayat trafik · {win}",
  "tr.compare": "Bandingkan periode sebelumnya",
  "tr.view.librenms": "Grafik LibreNMS",
  "tr.view.native": "Native",
  "tr.legacyCaption": "Dirender oleh LibreNMS",
  "tr.legacyNote": "masuk di atas sumbu, keluar di bawah",
  "tr.nativeEmpty": "Belum ada sampel untuk rentang ini.",
  "tr.nativeEmptyHint": "JALABYTE OPS menyimpan satu sampel per poll LibreNMS; riwayat terkumpul sejak sinkron pertama setelah fitur ini dipasang.",
  "tr.nativeAria": "Riwayat trafik masuk dan keluar",
  "tr.noDataBand": "TANPA DATA",
  "tr.capacityLine": "kapasitas {v}",
  "tr.prevPeriod": "periode sebelumnya",
  "tr.nativeNote": "satu titik tiap {step} · celah adalah poll yang hilang, bukan nol",
  "tr.noDataShort": "tanpa data",
  "tr.cur": "Saat ini",
  "tr.avg": "Rata-rata",
  "tr.peak": "Puncak",
  "tr.p95": "Ke-95",
  "tr.p95Withheld": "Hanya {pct}% rentang yang tercakup: persentil ke-95 tidak akan mewakilinya.",
  "tr.insufficient": "kurang",
  "tr.transferred": "Ditransfer · {win}",
  "tr.bytes.in": "Masuk",
  "tr.bytes.out": "Keluar",
  "tr.bytes.total": "Total",
  "tr.coverage": "Dari {n} dari {exp} poll yang diharapkan ({pct}%) sejak {since}.",
  "tr.noSamples": "Belum ada sampel di rentang ini.",
  "tr.peakShare": "puncak {pct}% kapasitas",
  "tr.lagMembers": "Anggota LAG",
  "tr.membersUp": "{up}/{total} hidup",
  "tr.details": "Detail antarmuka",
  "tr.viewTopology": "Lihat di Topologi",
  "tr.d.device": "Perangkat",
  "tr.d.alias": "Deskripsi",
  "tr.d.speed": "Kecepatan",
  "tr.d.type": "Jenis",
  "tr.d.status": "Status",
  "tr.d.polled": "Poll terakhir",
  "tr.d.source": "Sumber",
  "lm.viewTraffic": "Lihat trafik",
  "topbar.attention": "{n} alert perlu ditangani · {all} terbuka",
  "al.view.active": "Aktif",
  "al.view.resolved": "Selesai",
  "al.view.rules": "Aturan",
  "al.kind.power": "Pelanggan kehilangan listrik",
  "al.kind.los": "Kehilangan sinyal (LOS)",
  "al.kind.offline": "ONT offline",
  "al.kind.optical.warn": "Sinyal optik lemah",
  "al.kind.optical.crit": "Sinyal optik kritis",
  "al.kind.oltRx.warn": "RX OLT lemah",
  "al.kind.oltRx.crit": "RX OLT kritis",
  "al.kind.temperature": "Suhu tinggi",
  "al.kind.supply": "Catu daya tidak normal",
  "al.kind.other": "Alert",
  "al.sub.power": "dying-gasp · ONT kehilangan listrik",
  "al.sub.los": "tidak ada cahaya di ONT · sisi fiber atau optik",
  "al.sub.offline": "mati, penyebab tidak dilaporkan",
  "al.sub.optical": "daya terima ONT",
  "al.sub.oltRx": "daya terima OLT dari ONT ini",
  "al.sub.temperature": "suhu ONT",
  "al.sub.supply": "tegangan / arus ONT",
  "al.sub.other": "monitoring alert",
  "al.cause.power": "ONT mengirim dying-gasp: kehilangan listrik. Biasanya di sisi pelanggan (adaptor, stopkontak, pemadaman), bukan fiber.",
  "al.cause.los": "Kehilangan sinyal: ONT tidak menerima cahaya. Mengarah ke jalur fiber atau optik (drop cable, konektor, splitter), bukan listrik.",
  "al.cause.offline": "OLT melaporkan ONT mati tanpa penyebab.",
  "al.backOnlineNote": "ONT sudah online kembali; alert ini tertutup pada poll collector berikutnya.",
  "al.sevShort.critical": "Krit",
  "al.sevShort.warn": "Warn",
  "al.sevShort.info": "Info",
  "al.sev.critical": "kritis",
  "al.sev.warn": "peringatan",
  "al.below": "{sev} di bawah {v}",
  "al.above": "{sev} di atas {v}",
  "al.state.muted": "Dibisukan",
  "al.state.ack": "Diakui",
  "al.state.unack": "Belum",
  "al.sum.incidents": "{n} insiden",
  "al.sum.alerts": "{n} alert",
  "al.sum.crit": "{n} kritis",
  "al.sum.warn": "{n} peringatan",
  "al.sum.attention": "{n} perlu ditangani",
  "al.sum.down": "{n} offline",
  "al.sum.optical": "{n} optik",
  "al.quick.attention": "Perlu ditangani",
  "al.quick.critical": "Kritis",
  "al.quick.offline": "Offline",
  "al.quick.optical": "Optik",
  "al.quick.grouped": "Terkorelasi",
  "al.quick.all": "Semua aktif",
  "al.search": "Cari pelanggan, serial, ONT #, OLT, PON, jenis…",
  "al.groupBy": "Kelompok",
  "al.group.incident": "Insiden",
  "al.group.none": "Tanpa (alert mentah)",
  "al.group.olt": "OLT",
  "al.group.pon": "PON",
  "al.group.type": "Jenis",
  "al.bulk.n": "{n} insiden dipilih ({alerts} alert)",
  "al.bulk.clear": "Bersihkan",
  "al.act.ack": "Akui",
  "al.act.ackShort": "Akui",
  "al.act.ackAll": "Akui semua",
  "al.act.assignMe": "Tugaskan ke saya",
  "al.act.unassign": "Lepas tugas",
  "al.act.mute1h": "Bisukan 1j",
  "al.act.mute": "Bisukan notifikasi {d}",
  "al.act.unmute": "Aktifkan notifikasi",
  "al.act.details": "Detail",
  "al.act.ont": "Buka ONT",
  "al.act.pon": "Lihat PON",
  "al.act.olt": "Lihat OLT",
  "al.act.closeRecord": "Tutup catatan alert",
  "al.col.sev": "Tingkat",
  "al.col.incident": "Insiden",
  "al.col.target": "Target",
  "al.col.current": "Saat ini",
  "al.col.age": "Durasi",
  "al.col.state": "Keadaan",
  "al.col.owner": "Penanggung jawab",
  "al.selectAll": "Pilih semua",
  "al.select": "Pilih",
  "al.actions": "Tindakan",
  "al.related": "{n} alert terkait",
  "al.related1": "1 alert terkait",
  "al.more": "+{n} lagi",
  "al.openedAt": "Dibuka {at}",
  "al.cur.offline": "Offline",
  "al.cur.backOnline": "Sudah online",
  "al.cur.stale": "basi · {age} lalu",
  "al.empty.attention": "Tidak ada yang perlu ditangani: semua insiden aktif sudah diakui atau dibisukan ({n} aktif).",
  "al.empty.filter": "Tidak ada insiden yang cocok.",
  "al.grp.ponOutage": "Gangguan PON {pon} · {olt}",
  "al.grp.ponOutage.what": "{n} ONT mati dalam {spread}",
  "al.grp.ponOutage.hint": "kemungkinan masalah jaringan bersama di PON ini",
  "al.grp.powerOutage": "Kemungkinan pemadaman listrik area · {olt}",
  "al.grp.powerOutage.what": "{n} ONT mengirim dying-gasp dalam {spread}",
  "al.grp.powerOutage.hint": "banyak pelanggan kehilangan listrik bersamaan; belum dipastikan",
  "al.grp.affected": "{n} dari {total} ONT",
  "al.grp.onts": "{n} ONT",
  "al.ackTitle": "Akui {n} insiden",
  "al.ackMeaning": "Anda sudah melihat dan menanganinya. Tetap terbuka sampai kondisinya pulih.",
  "al.note": "Catatan",
  "al.notePh": "mis. pelanggan dihubungi, teknisi dikirim",
  "al.partOf": "Bagian dari: {what} ({n} ONT)",
  "al.d.cause": "Kemungkinan arti",
  "al.d.signal": "Sinyal saat ini",
  "al.d.status": "Status",
  "al.d.timeline": "Linimasa",
  "al.d.tech": "Detail teknis",
  "al.d.history": "Sinyal optik · 24 jam terakhir",
  "al.metric.ont_rx_power_dbm": "RX ONT",
  "al.metric.ont_olt_rx_dbm": "RX OLT",
  "al.metric.ont_tx_power_dbm": "TX ONT",
  "al.metric.ont_temperature_c": "Suhu",
  "al.metric.ont_voltage_v": "Tegangan",
  "al.metric.ont_current_ma": "Arus bias",
  "al.metric.ont_run_state": "Keadaan",
  "al.measured": "diukur {age} lalu",
  "al.staleNote": "Tidak ada bacaan baru: nilai di atas adalah yang terakhir diterima, bukan sinyal saat ini.",
  "al.ackedBy": "Diakui oleh {who}",
  "al.notAcked": "Belum diakui: belum ada yang menangani",
  "al.unassigned": "Belum ditugaskan",
  "al.mutedUntil": "Notifikasi dibisukan sampai {at}",
  "al.ev.opened": "{what} dibuka",
  "al.ev.closed": "{what} ditutup oleh {who}",
  "al.ev.notified": "Notifikasi terkirim",
  "al.ev.acknowledged": "Diakui oleh {who}",
  "al.ev.assigned": "Ditugaskan oleh {who}",
  "al.ev.suppressed": "Notifikasi dibisukan oleh {who}",
  "al.ev.unsuppressed": "Notifikasi diaktifkan oleh {who}",
  "al.system": "sistem",
  "al.src.rule": "aturan alert",
  "al.src.olt": "collector OLT",
  "al.tech.serial": "Serial ONT {s}",
  "al.closeTitle": "Tutup catatan alert?",
  "al.closeBody": "Ini hanya menutup catatan. Tidak memperbaiki atau menyelesaikan apa pun.",
  "al.closeWarnTelemetry": "Sinyal masih melewati ambang: monitor akan membuka alert baru pada evaluasi berikutnya.",
  "al.closeWarnDown": "ONT masih mati: menutup menyembunyikan gangguan sampai ONT berubah keadaan lagi.",
  "al.closeAlt": "Untuk menghentikan notifikasi selama ditangani, bisukan saja. Kondisi yang pulih tertutup sendiri.",
  "al.noHistory": "Tidak ada bacaan optik 24 jam terakhir.",
  "al.now": "sekarang",
  "al.lastDays": "{n} hari terakhir",
  "al.res.sum": "{n} selesai · {auto} pulih otomatis",
  "al.res.opened": "Dibuka",
  "al.res.duration": "Durasi",
  "al.res.how": "Penyelesaian",
  "al.res.closedBy": "Ditutup oleh {who}",
  "al.res.auto": "Pulih otomatis",
  "al.res.ackedBy": "diakui oleh {who}",
  "al.res.none": "Tidak ada yang selesai di rentang ini.",
  "al.res.note": "Alert telemetri tertutup sendiri saat nilai pulih melewati hysteresis aturan; alert offline saat ONT kembali online.",
  "al.ago": "{age} lalu",
  "ua.title": "Pengguna & akses",
  "ua.invite": "Undang pengguna",
  "ua.ago": "{age} lalu",
  "ua.remove": "Hapus",
  "ua.sum.active": "{n} aktif",
  "ua.sum.external": "{n} eksternal",
  "ua.sum.pending": "{n} undangan menunggu",
  "ua.tab.users": "Pengguna",
  "ua.tab.roles": "Peran & izin",
  "ua.tab.invites": "Undangan",
  "ua.tab.audit": "Log audit",
  "ua.role.admin": "Admin",
  "ua.role.noc": "NOC",
  "ua.role.viewer": "Viewer",
  "ua.role.portviewer": "Port viewer",
  "ua.roleDesc.admin": "Konfigurasi penuh dan kontrol akses",
  "ua.roleDesc.noc": "Pemantauan jaringan dan tindakan operasional",
  "ua.roleDesc.viewer": "Pemantauan baca-saja; data pelanggan disamarkan",
  "ua.roleDesc.portviewer": "Hanya grafik trafik yang ditetapkan",
  "ua.status.active": "Aktif",
  "ua.status.inactive": "Tidak aktif",
  "ua.status.disabled": "Dinonaktifkan",
  "ua.status.invited": "Diundang",
  "ua.status.expired": "Kedaluwarsa",
  "ua.search": "Cari pengguna, peran, port…",
  "ua.quick.all": "Semua",
  "ua.quick.admin": "Admin",
  "ua.quick.noc": "NOC",
  "ua.quick.external": "Eksternal",
  "ua.quick.inactive": "Tidak aktif",
  "ua.quick.never": "Belum pernah masuk",
  "ua.quick.disabled": "Dinonaktifkan",
  "ua.col.user": "Pengguna",
  "ua.col.role": "Peran",
  "ua.col.scope": "Cakupan akses",
  "ua.col.status": "Status",
  "ua.col.lastActive": "Terakhir aktif",
  "ua.col.security": "Keamanan",
  "ua.invitedAgo": "diundang {age} lalu",
  "ua.expiresIn": "kedaluwarsa dalam {age}",
  "ua.expiredAgo": "kedaluwarsa {age} lalu",
  "ua.acceptedAgo": "diterima {age} lalu",
  "ua.sec.invite": "Tautan undangan, belum dipakai",
  "ua.manage": "Kelola",
  "ua.noMatch": "Tidak ada pengguna yang cocok.",
  "ua.noPending": "Tidak ada undangan menunggu.",
  "ua.scope.admin": "Seluruh sistem",
  "ua.scope.noc": "Seluruh jaringan",
  "ua.scope.viewer": "Seluruh jaringan · baca-saja",
  "ua.scope.none": "Belum ada port ditetapkan",
  "ua.scope.ports": "{n} port ditetapkan",
  "ua.you": "Anda",
  "ua.owner": "Pemilik",
  "ua.external": "Eksternal",
  "ua.never": "Belum pernah masuk",
  "ua.review": "Pertimbangkan meninjau akses",
  "ua.sec.password": "Kata sandi diatur",
  "ua.sec.noPassword": "Tanpa kata sandi",
  "ua.sec.sessions": "{n} sesi aktif",
  "ua.sec.oneSession": "1 sesi aktif",
  "ua.sec.noSessions": "Tidak ada sesi aktif",
  "ua.sum.inactive": "{n} tidak aktif",
  "ua.actions": "Tindakan untuk {email}",
  "ua.act.view": "Lihat akses",
  "ua.act.role": "Ubah peran",
  "ua.act.graphs": "Tetapkan grafik",
  "ua.act.security": "Keamanan",
  "ua.act.reset": "Atur kata sandi baru",
  "ua.act.sessions": "Cabut sesi",
  "ua.act.disable": "Nonaktifkan akun",
  "ua.act.enable": "Aktifkan kembali",
  "ua.block.self": "Anda tidak bisa mengubah atau menonaktifkan akun sendiri; minta admin lain.",
  "ua.block.lastAdmin": "Tidak bisa melepas administrator aktif terakhir. Angkat admin lain lebih dulu.",
  "ua.block.owner": "Akun pemilik tidak bisa diturunkan atau dinonaktifkan oleh admin lain.",
  "ua.block.notOwner": "Hanya akun pemilik yang boleh mengatur kata sandi pengguna lain.",
  "ua.block.ownerTarget": "Kata sandi pemilik lain tidak bisa diatur.",
  "ua.roleTitle": "Ubah peran",
  "ua.current": "saat ini",
  "ua.grants": "Ini memberi",
  "ua.removes": "Ini mencabut",
  "ua.roleSessions": "Sesi terbukanya berakhir, sehingga peran baru berlaku saat masuk berikutnya.",
  "ua.grantAdmin": "Jadikan Admin",
  "ua.changeRole": "Ubah peran",
  "ua.disableTitle": "Nonaktifkan akun?",
  "ua.enableTitle": "Aktifkan kembali akun?",
  "ua.disableBody": "Ia langsung kehilangan akses ke JALABYTE OPS.",
  "ua.disableSessions": "{n} sesi aktifnya dicabut.",
  "ua.noDelete": "Akun dinonaktifkan, tidak pernah dihapus, agar riwayat audit tetap merujuk ke akun itu. Aktifkan kembali untuk memulihkan akses.",
  "ua.enableBody": "Akses dipulihkan dengan peran sebelumnya: {role}.",
  "ua.sessionsTitle": "Cabut semua sesi?",
  "ua.sessionsBody": "Mengakhiri {n} sesi masuknya. Akun tetap aktif dan bisa masuk lagi.",
  "ua.sessionsNote": "Gunakan untuk laptop hilang atau dugaan pembobolan. Halaman yang terbuka berhenti bekerja dalam beberapa menit, saat token aksesnya kedaluwarsa.",
  "ua.sessionsDone": "{n} sesi dicabut.",
  "ua.sec.access": "Akses",
  "ua.sec.activity": "Aktivitas",
  "ua.sec.security": "Keamanan",
  "ua.sec.recent": "Kejadian akses terbaru",
  "ua.lastLogin": "Masuk terakhir",
  "ua.created": "Dibuat",
  "ua.disabledAt": "Dinonaktifkan",
  "ua.inactiveNote": "Tidak ada aktivitas lebih dari {days} hari: pertimbangkan apakah akses ini masih perlu.",
  "ua.password": "Kata sandi",
  "ua.mfaNone": "Belum tersedia di JALABYTE OPS",
  "ua.sessionsLabel": "Sesi",
  "ua.selfNote": "Ini akun Anda: perubahan peran dan penonaktifan harus dilakukan admin lain.",
  "ua.audit.none": "Belum ada kejadian akses.",
  "ua.audit.someone": "Seseorang",
  "ua.audit.unknown": "akun tak dikenal",
  "ua.audit.anyUser": "Semua pengguna",
  "ua.audit.note": "Hanya kejadian akses dan keamanan. Semua akun tetap ada di riwayat, termasuk yang dinonaktifkan.",
  "ua.yes": "Ya",
  "ua.no": "Tidak",
  "ua.grant.masked": "Disamarkan",
  "ua.grant.assigned": "Hanya yang ditetapkan",
  "ua.grant.superadmin": "Hanya pemilik",
  "ua.rolesIntro": "Apa yang bisa dilakukan tiap peran. Cakupan akses (port mana yang dilihat port viewer) diatur per pengguna; tingkat izin berasal dari peran.",
  "ua.permission": "Izin",
  "ua.members": "{n} anggota",
  "ua.member": "1 anggota",
  "ua.group.monitor": "Pemantauan",
  "ua.group.operate": "Operasional",
  "ua.group.configure": "Konfigurasi",
  "ua.group.access": "Kontrol akses",
  "ua.perm.network": "Lihat ringkasan, wallboard, topologi, dan peta link",
  "ua.perm.devices": "Lihat OLT, PON, dan ONT",
  "ua.perm.alerts": "Lihat alert",
  "ua.perm.customers": "Lihat nama dan kontak pelanggan",
  "ua.perm.graphs": "Lihat grafik trafik",
  "ua.perm.ack": "Akui dan tutup alert",
  "ua.perm.reboot": "Reboot ONT",
  "ua.perm.ontEdit": "Ubah catatan ONT dan tautan CPE",
  "ua.perm.wan": "Lihat detail WAN CPE",
  "ua.perm.olts": "Tambah dan ubah OLT beserta kredensialnya",
  "ua.perm.acs": "Kelola server ACS dan kredensial",
  "ua.perm.integrations": "Uji dan sinkronkan integrasi",
  "ua.perm.rules": "Ubah aturan alert dan ambang",
  "ua.perm.linkmap": "Ubah peta link",
  "ua.perm.shares": "Buat tautan berbagi grafik",
  "ua.perm.users": "Kelola pengguna, peran, dan undangan",
  "ua.perm.resetOthers": "Atur kata sandi pengguna lain",
  "ua.perm.ownPassword": "Ubah kata sandi sendiri",
  "ua.inviteNote": "Tautan undangan hanya ditampilkan sekali, saat dibuat: yang disimpan hanya hash-nya. Kirim ulang membuat tautan baru dan membatalkan yang lama.",
  "ua.inv.open": "Menunggu dan kedaluwarsa",
  "ua.inv.none": "Tidak ada undangan menunggu.",
  "ua.inv.resend": "Kirim ulang",
  "ua.inv.revoke": "Batalkan",
  "ua.inv.history": "Riwayat",
  "ua.inv.acceptedN": "{n} diterima dalam 30 hari terakhir",
  "ua.inv.historyNote": "Undangan yang dibatalkan ada di log audit.",
  "ua.hide": "Sembunyikan",
  "ua.show": "Tampilkan",
  "ua.cat.all": "Semua",
  "ua.cat.role": "Peran",
  "ua.cat.account": "Akun",
  "ua.cat.invite": "Undangan",
  "ua.cat.password": "Kata sandi",
  "ua.cat.login": "Masuk",
  "ua.cat.session": "Sesi",
  "ua.cat.scope": "Akses grafik",
  "ua.ev.role": "{actor} mengubah peran {target}: {from} → {to}",
  "ua.ev.enabled": "{actor} mengaktifkan kembali {target}",
  "ua.ev.disabled": "{actor} menonaktifkan {target}",
  "ua.ev.update": "{actor} memperbarui {target}",
  "ua.ev.disable": "{actor} menonaktifkan {target}",
  "ua.ev.sessions_revoke": "{actor} mencabut {n} sesi {target}",
  "ua.ev.invite_create": "{actor} mengundang {target} sebagai {to}",
  "ua.ev.invite_revoke": "{actor} membatalkan undangan untuk {target}",
  "ua.ev.invite_accept": "{target} menerima undangan sebagai {to}",
  "ua.ev.password_reset": "{actor} mengatur kata sandi baru untuk {target}",
  "ua.ev.password_reset_denied": "{actor} ditolak mengatur kata sandi {target}",
  "ua.ev.password_change": "{target} mengganti kata sandinya",
  "ua.ev.password_fail": "{target} salah memasukkan kata sandi saat ini",
  "ua.ev.graph_ports_set": "{actor} mengubah grafik yang bisa dilihat {target}",
  "ua.ev.login_ok": "{target} masuk",
  "ua.ev.login_fail": "Gagal masuk untuk {target}",
  "ua.ev.logout": "{target} keluar",
  "ua.resendTitle": "Kirim ulang undangan",
  "ua.resendNote": "Tautan baru menggantikan yang lama, yang berhenti berfungsi.",
  "ua.inviteTitle": "Undang pengguna",
  "ua.linkOnce": "Bagikan tautan ini kepadanya. Hanya ditampilkan sekarang; bila hilang, kirim ulang.",
  "ua.expiry": "Masa berlaku undangan",
  "ua.days": "{n} hari",
  "ua.can": "Bisa",
  "ua.cannot": "Tidak bisa",
  "ua.pvNote": "Tetapkan grafik yang boleh dilihat setelah ia menerima undangan.",
  "users.roleAdmin": "Admin",
  "users.roleNoc": "NOC",
  "users.roleViewer": "Pengamat",
  "users.rolePortViewer": "Pemantau port",
  "users.graphs": "Grafik",
  "users.graphAccess": "Grafik untuk {email}",
  "users.graphAccessEmpty": "Belum ada grafik yang diberikan — akun ini akan melihat halaman kosong.",
  "pw.change": "Kata sandi",
  "pw.changeTitle": "Ubah kata sandi Anda",
  "pw.resetTitle": "Atur kata sandi baru untuk {email}",
  "pw.reset": "Kata sandi",
  "pw.current": "Kata sandi saat ini",
  "pw.new": "Kata sandi baru",
  "pw.confirm": "Ulangi kata sandi baru",
  "pw.tooShort": "Minimal {n} karakter.",
  "pw.mismatch": "Kedua kata sandi tidak sama.",
  "pw.changeNote": "Sesi Anda yang lain akan dikeluarkan.",
  "pw.resetNote": "Sesi mereka yang lain langsung berakhir. Sampaikan kata sandi baru lewat jalur yang Anda percaya.",
  "pw.changeDone": "Kata sandi diubah. Sesi lain sudah dikeluarkan.",
  "pw.resetDone": "Kata sandi untuk {email} sudah diatur. Sesi mereka sudah dikeluarkan.",

  "users.roleHint": "Admin mengatur pengguna dan pengaturan. NOC menangani alarm dan bisa reboot ONT. Pengamat hanya melihat, nama pelanggan disamarkan. Pemantau port hanya melihat grafik yang Anda berikan.",
  "users.status": "Status",
  "users.active": "aktif",
  "users.disabled": "nonaktif",
  "users.lastLogin": "Masuk terakhir",
  "users.expires": "Kedaluwarsa",
  "users.enable": "Aktifkan",
  "users.disable": "Nonaktifkan",
  "users.revoke": "Batalkan",
  "users.copy": "Salin",
  "users.copied": "Tersalin",
  "users.noUsers": "belum ada akun",
  "users.noInvites": "belum ada undangan",
  "users.invite_pending": "menunggu",
  "users.invite_accepted": "diterima",
  "users.invite_expired": "kedaluwarsa",

  // Accept invite
  "invite.title": "Terima undangan",
  "invite.intro": "Buat kata sandi untuk {email} ({role}).",
  "invite.password": "Kata sandi",
  "invite.confirm": "Ulangi kata sandi",
  "invite.mismatch": "Kedua kata sandi tidak sama.",
  "invite.pwHint": "Minimal 8 karakter.",
  "invite.submit": "Buat akun",
  "invite.checking": "Memeriksa undangan…",
  "invite.noToken": "Tautan ini tidak membawa token undangan.",

  "common.refresh": "Muat ulang",
  "common.retry": "Coba lagi",
  "common.all": "Semua",
  "common.none": "Tidak ada",
  "common.yes": "Ya",
  "common.no": "Tidak",
  "common.enabled": "Aktif",
  "common.disabled": "Tidak aktif",
  "common.loading": "Memuat…",
  "common.empty": "Belum ada data",
  "common.error": "Terjadi kesalahan",
  "common.confirm": "Yakin mau lanjut?",
  "common.name": "Nama",
  "common.description": "Keterangan",
  "common.notes": "Catatan",
  "common.status": "Keadaan",
  "common.actions": "Tindakan",
  "common.details": "Rincian",
  "common.lastSeen": "Terakhir terlihat",
  "common.updated": "Diperbarui",
  "common.created": "Dibuat",
  "common.signal": "Sinyal",
  "common.distance": "Jarak",
  "common.uptime": "Lama aktif",
  "common.customer": "Pelanggan",
  "common.reboot": "Nyalakan ulang",
  "common.threshold": "Batas",
  "common.recovered": "Pulih",
  "common.never": "Belum pernah",


  // overview
  "overview.openAlerts": "Notifikasi terbuka",
  "overview.title": "Ikhtisar jaringan",
  "ont.wan.lastKnown": "terakhir diketahui {ip}",
  "ont.live.online": "Aktif",
  "ont.live.offline": "Mati",
  "ont.live.missing": "Hilang",
  "ont.live.stale": "Kedaluwarsa",
  "ont.live.unknown": "Tidak diketahui",
  "ont.rx.good": "Baik",
  "ont.rx.moderate": "Sedang",
  "ont.rx.weak": "Lemah",
  "ont.rx.critical": "Kritis",
  "ont.rx.nodata": "Tanpa data",
  "ont.cause.dyingGasp": "Kehilangan daya terdeteksi (dying gasp)",
  "ont.cause.los": "Sinyal optik hilang (LOS)",
  "ont.cause.other": "Penyebab lain",
  "ont.cause.none": "Penyebab tidak dilaporkan",
  "ont.quick.all": "Semua",
  "ont.quick.offline": "Mati",
  "ont.quick.weak": "Optik lemah",
  "ont.quick.alerts": "Alarm aktif",
  "ont.quick.unlinked": "Belum terhubung pelanggan",
  "ont.quick.stale": "Kedaluwarsa",
  "ont.list.summary": "{n} ONT · {off} mati",
  "ont.list.search": "Serial, pelanggan, ONT #, MAC atau IP WAN…",
  "ont.list.anyOptical": "Semua level optik",
  "ont.list.anyCause": "Semua penyebab gangguan",
  "ont.list.neverDown": "Belum pernah mati",
  "ont.list.last": "terakhir",
  "ont.list.downFor": "mati {d}",
  "ont.list.ago": "{d} lalu",
  "ont.list.alertsN": "{n} alarm terbuka",
  "ont.list.range": "{a}–{b} dari {n}",
  "ont.col.ont": "ONT / pelanggan",
  "ont.col.oltPon": "OLT / PON",
  "ont.col.optical": "Optik",
  "ont.col.outage": "Gangguan terakhir",
  "ont.h.seen": "terlihat {age} lalu",
  "ont.h.uptime": "aktif {d}",
  "ont.h.offlineFor": "mati selama {d}",
  "ont.act.refresh": "Muat ulang",
  "ont.act.openPon": "Buka PON",
  "ont.act.more": "Lainnya",
  "ont.act.copy": "Salin",
  "ont.act.copySerial": "Salin serial",
  "ont.act.restartTr069": "Nyalakan ulang CPE lewat TR-069",
  "ont.act.restartOlt": "Nyalakan ulang ONT lewat OLT",
  "ont.act.restartWarn": "{name} akan mati satu-dua menit selama dinyalakan ulang. Pelanggan kehilangan layanan selama itu.",
  "ont.act.restartConfirm": "Nyalakan ulang sekarang",
  "ont.inc.active": "Insiden aktif",
  "ont.inc.offline": "ONT mati",
  "ont.inc.missing": "ONT hilang dari OLT",
  "ont.inc.started": "Mulai",
  "ont.inc.duration": "Durasi",
  "ont.inc.lastRx": "RX terakhir",
  "ont.inc.alertsTitle": "Alarm aktif",
  "ont.inc.lastOutage": "Gangguan terakhir",
  "ont.inc.none": "Tidak ada insiden aktif",
  "ont.inc.neverDown": "Belum ada gangguan tercatat",
  "ont.inc.cause": "Penyebab",
  "ont.inc.ongoing": "masih berlangsung · {d}",
  "ont.kpi.rx": "RX ONT",
  "ont.kpi.lastRx": "RX terakhir",
  "ont.kpi.offlineFor": "Mati selama",
  "ont.kpi.alerts": "Alarm",
  "ont.kpi.none": "Tidak ada",
  "ont.cpe.online": "Aktif",
  "ont.cpe.silent": "Tidak lapor baru-baru ini",
  "ont.cpe.unknown": "Tidak diketahui",
  "ont.cpe.none": "Belum terhubung",
  "ont.cpe.acsDown": "ACS tidak terjangkau",
  "ont.wan.up": "Aktif",
  "ont.wan.down": "Mati",
  "ont.wan.unknown": "Tidak diketahui",
  "ont.wan.none": "Tidak ada",
  "ont.wan.roleHidden": "Detail WAN hanya untuk peran admin dan NOC.",
  "ont.conn.up": "Aktif",
  "ont.conn.down": "Mati",
  "ont.conn.disabled": "Nonaktif",
  "ont.conn.unknown": "Tidak diketahui",
  "ont.cust.title": "Pelanggan / CPE",
  "ont.cust.notLinked": "Belum terhubung",
  "ont.cust.link": "Hubungkan pelanggan",
  "ont.bill.title": "Tagihan",
  "ont.bill.active": "Aktif",
  "ont.bill.suspended": "Ditangguhkan",
  "ont.bill.pending": "Menunggu",
  "ont.bill.suspendedNote": "Layanan ditangguhkan di WHMCS: ONT mati bisa karena blokir tagihan, bukan gangguan.",
  "ont.cust.inform": "lapor {age} lalu",
  "ont.opt.title": "Optik",
  "ont.opt.history": "Riwayat",
  "ont.opt.down": "OLT → ONT",
  "ont.opt.up": "ONT → OLT",
  "ont.opt.ontRx": "RX ONT (diterima ONT)",
  "ont.opt.oltRx": "RX OLT (diterima OLT)",
  "ont.opt.oltRxDir": "RX OLT: sinyal ONT yang diterima di OLT",
  "ont.opt.stale": "pembacaan lama",
  "ont.opt.lastKnown": "terakhir diketahui",
  "ont.opt.thresholds": "Ambang",
  "ont.opt.bands": "lemah < {w} · kritis < {c} dBm",
  "ont.opt.sampled": "Diambil",
  "ont.opt.current": "Terkini",
  "ont.opt.avg": "Rata-rata",
  "ont.opt.min": "Min",
  "ont.opt.max": "Maks",
  "ont.opt.gapNote": "Celah menandai periode tanpa pembacaan (ONT mati atau telemetri hilang); tidak pernah digambar sebagai 0 dBm.",
  "ont.opt.now": "Pembacaan terkini",
  "ont.opt.ponCompare": "Dibanding PON {pon}",
  "ont.opt.ponAvg": "RX rata-rata PON",
  "ont.opt.thisOnt": "ONT ini",
  "ont.opt.rank": "Peringkat",
  "ont.opt.rankOf": "terlemah ke-{r} dari {n} yang aktif",
  "ont.path.title": "Jalur jaringan",
  "ont.path.noCpe": "tidak ada CPE",
  "ont.ev.recent": "Kejadian terbaru",
  "ont.ev.all": "Riwayat lengkap",
  "ont.ev.history": "Riwayat kejadian",
  "ont.ev.oltReported": "dilaporkan OLT",
  "ont.notes.add": "Tambah catatan",
  "ont.notes.none": "Belum ada catatan operasional",
  "ont.mac.none": "OLT belum melaporkan MAC di belakang ONT ini.",
  "ont.tab.overview": "Ringkasan",
  "ont.tab.optical": "Optik",
  "ont.tab.wan": "WAN / CPE",
  "ont.tab.events": "Kejadian",
  "ont.tab.technical": "Teknis",
  "ont.tech.identity": "Identitas",
  "ont.tech.internalId": "Id internal",
  "ont.tech.provisioning": "Provisi & keadaan",
  "olt.si.offlineElsewhere": "{n} ONT lain mati di PON lain",
  "olt.lastValue": "terakhir {v}",
  "olt.tele.noData": "Tidak ada data · tidak terjangkau",
  "olt.pollFailed": "POLL GAGAL",
  "olt.ab.oltDown": "Semua {n} ONT tidak terjangkau bersama OLT; daftarnya ada di tab ONT.",
  "olt.fleet.title": "Armada OLT",
  "olt.fleet.summary": "{n} OLT · {healthy} sehat",
  "olt.fleet.all": "Semua",
  "olt.fleet.abnormal": "Perlu perhatian",
  "olt.fleet.search": "Cari OLT, IP, lokasi…",
  "olt.fleet.noMatch": "Tidak ada OLT yang cocok.",
  "olt.col.olt": "OLT",
  "olt.col.health": "Kesehatan",
  "olt.col.pon": "PON",
  "olt.col.ont": "ONT aktif",
  "olt.col.offline": "Mati",
  "olt.col.warn": "Peringatan",
  "olt.col.lastPoll": "Poll terakhir",
  "olt.state.healthy": "Sehat",
  "olt.state.degraded": "Terganggu",
  "olt.state.critical": "Kritis",
  "olt.state.down": "Mati",
  "olt.state.stale": "Kedaluwarsa",
  "olt.state.unknown": "Tidak diketahui",
  "olt.reason.unreachable": "OLT tidak terjangkau",
  "olt.reason.neverPolled": "belum pernah dipoll",
  "olt.reason.stale": "tidak dipoll 3 siklus",
  "olt.reason.ponDown": "ada PON mati",
  "olt.reason.manyOffline": "25%+ ONT mati",
  "olt.reason.offline": "ada ONT mati",
  "olt.reason.partial": "CLI terbaca sebagian",
  "olt.reason.hot": "suhu tinggi",
  "olt.ago": "{age} lalu",
  "olt.never": "belum pernah dipoll",
  "olt.polledAgo": "dipoll {age} lalu",
  "olt.uptime": "nyala {up}",
  "olt.staleTag": "KEDALUWARSA",
  "olt.notReported": "tidak dilaporkan",
  "olt.lastReported": "laporan terakhir {v} · {at}",
  "olt.kpi.pon": "PON",
  "olt.kpi.ont": "ONT",
  "olt.kpi.online": "Aktif",
  "olt.kpi.offline": "Mati",
  "olt.kpi.warnings": "Peringatan",
  "olt.tab.overview": "Ringkasan",
  "olt.tab.pons": "PON",
  "olt.tab.onts": "ONT",
  "olt.tab.traffic": "Trafik",
  "olt.tab.health": "Kesehatan",
  "olt.tab.events": "Kejadian",
  "olt.si.title": "Dampak layanan",
  "olt.si.all": "Semua yang mati",
  "olt.si.none": "Tidak ada pelanggan mati",
  "olt.si.oltDown": "OLT tidak terjangkau",
  "olt.si.potentially": "{n} ONT berpotensi terdampak",
  "olt.si.ponDown": "PON {pon} mati",
  "olt.si.offline": "{n} ONT mati",
  "olt.powerLoss": "listrik hilang (dying gasp)",
  "olt.moreN": "+{n} lagi",
  "olt.offlineN": "{n} mati",
  "olt.weakN": "{n} lemah",
  "olt.downFor": "mati {age}",
  "olt.h.title": "Kesehatan perangkat",
  "olt.h.reach": "Keterjangkauan",
  "olt.h.telemetry": "Telemetri",
  "olt.h.uptime": "Waktu nyala",
  "olt.h.memory": "Memori",
  "olt.h.temp": "Suhu",
  "olt.h.fan": "Kipas",
  "olt.h.sensors": "Sensor lingkungan",
  "olt.reach.reachable": "Aktif",
  "olt.reach.unreachable": "Mati",
  "olt.reach.degraded": "Aktif · terbaca sebagian",
  "olt.reach.unknown": "Tidak diketahui",
  "olt.reach.up": "Aktif",
  "olt.reach.down": "Mati",
  "olt.tele.fresh": "Terbaru",
  "olt.tele.stale": "Kedaluwarsa",
  "olt.fan.ok": "Normal",
  "olt.fan.fault": "Rusak",
  "olt.info.title": "Informasi perangkat",
  "olt.info.ip": "IP manajemen",
  "olt.info.mgmt": "Manajemen",
  "olt.info.advanced": "Lanjutan",
  "olt.info.lastError": "Galat terakhir",
  "olt.info.cliNote": "Catatan CLI",
  "olt.pon.title": "Kesehatan PON",
  "olt.pon.otherHealthy": "{n} PON lain sehat",
  "olt.pon.more": "+{n} PON lagi",
  "olt.pon.col": "PON",
  "olt.pon.weak": "RX lemah",
  "olt.pon.traffic": "Trafik",
  "olt.pon.onlyAbnormal": "Hanya PON bermasalah ({n})",
  "olt.pon.back": "Kembali ke PON",
  "olt.pon.onts": "ONT",
  "olt.pon.toOnts": "OLT → ONT (TX)",
  "olt.pon.fromOnts": "ONT → OLT (RX)",
  "olt.pon.noTraffic": "OLT ini tidak melaporkan penghitung per PON.",
  "olt.pon.allOnts": "Semua ONT di PON ini ({n})",
  "olt.ponTier.down": "Mati",
  "olt.ponTier.degraded": "Terganggu",
  "olt.ponTier.warn": "Optik lemah",
  "olt.ponTier.healthy": "Sehat",
  "olt.opt.title": "Kesehatan optik",
  "olt.opt.analysis": "Analisis optik",
  "olt.opt.good": "Baik",
  "olt.opt.moderate": "Sedang",
  "olt.opt.weak": "Lemah",
  "olt.opt.critical": "Kritis",
  "olt.opt.noReading": "{n} tanpa pembacaan terkini (mati)",
  "olt.opt.avg": "RX rata-rata",
  "olt.opt.worst": "RX terlemah",
  "olt.opt.ponWeak": "PON {pon}: {n} lemah",
  "olt.p.offline": "Mati",
  "olt.p.missing": "Hilang",
  "olt.p.rxCrit": "RX kritis",
  "olt.p.rxWarn": "RX rendah",
  "olt.ab.title": "ONT bermasalah",
  "olt.ab.count": "{n} dari {total}",
  "olt.ab.none": "Semua ONT aktif dengan sinyal baik.",
  "olt.ab.ont": "ONT / pelanggan",
  "olt.ab.problem": "Masalah",
  "olt.ab.distance": "Jarak",
  "olt.ab.allAbnormal": "Semua {n} bermasalah",
  "olt.ab.allOnts": "Lihat semua {n} ONT",
  "olt.inv.search": "Cari serial, pelanggan, PON…",
  "olt.inv.serial": "Serial",
  "olt.inv.since": "Mati selama",
  "olt.inv.allPons": "Semua PON",
  "olt.inv.none": "Tidak ada ONT yang cocok.",
  "olt.inv.showAll": "Tampilkan semua {n}",
  "olt.inv.f.all": "Semua",
  "olt.inv.f.abnormal": "Bermasalah",
  "olt.inv.f.offline": "Mati",
  "olt.inv.f.weak": "RX lemah",
  "olt.inv.f.online": "Aktif",
  "olt.tr.perspective": "Sudut pandang OLT",
  "olt.tr.txMeans": "dikirim OLT (ke ONT pada port PON)",
  "olt.tr.rxMeans": "diterima OLT",
  "olt.tr.cli": "Penghitung port · CLI OLT",
  "olt.tr.cliNote": "Dibaca dari penghitung OLT tiap poll dan dirata-rata antar poll. Hanya nilai terkini; tidak ada riwayat.",
  "olt.tr.sampled": "Diambil",
  "olt.tr.history": "Antarmuka & riwayat · LibreNMS",
  "olt.tr.historyNote": "Klik antarmuka untuk riwayat trafiknya.",
  "olt.tr.notMirrored": "LibreNMS tidak mencerminkan antarmuka OLT ini, jadi tidak ada riwayat.",
  "olt.tr.speed": "Kecepatan",
  "olt.tr.util": "Utilisasi",
  "olt.ev.none": "Tidak ada kejadian dalam rentang ini.",
  "olt.ev.cleared": "selesai",
  "olt.ev.online": "ONT aktif kembali",
  "olt.ev.offline": "ONT mati",
  "olt.ev.down_cause": "Penyebab mati dilaporkan",
  "olt.ev.alarm": "Alarm OLT",
  "olt.ev.discovered": "ONT ditemukan",
  "olt.ev.f.all": "Semua",
  "olt.ev.f.critical": "Kritis",
  "olt.ev.f.warning": "Peringatan",
  "olt.ev.f.ont": "ONT",
  "olt.ev.f.device": "Perangkat",
  "wb.apiDown": "Tidak bisa menghubungi API NocOps",
  "wb.stale": "DATA KEDALUWARSA",
  "wb.live": "LANGSUNG",
  "wb.polled": "OLT dipoll {age} lalu",
  "wb.network": "Jaringan",
  "wb.state.healthy": "SEHAT",
  "wb.state.degraded": "TERGANGGU",
  "wb.state.major": "GANGGUAN BESAR",
  "wb.state.unknown": "TIDAK DIKETAHUI",
  "wb.olt": "OLT",
  "wb.online": "Aktif",
  "wb.ont": "ONT",
  "wb.down": "Mati",
  "wb.ontsOffline": "ONT mati",
  "wb.incidents": "Insiden",
  "wb.correlated": "terkorelasi",
  "wb.warnings": "Peringatan",
  "wb.alertsOpen": "alarm terbuka: {n}",
  "wb.majorIncident": "Gangguan besar",
  "wb.more": "lagi",
  "wb.oltDown": "OLT MATI",
  "wb.ponDown": "PON MATI",
  "wb.ontsPotentially": "ONT berpotensi terdampak",
  "wb.downFor": "mati selama",
  "wb.staleApi": "Layar belum menghubungi API selama {age}.",
  "wb.staleNever": "Belum ada poll OLT yang tercatat.",
  "wb.stalePoll": "Poll OLT terakhir {age} lalu.",
  "wb.staleTitle": "DATA PEMANTAUAN KEDALUWARSA",
  "wb.staleHint": "Angka di bawah mungkin tidak mencerminkan jaringan saat ini.",
  "wb.serviceImpact": "Dampak layanan",
  "wb.ontsAffected": "{n} ONT terdampak",
  "wb.moreIncidents": "+{n} insiden lagi · {onts} ONT",
  "wb.noImpact": "Tidak ada dampak layanan",
  "wb.kind.olt": "OLT mati",
  "wb.kind.pon": "PON mati",
  "wb.kind.multi": "{n} ONT mati",
  "wb.kind.ont": "ONT mati",
  "wb.potentiallyN": "{n} ONT berpotensi terdampak",
  "wb.onlineOf": "{on}/{all} aktif",
  "wb.powerLoss": "Kehilangan daya terdeteksi (dying gasp)",
  "wb.lastCause": "Penyebab terakhir: {cause}",
  "wb.sitesWord": "lokasi",
  "wb.oltsWord": "OLT",
  "wb.ponsWord": "PON",
  "wb.sites": "Kesehatan POP / lokasi",
  "wb.fleet": "Armada OLT",
  "wb.oltsOnline": "{up}/{all} aktif",
  "wb.otherHealthy": "{n} {unit} lain sehat",
  "wb.moreAbnormal": "+{n} {unit} lagi · {bad} bermasalah",
  "wb.noSite": "Tanpa lokasi",
  "wb.offlineN": "{n} mati",
  "wb.oltPartial": "Poll sebagian",
  "wb.hot": "Suhu tinggi",
  "wb.up": "nyala",
  "wb.polledAgo": "dipoll {age} lalu",
  "wb.ponHealth": "Kesehatan PON",
  "wb.ponsFull": "{up}/{all} sehat",
  "wb.weakN": "{n} lemah",
  "wb.downN": "mati {age}",
  "wb.noPons": "Belum ada port PON yang dipoll",
  "wb.collectorError": "Pengambil data gagal",
  "wb.noWarnings": "Tidak ada peringatan lain",
  "wb.ponTrouble": "Gangguan PON",
  "incident.ont.temperature_c": "Suhu ONT",
  "incident.ont.voltage_v": "Tegangan ONT",
  "incident.ont.tx_power_dbm": "Daya TX ONT",
  "incident.ont.current_ma": "Arus bias laser ONT",
  "wb.unlisted": "+{n} peringatan lain tidak tampil — lihat Notifikasi",
  "wb.opticalElsewhere": "{n} alarm optik dihitung di Kesehatan optik.",
  "wb.moreWarnings": "+{n} peringatan lagi",
  "wb.optical": "Kesehatan optik",
  "wb.belowWarn": "ONT di bawah {dbm} dBm",
  "wb.noOptical": "Tidak ada pembacaan optik terbaru",
  "wb.uplinks": "Sambungan tersibuk",
  "wb.noUplinks": "Belum ada trafik sambungan",
  "lm.devices": "Perangkat",
  "lm.up": "Aktif",
  "lm.links": "Sambungan",
  "lm.down": "Mati",
  "lm.downTitle": "{d} perangkat dan {l} sambungan mati",
  "lm.search": "Cari perangkat, IP, antarmuka…",
  "lm.noResults": "Tidak ditemukan",
  "lm.filters": "Filter & lapisan",
  "lm.reset": "Atur ulang tampilan",
  "lm.zoomIn": "Perbesar",
  "lm.zoomOut": "Perkecil",
  "lm.layers": "Lapisan",
  "lm.labels": "Label",
  "lm.visualization": "Visualisasi",
  "lm.status": "Status",
  "lm.deviceType": "Jenis perangkat",
  "lm.layerMonitored": "Perangkat terpantau",
  "lm.layerManual": "Sambungan manual",
  "lm.layerDiscovered": "Sambungan terdeteksi",
  "lm.layerForeign": "Perangkat asing",
  "lm.labelNames": "Nama perangkat",
  "lm.labelIps": "IP manajemen",
  "lm.labelIfaces": "Nama antarmuka",
  "lm.labelCapacity": "Kapasitas sambungan",
  "lm.labelRates": "Nilai RX/TX",
  "lm.visUtil": "Utilisasi sambungan",
  "lm.visFlow": "Animasi arus trafik",
  "lm.st.up": "Aktif",
  "lm.st.warn": "Peringatan",
  "lm.st.down": "Mati",
  "lm.st.unknown": "Tidak diketahui",
  "lm.st.stale": "Terlambat",
  "lm.role.core": "Inti",
  "lm.role.agg": "Agregasi",
  "lm.role.router": "Router",
  "lm.role.switch": "Switch",
  "lm.role.olt": "OLT",
  "lm.role.device": "Perangkat jaringan",
  "lm.role.foreign": "Asing (LLDP/CDP)",
  "lm.inferred": "perkiraan",
  "lm.pinned": "ditandai Anda",
  "lm.setCore": "Tandai sebagai inti",
  "lm.unsetCore": "Batal tandai inti",
  "lm.coreTitle": "Jalur ke inti dan dampak memakai perangkat ini sebagai inti (tersimpan di peramban ini)",
  "lm.link.healthy": "< 50%",
  "lm.link.moderate": "50–80%",
  "lm.link.high": "> 80%",
  "lm.link.down": "Mati",
  "lm.link.nodata": "Tanpa data",
  "lm.help": "Legenda & bantuan peta",
  "lm.help1": "Warna dan tebal garis mengikuti utilisasi; ✕ merah menandai sambungan mati.",
  "lm.help2": "Garis putus-putus terdeteksi lewat LLDP/CDP; garis utuh digambar operator.",
  "lm.help3": "Kotak putus-putus redup adalah perangkat asing: terdeteksi, tidak dipantau, tanpa telemetri.",
  "lm.help4": "Peran diperkirakan: perangkat dengan tetangga terpantau terbanyak dianggap inti.",
  "lm.help5": "Perbesar untuk nama antarmuka dan trafik; saat diperkecil hanya inti, agregasi, OLT dan masalah yang diberi label.",
  "lm.help6": "Geser perangkat untuk menempatkannya; posisi tetap tersimpan.",
  "lm.linkDetails": "Detail sambungan",
  "lm.deviceDetails": "Detail perangkat",
  "lm.capacity": "Kapasitas",
  "lm.traffic": "Trafik",
  "lm.utilization": "Utilisasi",
  "lm.originManual": "Digambar operator",
  "lm.originDiscovered": "Terdeteksi lewat LLDP/CDP",
  "lm.graph": "Grafik trafik",
  "lm.viewPorts": "Lihat port",
  "lm.pathToCore": "Jalur ke inti",
  "lm.clear": "Hapus",
  "lm.alerts": "Notifikasi",
  "lm.trafficGraphs": "Grafik trafik",
  "lm.mgmt": "Manajemen",
  "lm.platform": "Platform",
  "lm.uptime": "Waktu aktif",
  "lm.load": "CPU / memori",
  "lm.noLinksHere": "Tidak ada sambungan ke perangkat ini.",
  "lm.foreignNote": "Terdeteksi lewat {proto}. Tidak dipantau: tidak ada telemetri.",
  "lm.impactPotential": "Potensi dampak",
  "lm.impactIfFails": "Jika ini gagal",
  "lm.impactInferred": "diperkirakan dari topologi",
  "lm.impactNone": "Tidak ada perangkat hilir yang kehilangan jalur ke inti.",
  "lm.impactDevices": "perangkat",
  "lm.impactOnts": "ONT",
  "lm.impactOntsOnline": "{n} aktif sekarang",
  "lm.confirmed": "Terkonfirmasi mati",
  "lm.potential": "Berpotensi terputus",
  "lm.showImpact": "Tampilkan di peta",
  "lm.relatedAlerts": "Notifikasi terkait",
  "lm.pathNone": "Tidak ada jalur ke perangkat inti.",
  "lm.pathSingle": "Satu jalur: tidak ada jalur cadangan.",
  "lm.pathPrimary": "Utama",
  "lm.pathBackup": "Cadangan",
  "lm.hops": "lompatan: {n}",
  "lm.impactOf": "Dampak {name}",
  "lm.noCore": "Perangkat inti tidak bisa diperkirakan tanpa sambungan.",
  "lm.close": "Tutup",
  "lm.edit": "Ubah sambungan",
  "overview.health": "Kesehatan",
  "overview.healthHint": "ONT aktif",
  "overview.affected": "Terdampak",
  "overview.affectedHint": "pelanggan terputus",
  "overview.incidents": "Insiden",
  "overview.activeIncidents": "Insiden aktif",
  "overview.viewAll": "Lihat semua",
  "overview.inspect": "Periksa",
  "overview.noIncidents": "Tidak ada insiden terbuka. Jaringan tenang.",
  "overview.networkStatus": "Status jaringan",
  "overview.collectors": "Pengambil data",
  "overview.api": "API",
  "overview.healthy": "Sehat",
  "overview.unreachable": "Tidak terjangkau",
  "overview.customerOffline": "pelanggan terputus",
  "incident.ont.run_state": "ONT mati",
  "incident.ont.rx_power_dbm": "Daya RX optik",
  "incident.ont.olt_rx_dbm": "Daya RX OLT",
  "incident.olt.unreachable": "OLT tidak terjangkau",
  "nav.group.network": "Jaringan",
  "nav.group.operations": "Operasional",
  "nav.group.admin": "Admin",
  "live.live": "Langsung",
  "live.stale": "Terlambat",
  "live.agoS": "diperbarui {n} dtk lalu",
  "live.agoM": "diperbarui {n} mnt lalu",
  "topbar.openAlerts": "{n} notifikasi terbuka",

  // OLT list / form
  "olts.add": "Tambah OLT",
  "olts.editTitle": "Ubah {name}",
  "olts.empty": "belum ada OLT terdaftar",
  "olts.mgmtIp": "IP pengelolaan",
  "olts.driver": "Driver",
  "olts.port": "Port (kosong = bawaan driver)",
  "olts.cliUser": "Nama pengguna CLI",
  "olts.cliPass": "Kata sandi CLI",
  "olts.cliPassKeep": "Kata sandi CLI (kosong = biarkan)",
  "olts.enablePass": "Kata sandi enable (opsional)",
  "olts.location": "Lokasi (opsional)",
  "olts.vendor": "Merek",
  "olts.model": "Model",
  "olts.firmware": "Firmware",

  // OLT detail
  "oltDetail.location": "Lokasi",
  "oltDetail.lastPolled": "Terakhir diperiksa",
  "oltDetail.noPorts": "belum ada port PON terdaftar",

  // ONT list
  "onts.allOlts": "semua OLT",
  "onts.allPons": "semua PON",
  "onts.empty": "tidak ada ONT yang cocok",
  "onts.serial": "Serial",
  "onts.number": "ONT #",
  "onts.distance": "Jarak",

  // ONT detail
  "ontDetail.runState": "Keadaan jalan",
  "ontDetail.configState": "Keadaan konfigurasi",
  "ontDetail.matchState": "Keadaan pencocokan",
  "ontDetail.lastUp": "Terakhir aktif",
  "ontDetail.lastDown": "Terakhir mati",
  "ontDetail.lastDyingGasp": "Pesan mati terakhir",
  "ontDetail.lastDownCause": "Penyebab mati terakhir",
  "ontDetail.ponPath": "Jalur PON",
  "ontDetail.index": "Indeks",
  "ontDetail.oltIp": "IP OLT",
  "ontDetail.oltStatus": "Keadaan OLT",
  "ontDetail.onuType": "Jenis ONU",
  "ontDetail.subscriber": "Pelanggan",
  "ontDetail.matchedBy": "Dicocokkan lewat",
  "ontDetail.acsServer": "Server ACS",
  "ontDetail.deviceId": "ID perangkat",
  "ontDetail.manufacturer": "Pembuat",
  "ontDetail.wanMac": "MAC WAN",
  "ontDetail.wanIp": "IP WAN",
  "ontDetail.lastInform": "Laporan terakhir",
  "ontDetail.lastBoot": "Nyala terakhir",
  "ontDetail.change": "Ganti",
  "ontDetail.unlink": "Lepaskan",
  "ontDetail.noCpe": "Tidak ada CPE yang cocok — GenieACS dan OLT sering melaporkan serial yang berbeda.",
  "ontDetail.linkCpe": "Hubungkan CPE",
  "ontDetail.cpeConflict": "CPE itu sudah terhubung ke ONT lain — {msg}. Lepaskan dulu di sana.",
  "ontDetail.notesPlaceholder": "mis. pelanggan X, lantai dua, ada ONT cadangan…",
  "ontDetail.learnedMacs": "MAC yang terbaca",
  "ontDetail.macsFrom": "{n} dari tabel MAC di OLT",
  "ontDetail.vlan": "vlan",
  "ontDetail.opticalHistory": "Riwayat sinyal optik",
  "ontDetail.noSamples": "belum ada data pada rentang ini",
  "ontDetail.noEvents": "belum ada kejadian tercatat",
  "ontDetail.rebootedByOperator": "dinyalakan ulang petugas",
  "ontDetail.noOpenAlerts": "tidak ada notifikasi terbuka",
  "ontDetail.rebootTr069": "Nyalakan ulang lewat TR-069",
  "ontDetail.rebooting": "Menyalakan ulang…",
  "ontDetail.rebootSentTr069": "Perintah nyalakan ulang dikirim lewat TR-069 (GenieACS).",
  "ontDetail.rebootSentOlt": "Perintah nyalakan ulang dikirim lewat CLI OLT.",
  "ontDetail.pickerTitle": "Hubungkan CPE",
  "ontDetail.pickerDesc": "Cari perangkat GenieACS lewat serial, ID perangkat, MAC atau model, lalu hubungkan yang benar.",
  "ontDetail.pickerPlaceholder": "serial, ID perangkat, MAC atau model",
  "ontDetail.noCpeMatch": "tidak ada CPE yang cocok",
  "ontDetail.linked": "Terhubung",
  "ontDetail.link": "Hubungkan",

  // optical metric names
  "metric.rxPower": "Daya terima (Rx)",
  "metric.txPower": "Daya kirim (Tx)",
  "metric.oltRx": "Daya terima di OLT",
  "metric.temperature": "Suhu",
  "metric.voltage": "Tegangan",
  "metric.current": "Arus",

  // ONT event names
  "event.down_cause": "Penyebab mati",

  // topology

  // link map
  "netmap.addLink": "Tambah sambungan",
  "netmap.editLink": "Ubah sambungan",
  "netmap.autoArrange": "Tata otomatis",
  "netmap.autoArrangeTitle": "Buang semua posisi yang Anda geser dan kembali ke tata letak otomatis",
  "netmap.autoArrangeConfirm": "Buang semua posisi perangkat yang digeser dan tata ulang otomatis?",
  "netmap.fit": "Pas layar",
  "netmap.posNotSaved": "Posisi perangkat gagal disimpan: {msg}",
  "netmap.autoArrangeFailed": "Tata otomatis gagal: {msg}",
  "netmap.noDevices": "Belum ada perangkat yang tersalin.",
  "netmap.noDevicesHint": "Peta ini diisi oleh pengambil data LibreNMS — perlu URL dan token API LibreNMS, lalu satu kali sinkronisasi selesai.",
  "netmap.configureLink": "Atur dan jalankan pengambil data LibreNMS →",
  "netmap.noLinks": "{n} perangkat tersalin, tapi belum ada sambungan di antaranya.",
  "netmap.noLinksHint": "Penemuan tetangga LibreNMS (CDP/LLDP) tidak menemukan sambungan antar perangkat Anda sendiri — semua tetangga yang ditemukan milik pihak lain, dan itu disembunyikan secara bawaan. Gambar sendiri topologi yang sebenarnya:",
  "netmap.noLinksAdmin": "“Tambah sambungan” di atas menyambungkan dua perangkat Anda, dan bisa dipasangkan ke port supaya sambungan menampilkan Rx/Tx langsung.",
  "netmap.noLinksViewer": "admin bisa menambah sambungan dari halaman ini.",
  "netmap.peersExist": " Ada {n} perangkat luar yang bisa ditampilkan dengan tombol di atas.",
  "netmap.svgLabel": "Peta jaringan: {nodes} perangkat, {links} sambungan. Geser untuk berpindah, gulir untuk memperbesar.",
  "netmap.svgTitle": "Peta jaringan: {nodes} perangkat, {links} sambungan",
  "netmap.aSide": "Sisi A",
  "netmap.bSide": "Sisi B",
  "netmap.pickDevice": "pilih perangkat",
  "netmap.portOptional": "Port (opsional — memberi Rx/Tx pada sambungan)",
  "netmap.noPort": "tanpa port (tanpa Rx/Tx)",
  "netmap.labelOptional": "Label (opsional)",
  "netmap.labelPlaceholder": "mis. trunk 10G",
  "netmap.needTwo": "sambungan butuh dua perangkat yang berbeda",
  "netmap.discoveredNoGraph": "Grafik LibreNMS hanya ada untuk sambungan yang digambar di halaman ini; yang ini berasal dari penemuan LLDP/CDP.",
  "netmap.noPortPinned": "belum ada port dipilih di sisi ini",
  "netmap.noPortPinnedAdmin": " — ubah sambungan dan pilih port untuk melihat grafik",
  "netmap.speed": "kecepatan",
  "netmap.loadingPorts": "memuat daftar port…",
  "netmap.notMirrored": "Perangkat ini belum ada di salinan LibreNMS — jalankan sinkronisasi LibreNMS.",
  "netmap.noPortsMirrored": "belum ada port tersalin untuk perangkat ini",
  "netmap.peerNoPorts": "{name} tidak dipantau LibreNMS — perangkat ini ditemukan lewat {proto}, jadi tidak ada daftar portnya di sini.",
  "netmap.portsName": "nama",
  "netmap.portsAlias": "alias",
  "netmap.portsUpDown": "aktif/mati",
  "netmap.portsRx": "Rx sekarang",
  "netmap.portsTx": "Tx sekarang",
  "netmap.rowTitle": "Klik untuk grafik LibreNMS",
  "netmap.openOlt": "Buka halaman OLT",

  // traffic graphs
  "graphs.subtitle": "trafik masuk dan keluar, dari LibreNMS",
  "graphs.port": "Port",
  "graphs.searchPlaceholder": "cari perangkat, ifName atau keterangan…",
  "graphs.devicePlaceholder": "perangkat",
  "graphs.portPlaceholder": "port",
  "graphs.inOut": "masuk {rx} · keluar {tx}",
  "graphs.capped": "daftar dipotong — persempit pencarian untuk melihat sisanya",
  "graphs.noMatch": "tidak ada yang cocok dengan pencarian itu",
  "graphs.noDevices": "belum ada perangkat LibreNMS tersalin — jalankan sinkronisasi LibreNMS",
  "graphs.trafficTitle": "Trafik masuk/keluar · {win}",
  "graphs.pick": "pilih perangkat dan port",
  "graphs.alt": "Grafik trafik LibreNMS untuk {device} {port} selama {win}",
  "graph.unavailable": "Grafik LibreNMS tidak tersedia: {msg}",
  "graph.loading": "memuat grafik…",
  "graph.none": "tidak ada grafik",

  // integrations
  "integrations.subtitle": "Sistem luar dan pengambilan data terakhirnya.",
  "integrations.test": "Uji",
  "integrations.testing": "Menguji…",
  "integrations.sync": "Sinkronkan",
  "integrations.syncing": "Menyinkronkan…",
  "integrations.verified": "Sambungan berhasil",
  "integrations.synced": "Tersinkron: {onts} ONT, {events} kejadian",
  "integrations.syncStarted": "Sinkronisasi dimulai",
  "integrations.loadingRuns": "memuat riwayat…",
  "integrations.noRuns": "belum ada riwayat",
  "integrations.finished": "Selesai",
  "lm.viewTopology": "Lihat di Topologi",
  "topo.expand": "Buka",
  "topo.inferred": "ditebak dari nama",
  "topo.collapse": "Tutup",
  "topo.subtitle": "Letak setiap perangkat di jaringan, dan di bagian mana masalah berada.",
  "topo.state.down": "Mati",
  "topo.state.critical": "Kritis",
  "topo.state.degraded": "Menurun",
  "topo.state.stale": "Basi",
  "topo.state.warn": "Peringatan",
  "topo.state.unknown": "Tidak diketahui",
  "topo.state.healthy": "Sehat",
  "topo.conf.confirmed": "Terkonfirmasi",
  "topo.conf.discovered": "Ditemukan",
  "topo.conf.manual": "Manual",
  "topo.conf.confirmed.hint": "dilaporkan kedua sisi",
  "topo.conf.discovered.hint": "dilaporkan satu sisi",
  "topo.conf.manual.hint": "dibuat operator",
  "topo.fresh.ftth": "FTTH",
  "topo.fresh.discovery": "Discovery",
  "topo.fresh.stale": "Basi",
  "topo.fresh.none": "Tanpa data",
  "topo.fresh.current": "Terkini",
  "topo.view.infra": "Infrastruktur",
  "topo.view.discovery": "Discovery",
  "topo.view.unresolved": "Belum terpecahkan",
  "topo.filter.all": "Semua",
  "topo.filter.problems": "Masalah saja",
  "topo.noProblems": "Tidak ada masalah di seluruh hierarki.",
  "topo.empty": "Belum ada site, OLT, atau perangkat.",
  "topo.sum.sites": "Site",
  "topo.sum.olts": "OLT",
  "topo.sum.pons": "PON",
  "topo.sum.onts": "ONT online",
  "topo.sum.devices": "Perangkat jaringan",
  "topo.sum.links": "Link",
  "topo.sum.unresolved": "Belum terpecahkan",
  "topo.sum.confirmedN": "{n} terkonfirmasi",
  "topo.issues": "Masalah infrastruktur · {n}",
  "topo.allHealthy": "Infrastruktur sehat",
  "topo.unassigned": "Belum masuk site",
  "topo.unassignedHint": "Perangkat ini tidak punya lokasi di LibreNMS dan namanya tidak memuat site. Isi lokasi di LibreNMS untuk menempatkannya.",
  "topo.nOlt": "{n} OLT",
  "topo.nPon": "{n} PON",
  "topo.onlineOf": "{on}/{total} ONT online",
  "topo.nDev": "{n} perangkat",
  "topo.access": "Jaringan akses",
  "topo.infra": "Infrastruktur jaringan",
  "topo.nOffline": "{n} offline",
  "topo.noPons": "Belum ada port PON.",
  "topo.nWeak": "{n} optik lemah",
  "topo.othersHealthy": "{n} ONT lain sehat",
  "topo.openPon": "Buka PON",
  "topo.nDown": "{n} mati",
  "topo.nLinks": "{n} link",
  "topo.noLinks": "tanpa link",
  "topo.search": "Cari topologi: perangkat, IP, OLT, PON, serial ONT, atau pelanggan…",
  "topo.noHits": "Tidak ada yang cocok.",
  "topo.kind.site": "Site",
  "topo.kind.olt": "OLT",
  "topo.kind.pon": "PON",
  "topo.kind.ont": "ONT",
  "topo.kind.device": "Perangkat",
  "topo.locate": "Temukan",
  "topo.detail.pick": "Pilih node untuk melihat letaknya dan apa yang terdampak. Kesehatan naik ke atas: ONT offline menurunkan PON dan OLT-nya; hanya PON gelap atau OLT mati yang membuat site kritis.",
  "topo.detail.reach": "Jangkauan",
  "topo.detail.temp": "Suhu",
  "topo.detail.alerts": "Alert terbuka",
  "topo.detail.online": "Online",
  "topo.detail.offline": "Offline",
  "topo.detail.weak": "Optik lemah",
  "topo.detail.lastDrop": "Putus terakhir",
  "topo.detail.cause": "Penyebab mati",
  "topo.detail.rx": "Rx",
  "topo.detail.status": "Status",
  "topo.detail.site": "Site",
  "topo.detail.links": "Link ditemukan",
  "topo.reach.down": "Tidak terjangkau",
  "topo.reach.unknown": "Belum di-poll",
  "topo.reach.up": "Terjangkau",
  "topo.polled": "di-poll {age} lalu",
  "topo.openOlt": "Buka OLT",
  "topo.viewMap": "Lihat di Peta Link",
  "topo.ontList": "Daftar ONT",
  "topo.openOnt": "Buka ONT",
  "topo.dev.up": "Hidup",
  "topo.dev.down": "Mati",
  "topo.dev.unknown": "Tidak diketahui",
  "topo.siteInferred": "ditebak dari nama perangkat",
  "topo.nUnresolvedFrom": "{n} tetangga yang dilaporkannya tidak dapat dipastikan — lihat Belum terpecahkan.",
  "topo.discStaleShort": "Discovery basi: link ini mungkin usang.",
  "topo.discStale": "Discovery LibreNMS basi: relasi di bawah mungkin usang. Hierarki FTTH berasal dari collector OLT dan punya kesegarannya sendiri.",
  "topo.legend": "Keadaan",
  "topo.legendHint": "Baris sehat hanya bertitik hijau; selain itu diberi label.",
  "topo.disc.source": "Sumber",
  "topo.disc.synced": "sinkron {age} lalu",
  "topo.disc.devices": "perangkat",
  "topo.disc.links": "link",
  "topo.disc.unresolved": "belum terpecahkan",
  "topo.disc.knownDevices": "Perangkat dikenal",
  "topo.disc.relationships": "Relasi",
  "topo.col.device": "Perangkat",
  "topo.col.platform": "Platform",
  "topo.col.site": "Site",
  "topo.col.links": "Link",
  "topo.col.status": "Status",
  "topo.col.local": "Perangkat",
  "topo.col.remote": "Tetangga",
  "topo.col.method": "Metode",
  "topo.col.confidence": "Keyakinan",
  "topo.isOlt": "OLT",
  "topo.viaN": "lewat {n} antarmuka",
  "topo.unres.intro": "Ujung jauh yang dilaporkan discovery tetapi tidak bisa dikaitkan ke perangkat terpantau. Tiap grup menjelaskan alasannya dan cara menyelesaikannya.",
  "topo.unres.none": "Semua tetangga yang ditemukan cocok dengan perangkat terpantau.",
  "topo.unres.resolve": "Cara menyelesaikan",
  "topo.unres.seenFrom": "Dilaporkan oleh",
  "topo.unres.noName": "tanpa nama",
  "topo.unres.xRows": "×{n}",
  "topo.unres.self": "Nama bawaan pabrik",
  "topo.unres.self.why": "Tetangga mengumumkan nama umum (misalnya \"MikroTik\") yang sama dengan perangkat pelapor sendiri, sehingga tampak seperti link ke dirinya sendiri.",
  "topo.unres.self.fix": "beri perangkat tetangga identitas unik (system name), atau buat link manual di Peta Link.",
  "topo.unres.ambiguous": "Nama dipakai beberapa perangkat",
  "topo.unres.ambiguous.why": "Beberapa perangkat berbeda (MAC chassis berbeda) mengumumkan nama yang sama, jadi nama itu tidak bisa menunjuk satu perangkat.",
  "topo.unres.ambiguous.fix": "ganti nama perangkat-perangkat itu agar masing-masing unik.",
  "topo.unres.anonymous": "Tanpa identitas",
  "topo.unres.anonymous.why": "Hanya satu sisi link yang ditemukan dan ujung jauh tidak melaporkan nama.",
  "topo.unres.anonymous.fix": "aktifkan LLDP/CDP dengan system name di ujung jauh.",
  "topo.unres.unmonitored": "Tetangga tidak dipantau",
  "topo.unres.unmonitored.why": "Ujung jauh punya nama tetapi tidak dipantau LibreNMS: sering perangkat pihak lain seperti peer IX atau router pelanggan.",
  "topo.unres.unmonitored.fix": "tambahkan ke LibreNMS bila milik Anda; selain itu memang wajar.",
  "int.subtitle": "Kesehatan collector dan sistem luar yang memberi data ke JALABYTE OPS: bisakah data di layar dipercaya?",
  "int.view.cards": "Kartu",
  "int.view.table": "Tabel",
  "int.state.healthy": "Sehat",
  "int.state.running": "Berjalan",
  "int.state.degraded": "Menurun",
  "int.state.failed": "Gagal",
  "int.state.stale": "Basi",
  "int.state.disabled": "Nonaktif",
  "int.state.unknown": "Tidak diketahui",
  "int.noun.olts": "OLT",
  "int.noun.devices": "Perangkat",
  "int.noun.services": "Layanan",
  "int.noun.objects": "Objek",
  "int.m.onts": "ONT",
  "int.m.events": "Kejadian",
  "int.m.links": "Link",
  "int.m.mirrored": "Dicerminkan",
  "int.m.failed": "Gagal",
  "int.m.linked": "Terhubung ONT",
  "int.never": "belum pernah",
  "int.ago": "{age} lalu",
  "int.job.verified": "Sambungan berhasil",
  "int.job.failedN": "{n} gagal",
  "int.job.testing": "Menguji sambungan",
  "int.job.syncing": "Sinkronisasi berjalan",
  "int.job.scheduled": "Jadwal sedang berjalan",
  "int.stage.clients": "Mengambil klien {done} / {total}",
  "int.job.test.ok": "Sambungan OK",
  "int.job.test.fail": "Sambungan gagal",
  "int.job.sync.ok": "Sinkronisasi selesai",
  "int.job.sync.fail": "Sinkronisasi gagal",
  "int.group.collectors": "Collector",
  "int.group.observability": "Observabilitas jaringan",
  "int.group.acs": "CPE / ACS",
  "int.group.billing": "Tagihan",
  "int.group.other": "Lainnya",
  "int.posture.operational": "Semua sistem normal",
  "int.posture.degraded": "Pemantauan menurun",
  "int.posture.failure": "Pemantauan gagal",
  "int.posture.unknown": "Belum ada data",
  "int.healthyOf": "{n} / {total} sehat",
  "int.fresh.olt": "Status ONT",
  "int.fresh.snmp": "Jangkauan OLT",
  "int.checked": "dicek {age} lalu",
  "int.conf.title": "Keyakinan pemantauan",
  "int.conf.good": "Baik",
  "int.conf.degraded": "Menurun",
  "int.conf.low": "Rendah",
  "int.conf.allFresh": "Semua collector penting segar: gambaran jaringan di layar terkini.",
  "int.banner.notDown": "Jangan anggap perangkat jaringan mati.",
  "int.banner.investigate": "Periksa",
  "int.tag.source": "Masalah sumber",
  "int.tag.collector": "Masalah collector",
  "int.tag.external": "Sistem luar",
  "int.why.stale": "Tidak ada run sukses selama {age}; seharusnya tiap {every}. Datanya mungkin usang.",
  "int.why.staleNever": "Belum pernah sukses; seharusnya tiap {every}.",
  "int.why.collector": "Job collector sendiri gagal: {detail}",
  "int.why.external": "Sistem luar mengembalikan galat: {detail}",
  "int.why.source": "Collector berjalan, tapi tak satu pun dari {n} {noun} menjawab. Periksa jalur jaringan ke sana.",
  "int.why.partial": "{n} dari {total} {noun} gagal; sisanya terkini.",
  "int.why.warnings": "Menjawab, tapi tidak lengkap: {w}",
  "int.why.slow": "Run terakhir {ms}, biasanya {p50}.",
  "int.why.never": "Belum ada run tercatat.",
  "int.more": "+{n} lagi",
  "int.affected": "Terdampak",
  "int.mayBeFine": "perangkat jaringan mungkin tetap berjalan",
  "int.lastOk": "Sukses terakhir",
  "int.busy": "Run sedang berjalan",
  "int.act.test": "Uji sambungan",
  "int.act.retry": "Ulangi",
  "int.act.run": "Jalankan",
  "int.act.sync": "Sinkronkan",
  "int.kind.collector": "Collector",
  "int.kind.external": "Eksternal",
  "int.disabled": "Belum dikonfigurasi, sengaja dimatikan.",
  "int.every": "tiap {every}",
  "int.took": "durasi",
  "int.lastFail": "gagal terakhir",
  "int.reliability24": "OK 24j",
  "int.provides": "Menyediakan",
  "int.investigate": "Periksa",
  "int.details": "Detail",
  "int.provides.ontStatus": "Status ONT",
  "int.provides.optical": "Level optik",
  "int.provides.ponHealth": "Kesehatan PON",
  "int.provides.oltHealth": "CPU & suhu OLT",
  "int.provides.outages": "Kejadian gangguan",
  "int.provides.oltReach": "Jangkauan manajemen OLT",
  "int.provides.devices": "Perangkat jaringan",
  "int.provides.links": "Topologi & peta link",
  "int.provides.traffic": "Grafik trafik",
  "int.provides.cpe": "Status CPE",
  "int.provides.wan": "IP WAN & PPPoE",
  "int.provides.tr069": "Reboot TR-069",
  "int.provides.subscribers": "Nama pelanggan",
  "int.provides.billing": "Status tagihan",
  "int.col.integration": "Integrasi",
  "int.col.state": "Keadaan",
  "int.col.lastOk": "Sukses terakhir",
  "int.col.every": "Interval",
  "int.col.duration": "Durasi",
  "int.col.result": "Hasil terakhir",
  "int.col.trend": "Run terakhir",
  "int.col.started": "Mulai",
  "int.tab.overview": "Ringkasan",
  "int.tab.history": "Riwayat",
  "int.tab.failures": "Kegagalan",
  "int.tab.performance": "Performa",
  "int.tab.config": "Konfigurasi",
  "int.cfg.fromOlts": "Langsung ke setiap data OLT",
  "int.cfg.noEndpoint": "Endpoint belum diatur",
  "int.cfg.enabled": "Aktif",
  "int.cfg.notConfigured": "Tidak — belum dikonfigurasi",
  "int.cfg.yes": "Ya",
  "int.cfg.defaultInterval": "bawaan, worker belum melaporkan intervalnya",
  "int.cfg.stale": "Basi setelah",
  "int.cfg.source": "Sumber",
  "int.cfg.timeout": "Batas waktu",
  "int.cfg.credentials": "Kredensial",
  "int.cfg.note": "Kredensial tidak pernah ditampilkan di sini. Operasi (uji, sinkron) ada di halaman ini; konfigurasi ada di tempat yang disebut.",
  "int.cfg.cred.olt": "Per OLT, terenkripsi — OLT → Ubah",
  "int.cfg.cred.snmp": "SNMP_COMMUNITY di .env server",
  "int.cfg.cred.env": ".env server (deploy ulang untuk mengubah)",
  "int.cfg.cred.acs": "Terenkripsi — Pengaturan → Server ACS",
  "int.sec.freshness": "Kesegaran",
  "int.sec.result": "Hasil terakhir",
  "int.sec.reliability": "Keandalan",
  "int.sec.sourceFailures": "Sumber yang gagal di run terakhir",
  "int.sec.failedRuns": "Run gagal",
  "int.lastRun": "Run terakhir",
  "int.none24": "Tidak ada",
  "int.staleAfter": "basi setelah {w}",
  "int.warning": "Peringatan",
  "int.w1h": "1 jam terakhir",
  "int.w24h": "24 jam terakhir",
  "int.okOf": "{ok} / {n} sukses",
  "int.run.ok": "Sukses",
  "int.run.error": "Gagal",
  "int.run.disabled": "Nonaktif",
  "int.run.partial": "Sebagian",
  "int.noSourceFailures": "Semua sumber menjawab di run terakhir.",
  "int.noFailedRuns": "Tidak ada run gagal tercatat.",
  "int.perf.last": "Terakhir",
  "int.perf.avg1h": "Rata-rata 1j",
  "int.perf.p50": "Median 24j",
  "int.perf.p95": "p95 24j",
  "int.perf.chart": "Durasi, {n} run terakhir",
  "int.perf.slowRule": "Run di atas 3× median 24 jam (dan 30 dtk di atasnya) menandai integrasi menurun walau sukses.",
  "conf.oltNever": "Belum ada pengambilan OLT: status ONT tidak diketahui.",
  "conf.oltDown": "Status ONT belum diperbarui selama {age}.",
  "conf.oltDownNever": "Collector OLT belum pernah selesai: status ONT tidak diketahui.",
  "conf.oltPartial": "{n} dari {total} OLT tidak menjawab collector: status ONT-nya tidak terkini.",
  "conf.snmpDownOltOk": "Jangkauan OLT (SNMP) terakhir dipastikan {age} lalu. Status ONT dari collector OLT terkini.",
  "conf.snmpDown": "Jangkauan OLT (SNMP) terakhir dipastikan {age} lalu.",
  "conf.side.librenms": "{name}: perangkat jaringan dan trafik mungkin usang",
  "conf.side.genieacs": "{name}: data CPE dan WAN mungkin usang",
  "conf.side.whmcs": "{name}: data pelanggan dan tagihan mungkin usang",
  "conf.side.other": "{name} mungkin usang",

  // subscribers

  // alerts
  "alerts.active": "Notifikasi aktif",
  "alerts.openCount": "{n} terbuka",
  "alerts.loading": "Memuat notifikasi…",
  "alerts.loadError": "Gagal memuat notifikasi.",
  "alerts.empty": "Tidak ada notifikasi aktif.",
  "alerts.severity": "Tingkat",
  "alerts.alert": "Notifikasi",
  "alerts.target": "Sasaran",
  "alerts.value": "Nilai",
  "alerts.opened": "Mulai",
  "alerts.ack": "Tangani",
  "alerts.rules": "Aturan notifikasi",
  "alerts.rule": "Aturan",
  "alerts.hysteresis": "Margin pulih",
  "alerts.consecutive": "Berturut-turut",
  "alerts.cooldown": "Jeda (detik)",
  "alerts.noRules": "belum ada aturan",
  "alerts.rulesHelp": "Margin pulih adalah selisih agar notifikasi dianggap pulih, dalam satuan metriknya sendiri (aturan “< -27,0” dengan margin 1,0 dianggap pulih di -26,0). Berturut-turut adalah berapa kali data harus melewati batas sebelum notifikasi muncul. Jeda menahan notifikasi berulang untuk aturan dan sasaran yang sama.",
  "alerts.deleteRuleTitle": "Hapus aturan “{name}”?",
  "alerts.deleteRuleBefore": "Tidak ada lagi yang dipantau untuk",
  "alerts.deleteRuleAfter": "setelah ini. Nonaktifkan saja kalau hanya ingin membisukannya sementara.",
  "alerts.deleting": "menghapus…",

  // settings
  "settings.addServer": "Tambah server",
  "settings.addServerTitle": "Tambah server GenieACS",
  "settings.editTitle": "Ubah {name}",
  "settings.nbiUrl": "URL dasar NBI",
  "settings.nbiHint": "API northbound GenieACS — port 7557, bukan tampilan web 3000.",
  "settings.username": "Nama pengguna (opsional)",
  "settings.passwordKeep": "Kata sandi (kosong = pakai yang lama)",
  "settings.password": "Kata sandi (opsional)",
  "settings.passwordKeepHint": "Biarkan kosong untuk memakai kata sandi yang tersimpan.",
  "settings.passwordHint": "NBI bisa saja tidak pakai sandi.",
  "settings.notesOptional": "Catatan (opsional)",
  "settings.enabledHint": "Aktif (diambil oleh pengumpul data)",
  "settings.urlScheme": "URL harus dimulai dengan http:// atau https://",
  "settings.deleteTitle": "Hapus {name}?",
  "settings.deleteBody": "Ini menghapus server beserta {n} data CPE salinannya. ONT tetap jalan; nyalakan ulang lewat TR-069 berhenti sampai ada server lain yang menyalinnya.",
  "settings.lastChecked": "Terakhir diperiksa",
  "settings.working": "sedang bekerja…",
  "settings.connected": "tersambung — {n} perangkat",
  "settings.syncedDevices": "{n} perangkat tersinkron",
  "settings.noServers": "belum ada server ACS",
  "settings.noServersAdmin": "belum ada server ACS — tambahkan satu untuk mulai menyalin data CPE",
  "settings.passwordNote": "Kata sandi disimpan terenkripsi dan tidak pernah dikirim balik oleh API.",
  "settings.oltCredentialsBody": "Kata sandi SSH/Telnet OLT disimpan terenkripsi dengan AES-GCM memakai variabel OPS_MASTER_KEY. Tampilan hanya memberikannya ke peran admin, dan setiap akses dicatat.",

  // shared additions
  "common.librenmsNotConfigured": "LibreNMS belum diatur — isi LIBRENMS_URL dan LIBRENMS_API_TOKEN, lalu sinkronkan.",
  "common.noDescription": "tanpa keterangan",
  "common.deleting": "Menghapus…",
  "status.ok": "OK",
  "status.error": "Gagal",
  "status.running": "Berjalan",
  "status.up": "Aktif",
  "status.down": "Mati",
  "status.disabled": "Tidak aktif",
  "status.active": "Aktif",
  "status.suspended": "Ditangguhkan",

  "status.online": "Aktif",
  "status.offline": "Mati",
  "status.degraded": "Terganggu",
  "status.reachable": "Terhubung",
  "status.unreachable": "Tidak terhubung",
  "status.unknown": "Tidak diketahui",
  "status.open": "Terbuka",
  "status.acknowledged": "Ditangani",
  "status.closed": "Selesai",
  "status.critical": "Gangguan",
  "status.warning": "Perhatian",
  "status.info": "Info",
};

const dicts: Record<Lang, Record<string, string>> = { en, id };

/** Locale tag for Intl: dates and numbers read naturally in both languages. */
export const localeOf = (lang: Lang) => (lang === "id" ? "id-ID" : "en-GB");

export function translate(lang: Lang, key: Key, vars?: Record<string, string | number>): string {
  const raw = dicts[lang][key] ?? dicts.en[key] ?? key;
  return vars ? raw.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : raw;
}

/**
 * Status word from the API ("online", "unreachable", "acknowledged", …) rendered
 * in the active language. Anything unmapped (vendor strings) is returned as-is,
 * so device data is never mangled by a bad guess.
 */
export function tStatus(t: Ctx["t"], value?: string | null): string {
  if (!value) return t("status.unknown");
  const key = `status.${value.toLowerCase()}`;
  return key in en ? t(key as Key) : value;
}

export function initialLang(): Lang {
  const saved = typeof localStorage !== "undefined" ? localStorage.getItem(LANG_KEY) : null;
  if (saved === "en" || saved === "id") return saved;
  const nav = typeof navigator !== "undefined" ? navigator.language : "";
  return nav?.toLowerCase().startsWith("id") ? "id" : "en";
}

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: Key, vars?: Record<string, string | number>) => string;
  locale: string;
};

const I18nContext = createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>(initialLang);
  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const t = useCallback<Ctx["t"]>((key, vars) => translate(lang, key, vars), [lang]);
  const value = useMemo(() => ({ lang, setLang, t, locale: localeOf(lang) }), [lang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const c = useContext(I18nContext);
  if (!c) throw new Error("useI18n outside I18nProvider");
  return c;
}

/** Locale-aware date+time for a timestamp from the API; "—" when absent. */
export const fmtDateTime = (locale: string, iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(locale) : "—";
/** Locale-aware calendar date: "24 Sep 2026". */
export const fmtDate = (locale: string, iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }) : "—";
/** Locale-aware time of day. */
export const fmtTime = (locale: string, iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString(locale) : "—";
/** Locale-aware number grouping (1.234 in id-ID, 1,234 in en-GB). */
export const fmtNum = (locale: string, n?: number | null) =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat(locale).format(n);
